/**
 * WarehouseConfig — Einrichten des Lager-Grundrisses (Single-Page).
 *
 * Inline-Halle-Header + Compact-Toolbar (56px) + FloorplanGrid + Properties-Panel.
 *
 * @page /warehouse/einrichten
 * @roles admin, manager
 * @api /api/warehouse/:id/config, /api/warehouse/:id/shelves, /api/warehouse/:id/objekte
 * @i18n warehouse.config, warehouse.*
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import useTabParam from '../hooks/useTabParam';
import {
 Settings, Check, Trash2, RotateCw,
 Upload, Package, Layers, Grid3x3, Info, X, Search, ChevronDown,
 Box, Truck, Square, Archive, Sparkles, ChevronUp,
 Building2, AlertTriangle, ArrowDownToLine, ArrowUpFromLine, DoorOpen,
 Plus, Minus, SplitSquareHorizontal, Pencil
} from 'lucide-react';
import { PageHeader } from '../components/ui';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API || '';

// ─── Reusable NumInput — holds local string state, commits on blur/Enter ───
function NumInput({ value, onChange, fallback = 0, integer, className, ...props }) {
 const [local, setLocal] = useState(String(value ?? ''));
 const prev = useRef(value);
 // Sync when external value changes (e.g. different item selected)
 useEffect(() => {
 if (value !== prev.current) {
 setLocal(String(value ?? ''));
 prev.current = value;
 }
 }, [value]);
 const commit = () => {
 const parsed = local.replace(',', '.');
 const num = integer ? parseInt(parsed, 10) : parseFloat(parsed);
 if (!isNaN(num)) {
 onChange(num);
 prev.current = num;
 } else {
 setLocal(String(value ?? ''));
 }
 };
 return (
 <input
 type="text"
 inputMode={integer ? 'numeric' : 'decimal'}
 value={local}
 onChange={e => setLocal(e.target.value)}
 onBlur={commit}
 onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
 className={className || 'w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]'}
 {...props}
 />
 );
}

// ─── Shelf type definitions ───
const SHELF_TYPES = [
 { type: 'fachboden', color: '#338edc', defaultW: 6, defaultH: 0.6, icon: Grid3x3, levels: 6, slots: 10, defaultDepth: 0.6 },
 { type: 'hochregal', color: '#4D9CE0', defaultW: 10, defaultH: 1.2, icon: Layers, levels: 5, slots: 10, defaultDepth: 1.2 },
 { type: 'blocklager', color: '#6366F1', defaultW: 4, defaultH: 4, icon: Box, levels: 1, slots: 4, defaultDepth: 4.0 },
 { type: 'packbereich', color: '#22D3EE', defaultW: 6, defaultH: 4, icon: Package, levels: 1, slots: 6, defaultDepth: 2.0 },
 { type: 'wareneingang', color: '#06b6d4', defaultW: 8, defaultH: 4, icon: Truck, levels: 1, slots: 4, defaultDepth: 3.0 },
 { type: 'ladetor', color: '#94a3b8', defaultW: 4, defaultH: 3, icon: Square, levels: 1, slots: 1, defaultDepth: 3.0 },
 { type: 'custom', color: '#8b5cf6', defaultW: 6, defaultH: 4, icon: Archive, levels: 1, slots: 4, defaultDepth: 1.0 },
];

function getShelfDef(type) {
 return SHELF_TYPES.find(s => s.type === type) || SHELF_TYPES[6];
}

// ─── Object type definitions (non-shelf items) ───
const OBJEKT_TYPEN = {
 packtisch: { label: 'warehouse.typ_packtisch', color: '#10B981', defaultW: 2, defaultH: 0.8, form: 'rechteck', darstellung: 'solid', opacity: 1.0, icon: Package, props: ['kapazitaet'] },
 dock: { label: 'warehouse.typ_dock', color: '#EF4444', defaultW: 4, defaultH: 3, form: 'rechteck', darstellung: 'solid', opacity: 0.8, icon: Truck, props: ['dock_nummer', 'seite'] },
 zone_anlieferung: { label: 'warehouse.typ_zone_anlieferung', color: '#10B981', defaultW: 10, defaultH: 6, form: 'flaeche', darstellung: 'gestrichelt', opacity: 0.08, icon: ArrowDownToLine, props: ['beschreibung'] },
 zone_versand: { label: 'warehouse.typ_zone_versand', color: '#338edc', defaultW: 10, defaultH: 6, form: 'flaeche', darstellung: 'gestrichelt', opacity: 0.08, icon: ArrowUpFromLine, props: ['beschreibung'] },
 buero: { label: 'warehouse.typ_buero', color: '#6B7280', defaultW: 8, defaultH: 6, form: 'flaeche', darstellung: 'solid', opacity: 0.15, icon: Building2, props: ['bezeichnung'] },
 tor: { label: 'warehouse.typ_tor', color: '#10B981', defaultW: 3, defaultH: 0.3, form: 'linie', darstellung: 'solid', opacity: 1.0, icon: DoorOpen, props: ['tor_nummer', 'seite'] },
 gefahrgut: { label: 'warehouse.typ_gefahrgut', color: '#F59E0B', defaultW: 4, defaultH: 4, form: 'flaeche', darstellung: 'gestrichelt', opacity: 0.12, icon: AlertTriangle, props: ['klasse', 'max_menge_kg'] },
};

// ─── Customer color palette ───
const CUSTOMER_COLORS = [
 '#338edc', '#6366F1', '#22D3EE', '#06b6d4', '#8b5cf6',
 '#ec4899', '#f59e0b', '#10b981', '#ef4444', '#14b8a6',
 '#a855f7', '#f97316', '#0ea5e9', '#84cc16', '#e11d48',
];
function customerColor(idx) {
 return CUSTOMER_COLORS[idx % CUSTOMER_COLORS.length];
}

// ─── Odoo type → shelf type & display mapping ───
const ODOO_TYP_CONFIG = {
 FB: { shelfType: 'fachboden', label: 'Fachbodenregal', color: '#338edc', reihenBreite: 0.5, breite: 0.6 },
 PR: { shelfType: 'hochregal', label: 'Palettenregal', color: '#4D9CE0', reihenBreite: 1.0, breite: 1.2 },
 PBL: { shelfType: 'blocklager', label: 'Palette Blocklager', color: '#6366F1', reihenBreite: 1.2, breite: 1.2 },
 ML: { shelfType: 'blocklager', label: 'Mittellager', color: '#8b5cf6', reihenBreite: 0.8, breite: 1.0 },
 KL: { shelfType: 'fachboden', label: 'Kleinteillager', color: '#0ea5e9', reihenBreite: 0.3, breite: 0.4 },
 KLN: { shelfType: 'fachboden', label: 'Kleinteillager N', color: '#0ea5e9', reihenBreite: 0.3, breite: 0.4 },
 FC: { shelfType: 'fachboden', label: 'Flowrack', color: '#22D3EE', reihenBreite: 0.6, breite: 0.8 },
 FC2: { shelfType: 'fachboden', label: 'Flowrack 2', color: '#22D3EE', reihenBreite: 0.6, breite: 0.8 },
 FCPR: { shelfType: 'hochregal', label: 'Flowrack PR', color: '#14b8a6', reihenBreite: 1.0, breite: 1.2 },
 'DG-BL':{ shelfType: 'blocklager', label: 'DG Blocklager', color: '#a855f7', reihenBreite: 1.2, breite: 1.2 },
 RKL: { shelfType: 'fachboden', label: 'Rollregal KL', color: '#06b6d4', reihenBreite: 0.3, breite: 0.4 },
 BG: { shelfType: 'blocklager', label: 'Bodengeschoss', color: '#94a3b8', reihenBreite: 1.0, breite: 1.0 },
 HR: { shelfType: 'hochregal', label: 'Hochregal', color: '#4D9CE0', reihenBreite: 1.0, breite: 1.2 },
 R: { shelfType: 'fachboden', label: 'Regal', color: '#338edc', reihenBreite: 0.5, breite: 0.6 },
 RL: { shelfType: 'fachboden', label: 'Rollregal', color: '#06b6d4', reihenBreite: 0.4, breite: 0.5 },
 S: { shelfType: 'fachboden', label: 'Sonderregal S', color: '#64748b', reihenBreite: 0.5, breite: 0.6 },
 P: { shelfType: 'blocklager', label: 'Palette P', color: '#7c3aed', reihenBreite: 0.8, breite: 1.0 },
 OHA: { shelfType: 'fachboden', label: 'Oberhang A', color: '#f59e0b', reihenBreite: 0.6, breite: 0.8 },
 OHB: { shelfType: 'fachboden', label: 'Oberhang B', color: '#f59e0b', reihenBreite: 0.6, breite: 0.8 },
 SL: { shelfType: 'fachboden', label: 'Seitenlager', color: '#64748b', reihenBreite: 0.5, breite: 0.6 },
 SD: { shelfType: 'fachboden', label: 'Sonderlager D', color: '#64748b', reihenBreite: 0.5, breite: 0.6 },
 WB: { shelfType: 'blocklager', label: 'Warenbereich', color: '#94a3b8', reihenBreite: 0.6, breite: 0.8 },
 BLA: { shelfType: 'blocklager', label: 'Blocklager A', color: '#6366F1', reihenBreite: 1.2, breite: 1.2 },
 BLB: { shelfType: 'blocklager', label: 'Blocklager B', color: '#6366F1', reihenBreite: 1.2, breite: 1.2 },
 BLC: { shelfType: 'blocklager', label: 'Blocklager C', color: '#6366F1', reihenBreite: 1.2, breite: 1.2 },
 BLD: { shelfType: 'blocklager', label: 'Blocklager D', color: '#6366F1', reihenBreite: 1.2, breite: 1.2 },
 BLE: { shelfType: 'blocklager', label: 'Blocklager E', color: '#6366F1', reihenBreite: 1.2, breite: 1.2 },
 RET: { shelfType: 'custom', label: 'Retouren', color: '#ef4444', reihenBreite: 0.8, breite: 1.0 },
 KUNDEN: { shelfType: 'blocklager', label: 'Kundenbereich', color: '#10b981', reihenBreite: 1.0, breite: 1.2 },
 NUM: { shelfType: 'fachboden', label: 'Nummeriert', color: '#64748b', reihenBreite: 0.5, breite: 0.6 },
 SONDER: { shelfType: 'custom', label: 'Sonderplatz', color: '#94a3b8', reihenBreite: 1.0, breite: 1.0 },
};

// Odoo typ categories for toolbar buttons
const ODOO_TYP_CATEGORIES = [
 { key: 'FB', label: 'FB', shortName: 'Fachboden', icon: '▤', typs: ['FB'] },
 { key: 'PR', label: 'PR', shortName: 'Paletten', icon: '▥', typs: ['PR'] },
 { key: 'PBL', label: 'PBL', shortName: 'Block', icon: '⊞', typs: ['PBL'] },
 { key: 'ML', label: 'ML', shortName: 'Mittel', icon: '▦', typs: ['ML'] },
 { key: 'KL', label: 'KL', shortName: 'Kleinteil', icon: '⊡', typs: ['KL', 'KLN'] },
 { key: 'RL', label: 'RL', shortName: 'Rollregal', icon: '☰', typs: ['RL'] },
 { key: 'FC', label: 'FC', shortName: 'Flow', icon: '▷', typs: ['FC', 'FC2', 'FCPR'] },
 { key: 'ANDERE', label: '...', shortName: 'Weitere', icon: '…', typs: ['DG-BL', 'RKL', 'BLA', 'BLB', 'BLC', 'BLD', 'BLE', 'BG', 'HR', 'R', 'S', 'P', 'OHA', 'OHB', 'SL', 'SD', 'WB', 'RET', 'KUNDEN', 'NUM', 'SONDER'] },
];

// ─── Subtype helpers ───
// Get the default subtype for an Odoo typ from the subtypen map
function getDefaultSubtyp(subtypen, odooTyp) {
 // Try exact match first, then strip trailing digits/letters for prefix match (e.g. BLA→BL, FC2→FC)
 const candidates = [odooTyp, odooTyp.replace(/[-].*$/, ''), odooTyp.replace(/\d+$/, '')];
 for (const key of candidates) {
 const list = subtypen[key];
 if (list?.length) return list.find(s => s.ist_default) || list[0];
 }
 return null;
}

// Get subtype by ID from the flat list
function getSubtypById(subtypen, id) {
 if (!id) return null;
 for (const list of Object.values(subtypen)) {
 const found = list.find(s => s.id === id);
 if (found) return found;
 }
 return null;
}

// Calculate shelf length from subtype + reihen count
function calcShelfLength(reihenCount, subtypConfig) {
 if (!subtypConfig) return null;
 return +(reihenCount * (subtypConfig.fach_breite_cm / 100)).toFixed(2);
}

// ─── Fetch helpers ───
async function apiFetch(url, opts = {}) {
 const res = await fetch(`${API}${url}`, {
 credentials: 'include',
 headers: opts.body && !(opts.body instanceof FormData)
 ? { 'Content-Type': 'application/json', ...opts.headers }
 : opts.headers,
 ...opts,
 body: opts.body && !(opts.body instanceof FormData) ? JSON.stringify(opts.body) : opts.body,
 });
 return res.json();
}

// ─── Wizard Step Indicator ───
function StepIndicator({ steps, current, t }) {
 return (
 <div className="flex items-center gap-1 mb-6">
 {steps.map((s, i) => {
 const done = i < current;
 const active = i === current;
 return (
 <div key={i} className="flex items-center gap-1">
 {i > 0 && <div className={`w-8 h-px ${done ? 'bg-[#338edc]' : 'bg-[var(--border-card)]'}`} />}
 <div className="flex items-center gap-2">
 <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors
 ${done ? 'bg-[#338edc] text-white' : active ? 'bg-[#338edc] text-white' : 'bg-[var(--bg-inset)] text-[var(--text-tertiary)] border border-[var(--border-card)]'}`}>
 {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
 </div>
 <span className={`text-xs font-medium hidden sm:inline ${active ? 'text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'}`}>
 {t(s)}
 </span>
 </div>
 </div>
 );
 })}
 </div>
 );
}

// ─── Onboarding Tooltip ───
function Hint({ text, className = '' }) {
 const [show, setShow] = useState(true);
 if (!show) return null;
 return (
 <div className={`flex items-start gap-2 px-3 py-2.5 bg-[#338edc]/8 border border-[#338edc]/15 rounded-lg text-xs text-[var(--text-secondary)] ${className}`}>
 <Info className="w-3.5 h-3.5 text-[#338edc] shrink-0 mt-0.5" />
 <span className="flex-1">{text}</span>
 <button onClick={() => setShow(false)} className="text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]">&times;</button>
 </div>
 );
}

// ─── Map Theme Colors ───
const MAP_COLORS = {
 light: {
 bg: '#F8FAFC',
 grid: 'rgba(0,0,0,0.06)',
 grid10: 'rgba(0,0,0,0.12)',
 halleStroke: '#CBD5E1',
 label: 'rgba(30,41,59,0.7)',
 labelFaint: 'rgba(30,41,59,0.5)',
 labelFaintest: 'rgba(30,41,59,0.35)',
 dimBg: 'rgba(241,245,249,0.9)',
 slotDivider: 'rgba(0,0,0,0.1)',
 },
 dark: {
 bg: '#0f172a',
 grid: 'rgba(255,255,255,0.06)',
 grid10: 'rgba(255,255,255,0.12)',
 halleStroke: '#475569',
 label: 'rgba(255,255,255,0.7)',
 labelFaint: 'rgba(255,255,255,0.6)',
 labelFaintest: 'rgba(255,255,255,0.4)',
 dimBg: 'rgba(15,23,42,0.8)',
 slotDivider: 'rgba(255,255,255,0.15)',
 },
};

// ─── Pick-frequency color interpolation ───
const FREQ_COLORS = [[51,142,220],[34,211,238],[245,158,11],[239,68,68]];
function pickFreqColor(ratio) {
 const t = Math.max(0, Math.min(1, ratio));
 const n = FREQ_COLORS.length - 1;
 const i = Math.min(Math.floor(t * n), n - 1);
 const f = t * n - i;
 const a = FREQ_COLORS[i], b = FREQ_COLORS[i + 1];
 return `rgb(${Math.round(a[0]+(b[0]-a[0])*f)},${Math.round(a[1]+(b[1]-a[1])*f)},${Math.round(a[2]+(b[2]-a[2])*f)})`;
}

// ─── Heatmap color interpolation (green → yellow → red) ───
function getHeatColor(pct) {
 if (pct <= 0) return '#22c55e';
 if (pct >= 100) return '#ef4444';
 if (pct <= 50) {
 const t = pct / 50;
 return `rgb(${Math.round(34 + (234 - 34) * t)},${Math.round(197 + (179 - 197) * t)},${Math.round(94 + (8 - 94) * t)})`;
 }
 const t = (pct - 50) / 50;
 return `rgb(${Math.round(234 + (239 - 234) * t)},${Math.round(179 + (68 - 179) * t)},${Math.round(8 + (68 - 8) * t)})`;
}

// ─── KPI Card ───
function KpiCard({ label, value, sub }) {
 return (
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-4 flex-1 min-w-[120px]">
 <div className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] mb-1">{label}</div>
 <div className="text-xl font-bold text-[var(--text-primary)]">{value}</div>
 {sub && <div className="text-[10px] text-[var(--text-secondary)] mt-0.5">{sub}</div>}
 </div>
 );
}

// ─── Modal Wrapper ───
function Modal({ open, onClose, title, children, wide }) {
 if (!open) return null;
 return (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
 <div className={`bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl shadow-sm ${wide ? 'max-w-2xl w-full' : 'max-w-md w-full'} max-h-[85vh] overflow-y-auto`}
 onClick={e => e.stopPropagation()}>
 <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-card)]">
 <h3 className="text-sm font-bold text-[var(--text-primary)]">{title}</h3>
 <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"><X className="w-4 h-4" /></button>
 </div>
 <div className="px-5 py-4">{children}</div>
 </div>
 </div>
 );
}

// ─── Zoom detail levels (Google Maps principle) ───
function getDetailLevel(zoom) {
 if (zoom < 0.8) return 1; // Übersicht: simple rectangles
 return 2; // Reihen: colored slot strips + labels
}

// ─── Build per-slot customer color map for selected shelf ───
function buildSlotColors(locs, kundenList) {
 if (!locs || locs.length === 0) return {};
 // Group locations by reihe_nr, count kunde_id occurrences
 const slotMap = {};
 for (const loc of locs) {
 const r = loc.reihe_nr;
 if (!r) continue;
 if (!slotMap[r]) slotMap[r] = {};
 const kid = loc.kunde_id || '__frei__';
 slotMap[r][kid] = (slotMap[r][kid] || 0) + 1;
 }
 // Build unique customer ID → color index
 const uniqueKunden = [...new Set(locs.filter(l => l.kunde_id).map(l => l.kunde_id))];
 const kundeColorMap = {};
 uniqueKunden.forEach((kid, i) => { kundeColorMap[kid] = customerColor(i); });

 // For each slot, determine dominant customer
 const result = {};
 const maxSlot = Math.max(0, ...Object.keys(slotMap).map(Number));
 for (let r = 1; r <= maxSlot; r++) {
 const counts = slotMap[r];
 if (!counts) { result[r] = { type: 'frei' }; continue; }
 const entries = Object.entries(counts).filter(([k]) => k !== '__frei__');
 const freiCount = counts['__frei__'] || 0;
 if (entries.length === 0) { result[r] = { type: 'frei' }; continue; }
 entries.sort((a, b) => b[1] - a[1]);
 const total = entries.reduce((s, [, c]) => s + c, 0) + freiCount;
 if (entries.length === 1 && freiCount === 0) {
 // Single customer owns entire slot
 result[r] = { type: 'single', color: kundeColorMap[entries[0][0]], kundeId: parseInt(entries[0][0]) };
 } else if (entries.length >= 2) {
 // Mixed — show two-color split
 result[r] = { type: 'mixed', color1: kundeColorMap[entries[0][0]], color2: kundeColorMap[entries[1][0]] };
 } else {
 // Partially occupied — dominant customer
 result[r] = { type: 'partial', color: kundeColorMap[entries[0][0]], kundeId: parseInt(entries[0][0]), ratio: entries[0][1] / total };
 }
 }
 // Build legend
 const legend = uniqueKunden.map(kid => {
 const k = kundenList.find(c => c.id === kid);
 return { id: kid, name: k?.name || `#${kid}`, color: kundeColorMap[kid] };
 });
 return { slots: result, maxSlot, legend };
}

// ─── SVG Floorplan Grid with Zoom & Pan ───
function FloorplanGrid({ width, height, scale, shelves, selectedId, onSelectShelf, onMoveShelf, onMoveSegment, backgroundImage,
 bgOpacity = 0.25, locations = [], onLocationDrag, onLocationClick, selectedLocationId, compact = false, t,
 objekte = [], selectedObjektId, onSelectObjekt, onMoveObjekt, placingType, onPlaceObjekt,
 placingShelfType, onPlaceShelf, selectedShelfLocs = [], kundenList = [],
 allShelfLocs = {}, onZoomChange, mapTheme = 'light', onThemeToggle, activeLayer = 'alle', layerKunden = [], kundenLayerData = {},
 placingSegments = null }) {
 const mc = MAP_COLORS[mapTheme] || MAP_COLORS.light;
 const svgW = width * scale;
 const svgH = height * scale;
 const [dragging, setDragging] = useState(null);
 const [draggingSegInfo, setDraggingSegInfo] = useState(null); // { shelfId, segmentId }
 const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
 const [draggingLoc, setDraggingLoc] = useState(null);
 const [locDragOffset, setLocDragOffset] = useState({ x: 0, y: 0 });
 const [draggingObj, setDraggingObj] = useState(null);
 const [objDragOffset, setObjDragOffset] = useState({ x: 0, y: 0 });
 const [ghostPos, setGhostPos] = useState(null);
 const svgRef = useRef(null);
 const containerRef = useRef(null);
 const shelfClickedRef = useRef(false);

 const [zoom, setZoom] = useState(1);
 const [pan, setPan] = useState({ x: 0, y: 0 });
 const [isPanning, setIsPanning] = useState(false);
 const panStartRef = useRef({ x: 0, y: 0 });
 const detailLevel = getDetailLevel(zoom);
 const prevDetailLevelRef = useRef(detailLevel);

 // Notify parent when detail level changes (for lazy data loading)
 useEffect(() => {
 if (detailLevel !== prevDetailLevelRef.current) {
 prevDetailLevelRef.current = detailLevel;
 onZoomChange?.(zoom, detailLevel);
 }
 }, [detailLevel, zoom, onZoomChange]);

 // Flatten segmented shelves into renderable units
 const renderableShelfUnits = useMemo(() => {
 const units = [];
 for (const shelf of shelves) {
 if (shelf.segmentiert && shelf.segmente?.length > 0) {
 for (const seg of shelf.segmente) {
 units.push({
 ...shelf,
 _isSegment: true, _segmentId: seg.id, _segmentNr: seg.segment_nr,
 _reiheVon: parseInt(seg.reihe_von), _reiheBis: parseInt(seg.reihe_bis),
 pos_x: parseFloat(seg.pos_x), pos_y: parseFloat(seg.pos_y),
 length_m: parseFloat(seg.laenge_m || shelf.length_m),
 width_m: parseFloat(seg.breite_m || shelf.width_m),
 rotation: seg.rotation || 0,
 slots_per_level: parseInt(seg.reihe_bis) - parseInt(seg.reihe_von) + 1,
 _displayName: `${shelf.name} (${seg.reihe_von}-${seg.reihe_bis})`,
 });
 }
 } else {
 units.push({ ...shelf, _isSegment: false, _displayName: shelf.name });
 }
 }
 return units;
 }, [shelves]);

 const handleWheel = useCallback((e) => {
 if (compact) return;
 e.preventDefault();
 const delta = e.deltaY > 0 ? 0.9 : 1.1;
 setZoom(prev => Math.max(0.3, Math.min(5, prev * delta)));
 }, [compact]);

 const handleContainerMouseDown = useCallback((e) => {
 if (compact || dragging || draggingLoc || draggingObj || placingType || placingShelfType) return;
 if (e.button === 0) {
 setIsPanning(true);
 panStartRef.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
 }
 }, [compact, pan, dragging, draggingLoc, draggingObj, placingType, placingShelfType]);

 const handleContainerMouseMove = useCallback((e) => {
 if (isPanning) {
 setPan({ x: e.clientX - panStartRef.current.x, y: e.clientY - panStartRef.current.y });
 }
 }, [isPanning]);

 const handleContainerMouseUp = useCallback(() => { setIsPanning(false); }, []);

 useEffect(() => {
 const el = containerRef.current;
 if (!el || compact) return;
 el.addEventListener('wheel', handleWheel, { passive: false });
 return () => el.removeEventListener('wheel', handleWheel);
 }, [handleWheel, compact]);

 const resetView = useCallback(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, []);

 const gridLines = useMemo(() => {
 const lines = [];
 const w = Number(width), h = Number(height);
 // 5m grid lines only — 10m lines slightly brighter
 for (let x = 5; x < w; x += 5) {
 const is10 = x % 10 === 0;
 lines.push(<line key={`v${x}`} x1={x * scale} y1={0} x2={x * scale} y2={svgH}
 stroke={is10 ? mc.grid10 : mc.grid} strokeWidth={is10 ? 0.7 : 0.5} />);
 }
 for (let y = 5; y < h; y += 5) {
 const is10 = y % 10 === 0;
 lines.push(<line key={`h${y}`} x1={0} y1={y * scale} x2={svgW} y2={y * scale}
 stroke={is10 ? mc.grid10 : mc.grid} strokeWidth={is10 ? 0.7 : 0.5} />);
 }
 // Axis labels
 for (let x = 0; x <= w; x += 10) {
 lines.push(<text key={`xl${x}`} x={x * scale} y={svgH + 14} fill="var(--text-tertiary)" fontSize={9} textAnchor="middle">{x}m</text>);
 }
 for (let y = 0; y <= h; y += 10) {
 lines.push(<text key={`yl${y}`} x={-6} y={y * scale + 3} fill="var(--text-tertiary)" fontSize={9} textAnchor="end">{Math.round(h - y)}m</text>);
 }
 return lines;
 }, [width, height, scale, svgW, svgH, mc]);

 function getSvgPoint(e) {
 const svg = svgRef.current;
 if (!svg) return { x: 0, y: 0 };
 const rect = svg.getBoundingClientRect();
 const viewBox = svg.viewBox.baseVal;
 const sx = viewBox.width / rect.width;
 const sy = viewBox.height / rect.height;
 return { x: (e.clientX - rect.left) * sx + viewBox.x, y: (e.clientY - rect.top) * sy + viewBox.y };
 }

 function handleShelfMouseDown(e, shelf, segmentId) {
 e.stopPropagation();
 shelfClickedRef.current = true;
 onSelectShelf(shelf.id);
 const pt = getSvgPoint(e);
 setDragOffset({ x: pt.x - shelf.pos_x * scale, y: pt.y - shelf.pos_y * scale });
 if (segmentId && onMoveSegment) {
 setDraggingSegInfo({ shelfId: shelf.id, segmentId });
 } else {
 setDraggingSegInfo(null);
 }
 setDragging(shelf._isSegment ? `seg-${shelf.id}-${segmentId}` : shelf.id);
 }

 function handleLocMouseDown(e, loc) {
 if (!onLocationDrag) return;
 e.stopPropagation();
 const pt = getSvgPoint(e);
 setLocDragOffset({ x: pt.x - (loc.x_coordinate || 0) * scale, y: pt.y - (loc.y_coordinate || 0) * scale });
 setDraggingLoc(loc.id);
 }

 function handleObjektMouseDown(e, obj) {
 e.stopPropagation();
 shelfClickedRef.current = true;
 onSelectObjekt?.(obj.id);
 const pt = getSvgPoint(e);
 setObjDragOffset({ x: pt.x - obj.pos_x * scale, y: pt.y - obj.pos_y * scale });
 setDraggingObj(obj.id);
 }

 function handleMouseMove(e) {
 // Ghost preview for placement mode
 if (placingType || placingShelfType) {
 const pt = getSvgPoint(e);
 setGhostPos({ x: Math.round((pt.x / scale) * 2) / 2, y: Math.round((pt.y / scale) * 2) / 2 });
 }
 if (draggingObj && onMoveObjekt) {
 const pt = getSvgPoint(e);
 const newX = Math.max(0, Math.min(width, (pt.x - objDragOffset.x) / scale));
 const newY = Math.max(0, Math.min(height, (pt.y - objDragOffset.y) / scale));
 onMoveObjekt(draggingObj, Math.round(newX * 2) / 2, Math.round(newY * 2) / 2);
 return;
 }
 if (draggingLoc && onLocationDrag) {
 const pt = getSvgPoint(e);
 const newX = Math.max(0, Math.min(width, (pt.x - locDragOffset.x) / scale));
 const newY = Math.max(0, Math.min(height, (pt.y - locDragOffset.y) / scale));
 onLocationDrag(draggingLoc, Math.round(newX * 4) / 4, Math.round(newY * 4) / 4, false);
 return;
 }
 if (!dragging) return;
 const pt = getSvgPoint(e);
 const newX = Math.max(0, Math.min(width, (pt.x - dragOffset.x) / scale));
 const newY = Math.max(0, Math.min(height, (pt.y - dragOffset.y) / scale));
 const snappedX = Math.round(newX * 2) / 2;
 const snappedY = Math.round(newY * 2) / 2;
 if (draggingSegInfo && onMoveSegment) {
 onMoveSegment(draggingSegInfo.shelfId, draggingSegInfo.segmentId, snappedX, snappedY);
 } else {
 onMoveShelf(dragging, snappedX, snappedY);
 }
 }

 function handleMouseUp() {
 if (draggingObj) { setDraggingObj(null); }
 if (draggingLoc && onLocationDrag) { onLocationDrag(draggingLoc, null, null, true); setDraggingLoc(null); }
 setDragging(null);
 setDraggingSegInfo(null);
 }

 // ─── Layer-aware shelf appearance ───
 function getShelfAppearance(shelf) {
 if (activeLayer === 'alle') return null; // Default rendering
 if (activeLayer === 'heatmap') {
 const layerInfo = kundenLayerData[shelf.id];
 const auslastung = layerInfo ? (layerInfo.belegt / Math.max(1, layerInfo.gesamt)) * 100 : 0;
 return { fill: getHeatColor(auslastung), fillOpacity: 0.5, strokeOpacity: 0.8, label: `${Math.round(auslastung)}%` };
 }
 // Single customer layer
 const layerInfo = kundenLayerData[shelf.id];
 if (!layerInfo) return { fillOpacity: 0.04, strokeOpacity: 0.2 }; // dimmed
 const kundeMatch = layerInfo.kunden?.find(k => k.kunde_id === activeLayer);
 if (!kundeMatch) return { fillOpacity: 0.04, strokeOpacity: 0.2 }; // dimmed
 const kIdx = layerKunden.findIndex(k => k.id === activeLayer);
 return { fill: customerColor(kIdx >= 0 ? kIdx : 0), fillOpacity: 0.45, strokeOpacity: 1, label: `${kundeMatch.anteil_pct}%` };
 }

 return (
 <div ref={containerRef}
 className={`${compact ? '' : 'flex-1'} overflow-hidden relative`}
 style={{ backgroundColor: mc.bg, height: compact ? '100%' : undefined, minHeight: compact ? undefined : '400px', cursor: compact ? 'default' : (placingType || placingShelfType) ? 'crosshair' : isPanning ? 'grabbing' : ((dragging || draggingObj) ? 'grabbing' : 'grab') }}
 onMouseDown={handleContainerMouseDown}
 onMouseMove={(e) => { handleContainerMouseMove(e); handleMouseMove(e); }}
 onMouseUp={() => { handleContainerMouseUp(); handleMouseUp(); }}
 onMouseLeave={() => { handleContainerMouseUp(); handleMouseUp(); }}
 >
 <svg ref={svgRef} width="100%" height="100%" viewBox={`-20 -5 ${svgW + 40} ${svgH + 25}`}
 preserveAspectRatio="xMidYMid meet" className="select-none"
 style={{ transform: compact ? 'none' : `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: 'center center', transition: (isPanning || dragging) ? 'none' : 'transform 0.1s ease-out' }}
 onClick={(e) => {
 const pt = getSvgPoint(e);
 const x = Math.round((pt.x / scale) * 2) / 2;
 const y = Math.round((pt.y / scale) * 2) / 2;
 if (placingShelfType && onPlaceShelf) { onPlaceShelf(x, y); return; }
 if (placingType && onPlaceObjekt) { onPlaceObjekt(x, y); return; }
 if (shelfClickedRef.current) { shelfClickedRef.current = false; return; }
 if (!dragging && !draggingObj) { onSelectShelf(null); onSelectObjekt?.(null); }
 }}
 >
 {backgroundImage && <image href={backgroundImage} x={0} y={0} width={svgW} height={svgH} opacity={bgOpacity} preserveAspectRatio="xMidYMid slice" />}
 {gridLines}
 <rect x={0} y={0} width={svgW} height={svgH} fill="none" stroke={mc.halleStroke} strokeWidth={2} rx={4} />

 {renderableShelfUnits.map(shelf => {
 const unitKey = shelf._isSegment ? `seg-${shelf.id}-${shelf._segmentId}` : `s-${shelf.id}`;
 const isSelected = selectedId === shelf.id;
 const def = getShelfDef(shelf.shelf_type);
 const rot = shelf.rotation || 0;
 const sw = (rot === 90 || rot === 270) ? shelf.width_m : shelf.length_m;
 const sh = (rot === 90 || rot === 270) ? shelf.length_m : shelf.width_m;
 const color = shelf.color || def.color;
 const numSlots = shelf.slots_per_level || 5;
 const numLevels = shelf.levels || 1;
 const slotsAlongX = (rot === 0 || rot === 180);
 // Get location data: selected shelf uses selectedShelfLocs, others use allShelfLocs
 let shelfLocs = isSelected && selectedShelfLocs.length > 0
 ? selectedShelfLocs
 : (allShelfLocs[shelf.id] || []);
 // Filter locations for segments
 if (shelf._isSegment && shelfLocs.length > 0) {
 shelfLocs = shelfLocs.filter(l => {
 const r = parseInt(l.loc_reihe || l.reihe_nr || 0);
 return r >= shelf._reiheVon && r <= shelf._reiheBis;
 });
 }
 const slotData = shelfLocs.length > 0 ? buildSlotColors(shelfLocs, kundenList) : null;
 const displayW = Math.max(sw * scale, 4);
 const displayH = Math.max(sh * scale, 4);
 // Layer appearance override
 const layerApp = getShelfAppearance(shelf);
 const shelfFill = layerApp?.fill || color;
 const shelfFillOp = layerApp ? layerApp.fillOpacity : undefined;
 const shelfStrokeOp = layerApp ? layerApp.strokeOpacity : 1;

 return (
 <g key={unitKey} onMouseDown={e => {
 if (shelf._isSegment) {
 // For segments, drag moves the segment, but selection selects the parent shelf
 handleShelfMouseDown(e, shelf, shelf._segmentId);
 } else {
 handleShelfMouseDown(e, shelf);
 }
 }} onClick={e => e.stopPropagation()} className="cursor-grab">

 {/* ═══ LEVEL 1: Simple rectangle + name (zoom < 80%) ═══ */}
 {detailLevel === 1 && (<>
 <rect x={shelf.pos_x * scale} y={shelf.pos_y * scale} width={displayW} height={displayH}
 fill={shelfFill} fillOpacity={shelfFillOp ?? (isSelected ? 0.3 : 0.15)} stroke={shelfFill} strokeWidth={isSelected ? 2.5 : 1.2} strokeOpacity={shelfStrokeOp} rx={3}
 onMouseEnter={e => { if (!isSelected) { e.currentTarget.setAttribute('fill-opacity', '0.25'); e.currentTarget.setAttribute('stroke-width', '1.8'); } }}
 onMouseLeave={e => { if (!isSelected) { e.currentTarget.setAttribute('fill-opacity', String(shelfFillOp ?? 0.15)); e.currentTarget.setAttribute('stroke-width', '1.2'); } }} />
 <text x={shelf.pos_x * scale + displayW / 2} y={shelf.pos_y * scale + displayH / 2 + 3}
 fill={mc.label} fontSize={Math.max(7, Math.min(10, displayW / 6))} fontWeight={500} textAnchor="middle" fontFamily="inherit">
 {layerApp?.label || shelf._displayName || shelf.name}
 </text>
 </>)}

 {/* ═══ LEVEL 2: Reihen with customer colors (zoom 80-200%) ═══ */}
 {detailLevel === 2 && (<>
 <rect x={shelf.pos_x * scale} y={shelf.pos_y * scale} width={displayW} height={displayH}
 fill={shelfFill} fillOpacity={shelfFillOp ?? (isSelected ? 0.3 : 0.12)} stroke={shelfFill} strokeWidth={isSelected ? 2.5 : 0.8} strokeOpacity={shelfStrokeOp} rx={2}
 onMouseEnter={e => { if (!isSelected) { e.currentTarget.setAttribute('fill-opacity', '0.2'); e.currentTarget.setAttribute('stroke-width', '1.5'); } }}
 onMouseLeave={e => { if (!isSelected) { e.currentTarget.setAttribute('fill-opacity', String(shelfFillOp ?? 0.12)); e.currentTarget.setAttribute('stroke-width', '0.8'); } }} />
 {/* Slot color strips — always shown at Level 2 (not just when selected) */}
 {slotData?.slots && (() => {
 const strips = [];
 const slots = slotData.slots;
 const maxSlot = slotData.maxSlot || numSlots;
 const n = Math.max(numSlots, maxSlot);
 for (let r = 1; r <= n; r++) {
 const info = slots[r];
 if (!info || info.type === 'frei') {
 const stripColor = '#64748b';
 if (slotsAlongX) {
 strips.push(<rect key={`s${r}`} x={(shelf.pos_x + (r - 1) * sw / n) * scale + 0.5} y={shelf.pos_y * scale + 0.5}
 width={(sw / n) * scale - 1} height={sh * scale - 1} fill={stripColor} fillOpacity={0.1} rx={0.5} />);
 } else {
 strips.push(<rect key={`s${r}`} x={shelf.pos_x * scale + 0.5} y={(shelf.pos_y + (r - 1) * sh / n) * scale + 0.5}
 width={sw * scale - 1} height={(sh / n) * scale - 1} fill={stripColor} fillOpacity={0.1} rx={0.5} />);
 }
 } else if (info.type === 'single' || info.type === 'partial') {
 const c = info.color;
 const op = info.type === 'partial' ? 0.25 + info.ratio * 0.2 : 0.4;
 if (slotsAlongX) {
 strips.push(<rect key={`s${r}`} x={(shelf.pos_x + (r - 1) * sw / n) * scale + 0.5} y={shelf.pos_y * scale + 0.5}
 width={(sw / n) * scale - 1} height={sh * scale - 1} fill={c} fillOpacity={op} rx={0.5} />);
 } else {
 strips.push(<rect key={`s${r}`} x={shelf.pos_x * scale + 0.5} y={(shelf.pos_y + (r - 1) * sh / n) * scale + 0.5}
 width={sw * scale - 1} height={(sh / n) * scale - 1} fill={c} fillOpacity={op} rx={0.5} />);
 }
 } else if (info.type === 'mixed') {
 if (slotsAlongX) {
 const sx = (shelf.pos_x + (r - 1) * sw / n) * scale + 0.5;
 const sW = (sw / n) * scale - 1;
 const half = sh * scale / 2;
 strips.push(<rect key={`s${r}a`} x={sx} y={shelf.pos_y * scale + 0.5} width={sW} height={half - 0.5} fill={info.color1} fillOpacity={0.4} rx={0.5} />);
 strips.push(<rect key={`s${r}b`} x={sx} y={shelf.pos_y * scale + half + 0.5} width={sW} height={half - 0.5} fill={info.color2} fillOpacity={0.4} rx={0.5} />);
 } else {
 const sy = (shelf.pos_y + (r - 1) * sh / n) * scale + 0.5;
 const sH = (sh / n) * scale - 1;
 const half = sw * scale / 2;
 strips.push(<rect key={`s${r}a`} x={shelf.pos_x * scale + 0.5} y={sy} width={half - 0.5} height={sH} fill={info.color1} fillOpacity={0.4} rx={0.5} />);
 strips.push(<rect key={`s${r}b`} x={shelf.pos_x * scale + half + 0.5} y={sy} width={half - 0.5} height={sH} fill={info.color2} fillOpacity={0.4} rx={0.5} />);
 }
 }
 }
 return strips;
 })()}
 {/* Label above shelf */}
 <text x={shelf.pos_x * scale + displayW / 2} y={shelf.pos_y * scale - 3}
 fill={mc.labelFaint} fontSize={8} fontWeight={500} textAnchor="middle" fontFamily="inherit">
 {layerApp?.label ? `${shelf._displayName || shelf.name} ${layerApp.label}` : (shelf._displayName || shelf.name)}
 </text>
 </>)}

 </g>
 );
 })}

 {/* Dashed connection lines between segments of same shelf */}
 {shelves.filter(s => s.segmentiert && s.segmente?.length > 1).map(shelf => {
 const segs = [...shelf.segmente].sort((a, b) => a.segment_nr - b.segment_nr);
 const lines = [];
 for (let i = 0; i < segs.length - 1; i++) {
 const a = segs[i], b = segs[i + 1];
 const ax = (parseFloat(a.pos_x) + parseFloat(a.laenge_m || shelf.length_m) / 2) * scale;
 const ay = (parseFloat(a.pos_y) + parseFloat(a.breite_m || shelf.width_m) / 2) * scale;
 const bx = (parseFloat(b.pos_x) + parseFloat(b.laenge_m || shelf.length_m) / 2) * scale;
 const by = (parseFloat(b.pos_y) + parseFloat(b.breite_m || shelf.width_m) / 2) * scale;
 lines.push(
 <line key={`segline-${shelf.id}-${i}`} x1={ax} y1={ay} x2={bx} y2={by}
 stroke={shelf.color || '#338edc'} strokeWidth={1} strokeDasharray="4 3" opacity={0.4} />
 );
 }
 return <g key={`seglines-${shelf.id}`}>{lines}</g>;
 })}

 {/* Already-placed segments during placement flow */}
 {placingSegments?.placed?.map((seg, i) => {
 const cfg = ODOO_TYP_CONFIG[placingSegments.gruppe?.typ] || ODOO_TYP_CONFIG.SONDER;
 const w = (seg.laenge_m || 5) * scale;
 const h = (seg.breite_m || cfg.breite || 0.6) * scale;
 return (
 <rect key={`placed-seg-${i}`} x={seg.pos_x * scale} y={seg.pos_y * scale} width={w} height={h}
 fill={cfg.color} fillOpacity={0.25} stroke={cfg.color} strokeWidth={1.5} rx={2} />
 );
 })}

 {/* Objekte rendering */}
 {objekte.map(obj => {
 const def = OBJEKT_TYPEN[obj.typ];
 if (!def) return null;
 const isSelected = selectedObjektId === obj.id;
 const color = obj.farbe || def.color;
 const w = (obj.laenge_m || def.defaultW) * scale;
 const h = (obj.breite_m || def.defaultH) * scale;
 const x = obj.pos_x * scale;
 const y = obj.pos_y * scale;
 const isDashed = def.darstellung === 'gestrichelt';
 return (
 <g key={`obj-${obj.id}`} onMouseDown={e => handleObjektMouseDown(e, obj)} onClick={e => e.stopPropagation()} className="cursor-grab">
 <rect x={x} y={y} width={w} height={h}
 fill={color} fillOpacity={isSelected ? Math.max(def.opacity, 0.25) : def.opacity}
 stroke={color} strokeWidth={isSelected ? 2.5 : (def.form === 'linie' ? 2 : 1.2)}
 strokeDasharray={isDashed ? '6 3' : 'none'} rx={def.form === 'linie' ? 1 : 3} />
 {obj.typ === 'gefahrgut' && (
 <text x={x + w / 2} y={y + h / 2 - 2} fill={color} fontSize={Math.min(16, w / 2)} textAnchor="middle" dominantBaseline="central" opacity={0.7}>⚠</text>
 )}
 <text x={x + w / 2} y={y + h / 2 + (obj.typ === 'gefahrgut' ? 8 : 0.3 * scale)}
 fill={color} fontSize={Math.min(10, w / 4)} fontWeight={600} textAnchor="middle" fontFamily="inherit"
 opacity={def.form === 'linie' ? 0 : 0.9}>
 {obj.name}
 </text>
 {def.form === 'linie' && (
 <text x={x + w / 2} y={y - 4} fill={color} fontSize={8} fontWeight={600} textAnchor="middle" fontFamily="inherit" opacity={0.8}>
 {obj.name}
 </text>
 )}
 {/* Selection indicator — subtle border highlight, no resize handles */}
 {isSelected && (
 <rect x={x - 2} y={y - 2} width={w + 4} height={h + 4}
 fill="none" stroke={color} strokeWidth={1} strokeDasharray="4 2" rx={4} opacity={0.5} />
 )}
 </g>
 );
 })}

 {/* Ghost preview for placement mode */}
 {placingShelfType && ghostPos && (() => {
 // Segment placement: use current segment dimensions
 if (placingSegments) {
 const seg = placingSegments.segments[placingSegments.currentIndex];
 const cfg = ODOO_TYP_CONFIG[placingSegments.gruppe?.typ] || ODOO_TYP_CONFIG.SONDER;
 const w = (seg?.laenge_m || 5) * scale;
 const h = (seg?.breite_m || cfg.breite || 0.6) * scale;
 return (
 <rect x={ghostPos.x * scale} y={ghostPos.y * scale} width={w} height={h}
 fill={cfg.color} fillOpacity={0.15} stroke={cfg.color} strokeWidth={2}
 strokeDasharray="6 3" rx={2} pointerEvents="none" />
 );
 }
 const def = getShelfDef(placingShelfType);
 const w = def.defaultW * scale;
 const h = def.defaultH * scale;
 return (
 <rect x={ghostPos.x * scale} y={ghostPos.y * scale} width={w} height={h}
 fill={def.color} fillOpacity={0.2} stroke={def.color} strokeWidth={2}
 strokeDasharray="6 3" rx={2} pointerEvents="none" />
 );
 })()}

 {placingType && ghostPos && OBJEKT_TYPEN[placingType] && (() => {
 const def = OBJEKT_TYPEN[placingType];
 const w = def.defaultW * scale;
 const h = def.defaultH * scale;
 return (
 <rect x={ghostPos.x * scale} y={ghostPos.y * scale} width={w} height={h}
 fill={def.color} fillOpacity={0.15} stroke={def.color} strokeWidth={1.5}
 strokeDasharray="4 4" rx={3} pointerEvents="none" />
 );
 })()}

 {locations.length > 0 && (() => {
 const maxPicks = Math.max(1, ...locations.map(l => l.pick_count_30d || 0));
 return locations.map(loc => {
 if (loc.x_coordinate == null || loc.y_coordinate == null) return null;
 const ratio = (loc.pick_count_30d || 0) / maxPicks;
 const color = pickFreqColor(ratio);
 const isSel = selectedLocationId === loc.id;
 const isDrag = draggingLoc === loc.id;
 return (
 <g key={loc.id} onMouseDown={e => handleLocMouseDown(e, loc)}
 onClick={e => { e.stopPropagation(); onLocationClick?.(loc); }} className="cursor-pointer">
 <circle cx={loc.x_coordinate * scale} cy={loc.y_coordinate * scale}
 r={isSel || isDrag ? 5 : 3.5} fill={color} stroke={isSel ? '#fff' : 'none'} strokeWidth={isSel ? 1.5 : 0} opacity={isDrag ? 1 : 0.85} />
 {isSel && <text x={loc.x_coordinate * scale + 8} y={loc.y_coordinate * scale + 3}
 fill="#e2e8f0" fontSize={8} fontWeight={600} fontFamily="inherit">{loc.location_name}</text>}
 </g>
 );
 });
 })()}
 </svg>

 {!compact && (
 <div className="absolute top-3 right-3 flex flex-col gap-1 z-10">
 <button onClick={() => setZoom(z => Math.min(z * 1.2, 5))}
 className="w-8 h-8 bg-white/90 border border-[var(--border-default,#E5E7EB)] rounded-lg flex items-center justify-center text-[#1A1A2E] hover:bg-white text-sm font-bold"
 title={t?.('warehouse.zoomIn') || 'Zoom in'}>+</button>
 <div className="w-8 h-8 bg-white/90 border border-[var(--border-default,#E5E7EB)] rounded-lg flex items-center justify-center text-[10px] text-[#6B7280] font-mono">
 {Math.round(zoom * 100)}%</div>
 <button onClick={() => setZoom(z => Math.max(z / 1.2, 0.3))}
 className="w-8 h-8 bg-white/90 border border-[var(--border-default,#E5E7EB)] rounded-lg flex items-center justify-center text-[#1A1A2E] hover:bg-white text-sm font-bold"
 title={t?.('warehouse.zoomOut') || 'Zoom out'}>−</button>
 <button onClick={resetView}
 className="w-8 h-8 bg-white/90 border border-[var(--border-default,#E5E7EB)] rounded-lg flex items-center justify-center text-[#6B7280] hover:bg-white mt-1 text-sm"
 title={t?.('warehouse.resetView') || 'Reset view'}>⟲</button>
 {/* Zoom detail level indicator */}
 <div className="mt-1 px-1 py-0.5 bg-white/90 border border-[var(--border-default,#E5E7EB)] rounded-lg text-center">
 <div className="text-[9px] text-[#6B7280] font-medium leading-tight">
 {detailLevel === 1 && (t?.('warehouse.levelUebersicht') || 'Übersicht')}
 {detailLevel === 2 && (t?.('warehouse.levelReihen') || 'Reihen')}
 </div>
 </div>
 </div>
 )}
 {/* Map theme toggle — bottom right */}
 {!compact && (
 <button
 onClick={() => {
 const next = mapTheme === 'light' ? 'dark' : 'light';
 onThemeToggle?.(next);
 }}
 className="absolute bottom-3 right-3 w-8 h-8 bg-white/90 border border-[var(--border-default,#E5E7EB)] rounded-lg flex items-center justify-center text-[#6B7280] hover:bg-white z-10 text-sm"
 title={mapTheme === 'light' ? (t?.('warehouse.mapThemeDark') || 'Dark') : (t?.('warehouse.mapThemeLight') || 'Light')}>
 {mapTheme === 'light' ? '🌙' : '☀️'}
 </button>
 )}

 <div className="absolute bottom-3 left-3 text-[10px] font-mono text-[var(--text-tertiary)] px-2 py-1 rounded-lg" style={{ backgroundColor: mc.dimBg }}>
 {width}m x {height}m
 </div>
 </div>
 );
}

// ─── Side View: Ebenen × Fächer Grid ───
function ShelfSideView({ shelf, locations, kunden, onAssign, onClose, warehouseId, t }) {
 const [selected, setSelected] = useState(new Set());
 const [assignKundeId, setAssignKundeId] = useState('');
 const [shiftStart, setShiftStart] = useState(null);
 const [viewMode, setViewMode] = useState('zuordnung'); // zuordnung | pickdaten
 const [pickData, setPickData] = useState(null);
 const [pickLoading, setPickLoading] = useState(false);
 const [hoveredCell, setHoveredCell] = useState(null);

 // Load pick data when switching to pickdaten view
 useEffect(() => {
 if (viewMode !== 'pickdaten' || !shelf || !warehouseId) return;
 if (pickData) return; // Already loaded
 (async () => {
 setPickLoading(true);
 try {
 const res = await fetch(`${API}/api/warehouse/${warehouseId}/shelves/${shelf.id}/location-customers?tage=30`, { credentials: 'include' });
 const data = await res.json();
 setPickData(data);
 } catch { setPickData({ locations: {} }); }
 setPickLoading(false);
 })();
 }, [viewMode, shelf, warehouseId]);

 if (!shelf) return null;

 const levels = shelf.levels || 1;
 const slotsPerLevel = shelf.slots_per_level || 1;
 const def = getShelfDef(shelf.shelf_type);

 // Build a grid map: [ebene][fach] → location
 const grid = {};
 for (const loc of locations) {
 if (!grid[loc.level_nr]) grid[loc.level_nr] = {};
 grid[loc.level_nr][loc.reihe_nr] = loc;
 }

 // Build customer color map from manual assignment
 const kundeColorMap = {};
 const kundeIds = [...new Set(locations.filter(l => l.kunde_id).map(l => l.kunde_id))];
 kundeIds.forEach((kid, i) => { kundeColorMap[kid] = customerColor(i); });

 // Build pick-based color map — unique customers from pick data
 const pickCustomerMap = {};
 const pickCustomerList = [];
 if (pickData?.locations) {
 const allKundeIds = new Set();
 for (const locCusts of Object.values(pickData.locations)) {
 for (const c of locCusts) allKundeIds.add(c.kunde_id);
 }
 let idx = 0;
 for (const kid of allKundeIds) {
 pickCustomerMap[kid] = customerColor(idx++);
 const cust = pickData.locations && Object.values(pickData.locations).flat().find(c => c.kunde_id === kid);
 pickCustomerList.push({ id: kid, name: cust?.kunde_name || `#${kid}`, color: pickCustomerMap[kid] });
 }
 }

 function handleCellClick(e, ebene, reihe) {
 if (viewMode === 'pickdaten') return; // No selection in pick view
 const loc = grid[ebene]?.[reihe];
 if (!loc) return;

 if (e.shiftKey && shiftStart) {
 const newSelected = new Set(selected);
 const minE = Math.min(shiftStart.e, ebene), maxE = Math.max(shiftStart.e, ebene);
 const minF = Math.min(shiftStart.r, reihe), maxF = Math.max(shiftStart.r, reihe);
 for (let e2 = minE; e2 <= maxE; e2++) {
 for (let f2 = minF; f2 <= maxF; f2++) {
 const l = grid[e2]?.[f2];
 if (l) newSelected.add(l.id);
 }
 }
 setSelected(newSelected);
 } else {
 const newSelected = new Set();
 if (!selected.has(loc.id)) newSelected.add(loc.id);
 setSelected(newSelected);
 setShiftStart({ e: ebene, r: reihe });
 }
 }

 async function assignSelected() {
 if (selected.size === 0) return;
 for (const locId of selected) {
 await onAssign(locId, assignKundeId ? parseInt(assignKundeId) : null);
 }
 setSelected(new Set());
 }

 // Customer legend (assignment-based)
 const legend = kundeIds.map(kid => {
 const k = kunden.find(c => c.id === kid);
 return { id: kid, name: k?.name || `#${kid}`, color: kundeColorMap[kid] };
 });

 const belegt = locations.filter(l => l.status === 'belegt').length;
 const reserviert = locations.filter(l => l.status === 'reserviert').length;
 const frei = locations.filter(l => l.status === 'frei' || !l.status).length;

 // Cell rendering helper
 function renderCell(ebene, reihe, isNB) {
 const loc = grid[ebene]?.[reihe];

 if (viewMode === 'pickdaten') {
 // Pick-data view: color by dominant customer from pick stats
 const locCusts = loc && pickData?.locations?.[loc.location_name];
 const dominant = locCusts?.[0];
 const isMulti = locCusts && locCusts.length > 1;
 const bgColor = dominant ? pickCustomerMap[dominant.kunde_id] : null;
 const totalPicks = locCusts ? locCusts.reduce((s, c) => s + c.picks, 0) : 0;

 const tooltipParts = [loc?.location_name || ''];
 if (locCusts) {
 for (const c of locCusts.slice(0, 3)) {
 tooltipParts.push(`${c.kunde_name}: ${c.picks} (${c.pct}%)`);
 }
 if (locCusts.length > 3) tooltipParts.push(`+${locCusts.length - 3} more`);
 } else {
 tooltipParts.push(t('warehouse.keinePicks'));
 }

 return (
 <div key={reihe}
 onMouseEnter={() => setHoveredCell({ ebene, reihe, loc, custs: locCusts })}
 onMouseLeave={() => setHoveredCell(null)}
 className={`flex-1 min-w-[28px] h-6 rounded-sm border relative
 ${!bgColor ? 'border-[var(--border-card)] bg-[var(--bg-inset)]' : 'border-transparent'}`}
 style={bgColor ? { backgroundColor: bgColor + '35', borderColor: bgColor + '60' } : isNB ? { background: 'repeating-linear-gradient(45deg, transparent, transparent 3px, var(--border-card) 3px, var(--border-card) 4px)' } : {}}
 title={tooltipParts.join('\n')}>
 {isMulti && (
 <div className="absolute top-0 right-0 w-0 h-0 border-t-[6px] border-r-[6px] border-t-white/80 border-r-transparent"
 style={{ borderTopColor: 'var(--text-tertiary)' }} />
 )}
 {totalPicks > 0 && (
 <span className="absolute inset-0 flex items-center justify-center text-[7px] font-mono text-[var(--text-tertiary)] opacity-70">
 {totalPicks > 999 ? `${Math.round(totalPicks / 1000)}k` : totalPicks > 0 ? totalPicks : ''}
 </span>
 )}
 </div>
 );
 }

 // Zuordnung view (original)
 const isSel = loc && selected.has(loc.id);
 const bgColor = loc?.kunde_id ? kundeColorMap[loc.kunde_id] : 'transparent';
 const isEmpty = !loc?.kunde_id;
 return (
 <div key={reihe}
 onClick={e => handleCellClick(e, ebene, reihe)}
 className={`flex-1 min-w-[28px] h-6 rounded-sm border cursor-pointer transition-all
 ${isSel ? 'ring-2 ring-[#338edc] ring-offset-1' : ''}
 ${isEmpty ? 'border-[var(--border-card)] hover:border-[#338edc]/40' : 'border-transparent'}
 ${isNB && isEmpty ? '' : isEmpty ? 'bg-[var(--bg-inset)]' : ''}`}
 style={isEmpty
 ? isNB ? { background: 'repeating-linear-gradient(45deg, transparent, transparent 3px, var(--border-card) 3px, var(--border-card) 4px)' } : {}
 : { backgroundColor: bgColor + '30', borderColor: bgColor + '60' }}
 title={loc ? `${loc.location_name}${loc.kunde_name ? ' — ' + loc.kunde_name : ''} (${loc.status || 'frei'})` : ''}
 />
 );
 }

 return (
 <div className="space-y-3">
 <div className="flex items-center gap-3">
 <div className="w-3 h-3 rounded" style={{ backgroundColor: shelf.color || def.color }} />
 <span className="text-xs text-[var(--text-tertiary)]">{t(`warehouse.config.shelfTypes.${shelf.shelf_type}`)}</span>
 <span className="text-[10px] text-[var(--text-secondary)] ml-auto">
 {locations.length} {t('warehouse.platzeGesamt')}: {belegt} {t('warehouse.belegt')}, {reserviert} {t('warehouse.reserviert')}, {frei} {t('warehouse.frei')}
 </span>
 </div>

 {/* View mode toggle */}
 <div className="flex gap-1 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-card)]">
 {[['zuordnung', t('warehouse.zuordnung')], ['pickdaten', t('warehouse.pickDaten')]].map(([key, label]) => (
 <button key={key} onClick={() => setViewMode(key)}
 className={`flex-1 px-3 py-1 text-[11px] font-medium rounded-[8px] transition-colors
 ${viewMode === key ? 'bg-white dark:bg-[var(--bg-card)] shadow-sm text-[var(--text-primary)]' : 'text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]'}`}>
 {label}
 </button>
 ))}
 </div>

 {/* Pick data loading */}
 {viewMode === 'pickdaten' && pickLoading && (
 <div className="flex items-center gap-2 py-4 justify-center text-xs text-[var(--text-tertiary)]">
 <div className="w-3 h-3 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
 {t('warehouse.ladePickdaten')}
 </div>
 )}

 {/* Grid: rows=levels (bottom to top), cols=slots — with gang separators for segmented shelves */}
 {!(viewMode === 'pickdaten' && pickLoading) && (() => {
 // Build column sequence: reihen + optional gang separators between segments
 const cols = [];
 if (shelf.segmentiert && shelf.segmente?.length > 1) {
 const sortedSegs = [...shelf.segmente].sort((a, b) => parseInt(a.reihe_von) - parseInt(b.reihe_von));
 for (let si = 0; si < sortedSegs.length; si++) {
 const seg = sortedSegs[si];
 const von = parseInt(seg.reihe_von), bis = parseInt(seg.reihe_bis);
 for (let r = von; r <= bis; r++) cols.push({ type: 'reihe', nr: r });
 if (si < sortedSegs.length - 1) cols.push({ type: 'gang', segNr: si + 1 });
 }
 } else {
 for (let f = 1; f <= slotsPerLevel; f++) cols.push({ type: 'reihe', nr: f });
 }
 // Find segment label ranges for header
 const segRanges = shelf.segmentiert && shelf.segmente?.length > 1
 ? [...shelf.segmente].sort((a, b) => parseInt(a.reihe_von) - parseInt(b.reihe_von))
 : null;
 return (
 <div className="overflow-x-auto">
 <div className="inline-block min-w-full">
 {/* Segment labels row */}
 {segRanges && (
 <div className="flex items-center gap-px mb-1">
 <div className="w-8 shrink-0" />
 {segRanges.map((seg, si) => {
 const count = parseInt(seg.reihe_bis) - parseInt(seg.reihe_von) + 1;
 return (
 <div key={si} className="contents">
 <div className="text-center text-[8px] font-semibold text-[#338edc]"
 style={{ flex: `${count} 0 0`, minWidth: count * 28 + (count - 1) }}>
 {t('warehouse.segmente')} {seg.segment_nr} (R{seg.reihe_von}–R{seg.reihe_bis})
 </div>
 {si < segRanges.length - 1 && (
 <div className="w-8 shrink-0 text-center text-[7px] font-medium text-[var(--text-tertiary)] uppercase tracking-wide">
 {t('warehouse.gang')}
 </div>
 )}
 </div>
 );
 })}
 </div>
 )}
 {/* Column headers */}
 <div className="flex items-center gap-px mb-px">
 <div className="w-8 shrink-0" />
 {cols.map((col, ci) => col.type === 'gang' ? (
 <div key={`gang-${ci}`} className="w-8 shrink-0" />
 ) : (
 <div key={ci} className="flex-1 min-w-[28px] text-center text-[8px] text-[var(--text-tertiary)] font-mono">
 R{String(col.nr).padStart(2, '0')}
 </div>
 ))}
 </div>

 {/* Rows: top = highest level */}
 {Array.from({ length: levels }, (_, li) => {
 const ebene = levels - li;
 const isNB = ebene === levels && shelf.nachbestuckung_ebene && shelf.shelf_type === 'fachboden';
 const levelLabel = isNB ? 'NB' : String.fromCharCode(64 + ebene);
 return (
 <div key={ebene} className="flex items-center gap-px mb-px">
 <div className={`w-8 shrink-0 text-[9px] font-mono text-right pr-1.5 ${isNB ? 'text-amber-500 font-bold' : 'text-[var(--text-tertiary)]'}`}>{levelLabel}</div>
 {cols.map((col, ci) => col.type === 'gang' ? (
 <div key={`gang-${ci}`} className="w-8 shrink-0 flex items-center justify-center">
 <div className="w-px h-6 bg-[var(--border-card)] opacity-50" style={{ backgroundImage: 'repeating-linear-gradient(to bottom, var(--border-card) 0px, var(--border-card) 2px, transparent 2px, transparent 4px)' }} />
 </div>
 ) : renderCell(ebene, col.nr, isNB))}
 </div>
 );
 })}
 </div>
 </div>
 );
 })()}

 {/* Hover tooltip for pick data */}
 {viewMode === 'pickdaten' && hoveredCell?.custs && (
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-lg px-3 py-2 text-[10px] space-y-1">
 <div className="font-semibold text-[var(--text-primary)]">{hoveredCell.loc?.location_name}</div>
 {hoveredCell.custs.map(c => (
 <div key={c.kunde_id} className="flex items-center gap-2">
 <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: pickCustomerMap[c.kunde_id] }} />
 <span className="text-[var(--text-secondary)] flex-1 truncate">{c.kunde_name}</span>
 <span className="text-[var(--text-tertiary)] font-mono">{c.picks} ({c.pct}%)</span>
 </div>
 ))}
 </div>
 )}

 {/* Legend — pick data view */}
 {viewMode === 'pickdaten' && pickCustomerList.length > 0 && (
 <div className="flex flex-wrap gap-2 text-[9px]">
 {pickCustomerList.map(l => (
 <div key={l.id} className="flex items-center gap-1">
 <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: l.color }} />
 <span className="text-[var(--text-secondary)] max-w-[120px] truncate">{l.name}</span>
 </div>
 ))}
 <div className="flex items-center gap-1 ml-1">
 <div className="w-0 h-0 border-t-[5px] border-r-[5px] border-t-[var(--text-tertiary)] border-r-transparent" />
 <span className="text-[var(--text-tertiary)]">= {t('warehouse.multiKunde')}</span>
 </div>
 </div>
 )}

 {viewMode === 'pickdaten' && !pickLoading && pickCustomerList.length === 0 && (
 <div className="text-xs text-[var(--text-tertiary)] text-center py-3">{t('warehouse.keinePickdaten')}</div>
 )}

 {/* Legend — zuordnung view */}
 {viewMode === 'zuordnung' && legend.length > 0 && (
 <div className="flex flex-wrap gap-2 text-[9px]">
 {legend.map(l => (
 <div key={l.id} className="flex items-center gap-1">
 <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: l.color }} />
 <span className="text-[var(--text-secondary)]">{l.name}</span>
 </div>
 ))}
 <div className="flex items-center gap-1">
 <div className="w-2.5 h-2.5 rounded-sm bg-[var(--bg-inset)] border border-[var(--border-card)]" />
 <span className="text-[var(--text-secondary)]">{t('warehouse.frei')}</span>
 </div>
 </div>
 )}

 {/* Assign selected — only in zuordnung mode */}
 {viewMode === 'zuordnung' && selected.size > 0 && (
 <div className="flex items-center gap-2 pt-2 border-t border-[var(--border-card)]">
 <span className="text-xs text-[var(--text-secondary)]">{selected.size} {t('warehouse.config.positioned')}</span>
 <select value={assignKundeId} onChange={e => setAssignKundeId(e.target.value)}
 className="flex-1 bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-2 py-1 text-xs text-[var(--text-primary)] focus:outline-none">
 <option value="">— {t('warehouse.frei')} —</option>
 {kunden.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
 </select>
 <button onClick={assignSelected}
 className="px-3 py-1 bg-[#338edc] text-white text-xs font-semibold rounded-lg hover:bg-[#2b7bc4]">
 {t('warehouse.bulkZuweisen')}
 </button>
 </div>
 )}
 </div>
 );
}

// ─── Error Boundary for Panel Components ───
class PanelErrorBoundary extends React.Component {
 state = { hasError: false };
 static getDerivedStateFromError() { return { hasError: true }; }
 componentDidCatch(err) { console.error('[PanelErrorBoundary]', err); }
 render() {
 if (this.state.hasError) return this.props.fallback || (
 <div className="p-4 text-xs text-red-500 text-center">Fehler beim Laden</div>
 );
 return this.props.children;
 }
}

// ─── Seitenansicht — Inline Panel Component ───
const SEITENANSICHT_COLORS = ['#DBEAFE', '#E0E7FF', '#D1FAE5', '#FEF3C7', '#FCE7F3'];
const SEITENANSICHT_TEXT = ['#1E40AF', '#3730A3', '#065F46', '#92400E', '#9D174D'];

function ShelfSeitenansicht({ shelfId, warehouseId, t, onHoverLoc, shelves }) {
 const [data, setData] = useState(null);
 const [loading, setLoading] = useState(true);
 const [subView, setSubView] = useState('kunden'); // kunden | heatmap | zuordnung
 const [hoveredCell, setHoveredCell] = useState(null);
 const [tooltipPos, setTooltipPos] = useState(null);

 useEffect(() => {
 if (!shelfId || !warehouseId) return;
 setLoading(true);
 (async () => {
 try {
 const res = await fetch(`${API}/api/warehouse/${warehouseId}/shelves/${shelfId}/seitenansicht?tage=30`, { credentials: 'include' });
 const d = await res.json();
 setData(d);
 } catch { setData(null); }
 setLoading(false);
 })();
 }, [shelfId, warehouseId]);

 if (loading) return (
 <div className="flex items-center justify-center py-12">
 <div className="w-4 h-4 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
 </div>
 );
 if (!data || data.error) return (
 <div className="text-xs text-[var(--text-tertiary)] text-center py-8">{t('warehouse.keineDaten')}</div>
 );

 const { shelf, locations, top_kunden, location_picks, max_picks, warehouse, actual_ebenen, actual_reihen } = data;
 const levels = shelf.levels || 1;
 const slotsPerLevel = shelf.slots_per_level || 1;

 // Build ebenen label map from actual location data (e.g. level_nr 9 → "NB")
 const ebenenLabelMap = {};
 if (actual_ebenen?.length) {
 for (const e of actual_ebenen) ebenenLabelMap[e.nr] = e.label;
 }
 // Build reihen label map from actual location data
 const reihenLabelMap = {};
 if (actual_reihen?.length) {
 for (const r of actual_reihen) reihenLabelMap[r.nr] = r.label;
 }

 // Build grid map
 const grid = {};
 for (const loc of locations) {
 if (!grid[loc.level_nr]) grid[loc.level_nr] = {};
 grid[loc.level_nr][loc.reihe_nr] = loc;
 }

 // Top 5 customer color map
 const top5Map = {};
 top_kunden.forEach((k, i) => {
 top5Map[k.id] = { bg: SEITENANSICHT_COLORS[i], text: SEITENANSICHT_TEXT[i], abbr: k.name.substring(0, 2).toUpperCase() };
 });

 // Build column sequence (handle segments)
 const cols = [];
 if (shelf.segmentiert && shelf.segmente?.length > 1) {
 const sortedSegs = [...shelf.segmente].sort((a, b) => parseInt(a.reihe_von) - parseInt(b.reihe_von));
 for (let si = 0; si < sortedSegs.length; si++) {
 const seg = sortedSegs[si];
 for (let r = parseInt(seg.reihe_von); r <= parseInt(seg.reihe_bis); r++) cols.push({ type: 'reihe', nr: r });
 if (si < sortedSegs.length - 1) cols.push({ type: 'gang', segNr: si + 1 });
 }
 } else {
 for (let f = 1; f <= slotsPerLevel; f++) cols.push({ type: 'reihe', nr: f });
 }

 function handleCellHover(ebene, reihe, loc, e) {
 if (!loc) { setHoveredCell(null); onHoverLoc?.(null); return; }
 setHoveredCell({ ebene, reihe, loc });
 setTooltipPos({ x: e.clientX, y: e.clientY });
 onHoverLoc?.(loc.location_name);
 }

 function renderCell(ebene, reihe) {
 const loc = grid[ebene]?.[reihe];
 const actualLabel = ebenenLabelMap[ebene];
 const isNB = actualLabel === 'NB' || (ebene === levels && shelf.nachbestuckung_ebene && shelf.shelf_type === 'fachboden');
 const locPicks = loc ? location_picks[loc.location_name] : null;
 const isHovered = hoveredCell?.loc?.id === loc?.id;

 if (subView === 'kunden') {
 // Color by dominant top-5 customer
 const dominant = locPicks?.kunden?.find(k => top5Map[k.kunde_id]);
 const custInfo = dominant ? top5Map[dominant.kunde_id] : null;
 const isSonstige = locPicks?.kunden?.length > 0 && !custInfo;
 return (
 <div key={reihe}
 onMouseEnter={e => handleCellHover(ebene, reihe, loc, e)}
 onMouseLeave={() => { setHoveredCell(null); onHoverLoc?.(null); }}
 className={`flex-1 min-w-[24px] h-5 rounded-sm border text-[7px] font-bold flex items-center justify-center transition-all
 ${isHovered ? 'ring-1 ring-[#338edc] ring-offset-1' : ''}
 ${!custInfo && !isSonstige ? 'border-[var(--border-card)] bg-[var(--bg-inset)]' : 'border-transparent'}`}
 style={custInfo ? { backgroundColor: custInfo.bg, color: custInfo.text, borderColor: custInfo.bg }
 : isSonstige ? { backgroundColor: '#F3F4F6', color: '#6B7280', borderColor: '#E5E7EB' }
 : isNB ? { background: 'repeating-linear-gradient(45deg, transparent, transparent 3px, var(--border-card) 3px, var(--border-card) 4px)' } : {}}>
 {custInfo ? custInfo.abbr : isSonstige ? '...' : ''}
 </div>
 );
 }

 if (subView === 'heatmap') {
 const total = locPicks?.total || 0;
 const intensity = max_picks > 0 ? total / max_picks : 0;
 // Gradient: transparent → light blue → blue → dark blue
 const bgColor = total === 0 ? 'var(--bg-inset)'
 : intensity < 0.25 ? '#DBEAFE'
 : intensity < 0.5 ? '#93C5FD'
 : intensity < 0.75 ? '#3B82F6'
 : '#1D4ED8';
 const textColor = intensity >= 0.5 ? '#FFF' : 'var(--text-tertiary)';
 return (
 <div key={reihe}
 onMouseEnter={e => handleCellHover(ebene, reihe, loc, e)}
 onMouseLeave={() => { setHoveredCell(null); onHoverLoc?.(null); }}
 className={`flex-1 min-w-[24px] h-5 rounded-sm border text-[7px] font-mono flex items-center justify-center transition-all
 ${isHovered ? 'ring-1 ring-[#338edc] ring-offset-1' : ''}
 ${total === 0 ? 'border-[var(--border-card)]' : 'border-transparent'}`}
 style={total > 0 ? { backgroundColor: bgColor, color: textColor } : isNB ? { background: 'repeating-linear-gradient(45deg, transparent, transparent 3px, var(--border-card) 3px, var(--border-card) 4px)' } : {}}>
 {total > 0 ? (total > 999 ? `${Math.round(total / 1000)}k` : total) : ''}
 </div>
 );
 }

 // Zuordnung: green=belegt with matching pick, yellow=belegt no recent picks, red=frei but has picks, gray=frei
 const hasPicks = locPicks && locPicks.total > 0;
 const hasKunde = loc?.kunde_id;
 const cellColor = hasKunde && hasPicks ? '#D1FAE5' // green — assigned + used
 : hasKunde && !hasPicks ? '#FEF3C7' // yellow — assigned but no picks
 : !hasKunde && hasPicks ? '#FEE2E2' // red — unassigned but used
 : 'var(--bg-inset)'; // gray — free + unused
 const borderColor = hasKunde && hasPicks ? '#6EE7B7'
 : hasKunde && !hasPicks ? '#FCD34D'
 : !hasKunde && hasPicks ? '#FCA5A5'
 : 'var(--border-card)';
 return (
 <div key={reihe}
 onMouseEnter={e => handleCellHover(ebene, reihe, loc, e)}
 onMouseLeave={() => { setHoveredCell(null); onHoverLoc?.(null); }}
 className={`flex-1 min-w-[24px] h-5 rounded-sm border text-[7px] flex items-center justify-center transition-all
 ${isHovered ? 'ring-1 ring-[#338edc] ring-offset-1' : ''}`}
 style={{ backgroundColor: cellColor, borderColor }}
 />
 );
 }

 // Position indicator
 const posIndicator = warehouse && shelf.pos_x != null ? (() => {
 const posX = parseFloat(shelf.pos_x) || 0;
 const whLen = parseFloat(warehouse.length_m) || 60;
 return (
 <div className="mb-3">
 <div className="text-[9px] text-[var(--text-tertiary)] mb-1">{t('warehouse.positionImLager')}</div>
 <div className="relative h-3 bg-[var(--bg-inset)] rounded-full border border-[var(--border-card)] overflow-hidden">
 <div className="absolute top-0 h-full w-1.5 rounded-full bg-[#338edc]"
 style={{ left: `${Math.min(95, Math.max(2, (posX / whLen) * 100))}%` }} />
 </div>
 <div className="flex justify-between text-[8px] text-[var(--text-tertiary)] mt-0.5">
 <span>0m</span>
 <span>{posX.toFixed(1)}m</span>
 <span>{whLen}m</span>
 </div>
 </div>
 );
 })() : null;

 // Segment labels
 const segRanges = shelf.segmentiert && shelf.segmente?.length > 1
 ? [...shelf.segmente].sort((a, b) => parseInt(a.reihe_von) - parseInt(b.reihe_von))
 : null;

 return (
 <div className="space-y-2">
 {/* Shelf name + stats */}
 <div className="flex items-center gap-2">
 <div className="w-2.5 h-2.5 rounded" style={{ backgroundColor: shelf.color || '#338edc' }} />
 <span className="text-xs font-semibold text-[var(--text-primary)] truncate flex-1">{shelf.name}</span>
 <span className="text-[9px] text-[var(--text-tertiary)]">{locations.length} loc</span>
 </div>

 {/* Position indicator */}
 {posIndicator}

 {/* Sub-view toggle */}
 <div className="flex gap-1 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-card)]">
 {[['kunden', t('warehouse.kunden')], ['heatmap', 'Heatmap'], ['zuordnung', t('warehouse.zuordnung')]].map(([key, label]) => (
 <button key={key} onClick={() => setSubView(key)}
 className={`flex-1 px-2 py-1 text-[10px] font-medium rounded-[8px] transition-colors
 ${subView === key ? 'bg-white dark:bg-[var(--bg-card)] shadow-sm text-[var(--text-primary)]' : 'text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]'}`}>
 {label}
 </button>
 ))}
 </div>

 {/* Grid */}
 <div className="overflow-x-auto">
 <div className="inline-block min-w-full">
 {/* Segment labels */}
 {segRanges && (
 <div className="flex items-center gap-px mb-1">
 <div className="w-6 shrink-0" />
 {segRanges.map((seg, si) => {
 const count = parseInt(seg.reihe_bis) - parseInt(seg.reihe_von) + 1;
 return (
 <div key={si} className="contents">
 <div className="text-center text-[7px] font-semibold text-[#338edc]"
 style={{ flex: `${count} 0 0`, minWidth: count * 24 + (count - 1) }}>
 S{seg.segment_nr}
 </div>
 {si < segRanges.length - 1 && <div className="w-5 shrink-0" />}
 </div>
 );
 })}
 </div>
 )}
 {/* Column headers */}
 <div className="flex items-center gap-px mb-px">
 <div className="w-6 shrink-0" />
 {cols.map((col, ci) => col.type === 'gang' ? (
 <div key={`g-${ci}`} className="w-5 shrink-0" />
 ) : (
 <div key={ci} className="flex-1 min-w-[24px] text-center text-[7px] text-[var(--text-tertiary)] font-mono">
 {reihenLabelMap[col.nr] || String(col.nr).padStart(2, '0')}
 </div>
 ))}
 </div>
 {/* Rows: top = highest level */}
 {Array.from({ length: levels }, (_, li) => {
 const ebene = levels - li;
 const actualLabel = ebenenLabelMap[ebene];
 const isNB = actualLabel === 'NB' || (ebene === levels && shelf.nachbestuckung_ebene && shelf.shelf_type === 'fachboden');
 const levelLabel = actualLabel || (isNB ? 'NB' : String.fromCharCode(64 + ebene));
 return (
 <div key={ebene} className="flex items-center gap-px mb-px">
 <div className={`w-6 shrink-0 text-[8px] font-mono text-right pr-1 ${isNB ? 'text-amber-500 font-bold' : 'text-[var(--text-tertiary)]'}`}>{levelLabel}</div>
 {cols.map((col, ci) => col.type === 'gang' ? (
 <div key={`g-${ci}`} className="w-5 shrink-0 flex items-center justify-center">
 <div className="w-px h-5 opacity-40" style={{ backgroundImage: 'repeating-linear-gradient(to bottom, var(--border-card) 0px, var(--border-card) 2px, transparent 2px, transparent 4px)' }} />
 </div>
 ) : renderCell(ebene, col.nr))}
 </div>
 );
 })}
 </div>
 </div>

 {/* Legend */}
 {subView === 'kunden' && (
 <div className="flex flex-wrap gap-1.5 text-[8px]">
 {top_kunden.map((k, i) => (
 <div key={k.id} className="flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ backgroundColor: SEITENANSICHT_COLORS[i] + '80' }}>
 <span className="font-bold" style={{ color: SEITENANSICHT_TEXT[i] }}>{k.name.substring(0, 2).toUpperCase()}</span>
 <span className="text-[var(--text-secondary)] max-w-[80px] truncate">{k.name}</span>
 </div>
 ))}
 <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#F3F4F6]">
 <span className="text-[#6B7280]">...</span>
 <span className="text-[var(--text-tertiary)]">{t('warehouse.sonstige')}</span>
 </div>
 <div className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--bg-inset)] border border-[var(--border-card)]">
 <span className="text-[var(--text-tertiary)]">{t('warehouse.frei')}</span>
 </div>
 </div>
 )}
 {subView === 'heatmap' && (
 <div className="flex items-center gap-1 text-[8px]">
 <span className="text-[var(--text-tertiary)]">{t('warehouse.wenigPicks')}</span>
 <div className="flex gap-px">
 {['#DBEAFE', '#93C5FD', '#3B82F6', '#1D4ED8'].map((c, i) => (
 <div key={i} className="w-5 h-3 rounded-sm" style={{ backgroundColor: c }} />
 ))}
 </div>
 <span className="text-[var(--text-tertiary)]">{t('warehouse.vielePicks')}</span>
 </div>
 )}
 {subView === 'zuordnung' && (
 <div className="flex flex-wrap gap-1.5 text-[8px]">
 {[
 ['#D1FAE5', '#6EE7B7', t('warehouse.zugeordnetAktiv')],
 ['#FEF3C7', '#FCD34D', t('warehouse.zugeordnetInaktiv')],
 ['#FEE2E2', '#FCA5A5', t('warehouse.nichtZugeordnetAktiv')],
 ['var(--bg-inset)', 'var(--border-card)', t('warehouse.frei')],
 ].map(([bg, border, label]) => (
 <div key={label} className="flex items-center gap-1">
 <div className="w-3 h-3 rounded-sm border" style={{ backgroundColor: bg, borderColor: border }} />
 <span className="text-[var(--text-secondary)]">{label}</span>
 </div>
 ))}
 </div>
 )}

 {/* Click tooltip */}
 {hoveredCell && (
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-lg px-3 py-2 text-[10px] space-y-1">
 <div className="font-semibold text-[var(--text-primary)]">{hoveredCell.loc.location_name}</div>
 <div className="text-[var(--text-tertiary)]">
 {hoveredCell.loc.kunde_name ? `${t('warehouse.kunde')}: ${hoveredCell.loc.kunde_name}` : t('warehouse.frei')}
 {' · '}{hoveredCell.loc.status || 'frei'}
 </div>
 {location_picks[hoveredCell.loc.location_name] && (
 <div className="space-y-0.5 pt-1 border-t border-[var(--border-card)]">
 {location_picks[hoveredCell.loc.location_name].kunden.slice(0, 5).map(c => (
 <div key={c.kunde_id} className="flex items-center gap-1.5">
 {top5Map[c.kunde_id] ? (
 <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: top5Map[c.kunde_id].bg }} />
 ) : (
 <div className="w-2 h-2 rounded-sm bg-[#F3F4F6]" />
 )}
 <span className="text-[var(--text-secondary)] flex-1 truncate">{c.kunde_name}</span>
 <span className="text-[var(--text-tertiary)] font-mono">{c.picks}</span>
 </div>
 ))}
 </div>
 )}
 </div>
 )}
 </div>
 );
}

// ─── Create Shelf Modal ───
function CreateShelfModal({ open, onClose, onCreated, warehouseId, t }) {
 const [form, setForm] = useState({
 name: 'R01',
 shelf_type: 'hochregal',
 length_m: 10,
 width_m: 1.2,
 levels: 4,
 slots_per_level: 10,
 pos_x: 5,
 pos_y: 5,
 rotation: 0,
 });
 const [creating, setCreating] = useState(false);

 function setField(k, v) {
 setForm(prev => {
 const next = { ...prev, [k]: v };
 // Auto-adjust defaults when type changes
 if (k === 'shelf_type') {
 const def = getShelfDef(v);
 next.length_m = def.defaultW;
 next.width_m = def.defaultDepth;
 next.levels = def.levels;
 next.slots_per_level = def.slots;
 next.color = def.color;
 }
 return next;
 });
 }

 const totalPlaetze = (form.levels || 1) * (form.slots_per_level || 1);
 const reiheBreite = form.slots_per_level > 0 ? Math.round(form.length_m / form.slots_per_level * 100) / 100 : 0;
 const prefix = form.name.replace(/\s+/g, '-');
 const TYP_P = { fachboden: 'FB', hochregal: 'PR', blocklager: 'BLA', packbereich: 'PB', wareneingang: 'WE', ladetor: 'LT', custom: 'CU' };
 const typP = TYP_P[form.shelf_type] || 'XX';
 const regalNr = prefix.replace(/\D/g, '').padStart(2, '0') || '01';
 const firstLoc = `${typP}-${regalNr}-01-A`;
 const lastLoc = `${typP}-${regalNr}-${String(form.slots_per_level).padStart(2, '0')}-${String.fromCharCode(64 + form.levels)}`;

 async function handleCreate() {
 setCreating(true);
 const def = getShelfDef(form.shelf_type);
 const shelf = await apiFetch(`/api/warehouse/${warehouseId}/shelves`, {
 method: 'POST',
 body: { ...form, color: form.color || def.color, odoo_prefix: prefix },
 });
 if (shelf && shelf.id) {
 // Auto-generate locations
 await apiFetch(`/api/warehouse/${warehouseId}/shelves/${shelf.id}/generate-locations`, { method: 'POST' });
 onCreated(shelf);
 }
 setCreating(false);
 onClose();
 }

 return (
 <Modal open={open} onClose={onClose} title={t('warehouse.neuesRegal')}>
 <div className="space-y-4">
 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.regalName')}</label>
 <input type="text" value={form.name} onChange={e => setField('name', e.target.value)}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.regalTyp')}</label>
 <select value={form.shelf_type} onChange={e => setField('shelf_type', e.target.value)}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]">
 {SHELF_TYPES.filter(s => ['hochregal', 'fachboden', 'blocklager'].includes(s.type)).map(s => (
 <option key={s.type} value={s.type}>{t(`warehouse.config.shelfTypes.${s.type}`)}</option>
 ))}
 </select>
 </div>
 </div>

 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.laenge')}</label>
 <NumInput value={form.length_m} onChange={v => setField('length_m', v)}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.tiefe')}</label>
 <NumInput value={form.width_m} onChange={v => setField('width_m', v)}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 </div>

 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.ebenen')}</label>
 {form.shelf_type === 'blocklager' ? (
 <div>
 <input type="number" value={1} disabled
 className="w-full bg-[var(--bg-inset)] border border-[var(--border-input)] rounded-lg px-3 py-2 text-sm text-[var(--text-tertiary)] cursor-not-allowed" />
 <span className="text-[9px] text-[var(--text-tertiary)] mt-0.5 block">{t('warehouse.blocklagerKeineEbenen')}</span>
 </div>
 ) : (
 <NumInput value={form.levels} integer onChange={v => setField('levels', v)}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 )}
 </div>
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.faecherProEbene')}</label>
 <NumInput value={form.slots_per_level} integer onChange={v => setField('slots_per_level', v)}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 </div>

 <div className="flex items-center gap-3 text-xs text-[var(--text-secondary)]">
 <span>{t('warehouse.reiheBreite')}: <strong className="text-[var(--text-primary)]">{reiheBreite}m</strong></span>
 </div>

 <div className="grid grid-cols-3 gap-3">
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.posX')}</label>
 <NumInput value={form.pos_x} onChange={v => setField('pos_x', v)}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.posY')}</label>
 <NumInput value={form.pos_y} onChange={v => setField('pos_y', v)}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.rotation')}</label>
 <select value={form.rotation} onChange={e => setField('rotation', parseInt(e.target.value))}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]">
 <option value={0}>0°</option><option value={90}>90°</option><option value={180}>180°</option><option value={270}>270°</option>
 </select>
 </div>
 </div>

 {/* Preview info */}
 <div className="bg-[var(--bg-inset)] border border-[var(--border-card)] rounded-lg p-3 space-y-1">
 <div className="flex items-center gap-2 text-xs">
 <Info className="w-3.5 h-3.5 text-[#338edc]" />
 <span className="text-[var(--text-primary)] font-medium">
 {t('warehouse.generiertePlaetze', { count: totalPlaetze })}
 </span>
 </div>
 <div className="text-[10px] text-[var(--text-secondary)] font-mono pl-5">
 {firstLoc} {t('warehouse.bezeichnungVonBis', { von: '', bis: '' })} {lastLoc}
 </div>
 </div>

 <div className="flex justify-end gap-2 pt-2">
 <button onClick={onClose}
 className="px-4 py-2 border border-[var(--border-card)] text-[var(--text-primary)] text-sm font-medium rounded-lg hover:bg-[var(--bg-card-hover)]">
 {t('warehouse.config.step1') === 'Warehouse' ? 'Cancel' : 'Abbrechen'}
 </button>
 <button onClick={handleCreate} disabled={creating || !form.name}
 className="px-4 py-2 bg-[#338edc] text-white text-sm font-semibold rounded-lg hover:bg-[#2b7bc4] disabled:opacity-50">
 {creating ? '...' : (t('warehouse.config.step1') === 'Warehouse' ? 'Create' : 'Erstellen')}
 </button>
 </div>
 </div>
 </Modal>
 );
}

// ─── Bulk Assignment Modal ───
function BulkAssignModal({ open, onClose, shelf, kunden, warehouseId, onDone, t }) {
 const [form, setForm] = useState({ ebene_von: 1, ebene_bis: 1, reihe_von: 1, reihe_bis: 1, kunde_id: '', status: 'belegt' });

 useEffect(() => {
 if (shelf) {
 setForm(prev => ({
 ...prev,
 ebene_bis: shelf.levels || 1,
 reihe_bis: shelf.slots_per_level || 1,
 }));
 }
 }, [shelf]);

 if (!shelf) return null;

 const count = Math.max(0, (form.ebene_bis - form.ebene_von + 1)) * Math.max(0, (form.reihe_bis - form.reihe_von + 1));

 async function handleAssign() {
 await apiFetch(`/api/warehouse/${warehouseId}/lagerplaetze/zuweisen`, {
 method: 'POST',
 body: { shelf_id: shelf.id, ...form, kunde_id: form.kunde_id ? parseInt(form.kunde_id) : null },
 });
 onDone();
 onClose();
 }

 return (
 <Modal open={open} onClose={onClose} title={t('warehouse.bulkZuweisen')}>
 <div className="space-y-4">
 <div className="text-xs text-[var(--text-secondary)]">
 {t('warehouse.regale')}: <strong className="text-[var(--text-primary)]">{shelf.name}</strong>
 </div>

 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.ebeneVon')}</label>
 <NumInput value={form.ebene_von} integer onChange={v => setForm(prev => ({ ...prev, ebene_von: v }))}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.ebeneBis')}</label>
 <NumInput value={form.ebene_bis} integer onChange={v => setForm(prev => ({ ...prev, ebene_bis: v }))}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 </div>

 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.reiheVon')}</label>
 <NumInput value={form.reihe_von} integer onChange={v => setForm(prev => ({ ...prev, reihe_von: v }))}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.reiheBis')}</label>
 <NumInput value={form.reihe_bis} integer onChange={v => setForm(prev => ({ ...prev, reihe_bis: v }))}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 </div>

 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">Kunde</label>
 <select value={form.kunde_id} onChange={e => setForm(prev => ({ ...prev, kunde_id: e.target.value }))}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]">
 <option value="">— {t('warehouse.frei')} —</option>
 {kunden.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
 </select>
 </div>
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">Status</label>
 <select value={form.status} onChange={e => setForm(prev => ({ ...prev, status: e.target.value }))}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]">
 <option value="belegt">{t('warehouse.belegt')}</option>
 <option value="reserviert">{t('warehouse.reserviert')}</option>
 <option value="frei">{t('warehouse.frei')}</option>
 <option value="gesperrt">{t('warehouse.gesperrt')}</option>
 </select>
 </div>
 </div>

 <div className="bg-[var(--bg-inset)] border border-[var(--border-card)] rounded-lg p-3 text-xs text-[var(--text-primary)]">
 {t('warehouse.werdenZugewiesen', { count: count })}
 </div>

 <div className="flex justify-end gap-2 pt-2">
 <button onClick={onClose}
 className="px-4 py-2 border border-[var(--border-card)] text-[var(--text-primary)] text-sm font-medium rounded-lg hover:bg-[var(--bg-card-hover)]">
 {t('warehouse.config.step1') === 'Warehouse' ? 'Cancel' : 'Abbrechen'}
 </button>
 <button onClick={handleAssign}
 className="px-4 py-2 bg-[#338edc] text-white text-sm font-semibold rounded-lg hover:bg-[#2b7bc4]">
 {t('warehouse.bulkZuweisen')}
 </button>
 </div>
 </div>
 </Modal>
 );
}

// ─── Regal Auswahl Panel (Odoo dropdown) ───
function RegalAuswahlPanel({ gruppen, onSelect, onClose, onDragStart, subtypen, subtypOverrides, onSubtypChange, t }) {
 const [search, setSearch] = useState('');
 const [sortBy, setSortBy] = useState('name'); // name | locations

 const filtered = useMemo(() => {
 let list = [...gruppen];
 if (search) {
 const q = search.toLowerCase();
 list = list.filter(g => g.regalGruppe.toLowerCase().includes(q) || (g.typ || '').toLowerCase().includes(q));
 }
 list.sort((a, b) => {
 if (sortBy === 'locations') return (b.locations || 0) - (a.locations || 0);
 return a.regalGruppe.localeCompare(b.regalGruppe);
 });
 return list;
 }, [gruppen, search, sortBy]);

 const placed = gruppen.filter(g => g.hat_shelf).length;

 return (
 <div className="flex flex-col h-full overflow-hidden"
 onClick={e => e.stopPropagation()}>
 <div className="px-3 pt-3 pb-2 border-b border-[var(--border-card)]">
 <div className="flex items-center gap-2 mb-2">
 <div className="relative flex-1">
 <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" />
 <input type="text" value={search} onChange={e => setSearch(e.target.value)}
 placeholder={t('warehouse.regalSuche')}
 className="w-full pl-8 pr-3 py-1.5 bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg text-xs text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]"
 autoFocus />
 </div>
 <button onClick={onClose} className="p-1 text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
 <X className="w-3.5 h-3.5" />
 </button>
 </div>
 <div className="flex gap-1">
 {[['name', 'A-Z'], ['locations', 'Loc']].map(([key, label]) => (
 <button key={key} onClick={() => setSortBy(key)}
 className={`px-2 py-0.5 text-[10px] font-medium rounded-lg transition-colors
 ${sortBy === key ? 'bg-[var(--bg-inset)] text-[var(--text-primary)] shadow-sm' : 'text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]'}`}>
 {label}
 </button>
 ))}
 </div>
 </div>
 <div className="flex-1 overflow-y-auto">
 {filtered.length === 0 ? (
 <div className="px-3 py-6 text-xs text-center text-[var(--text-tertiary)]">{t('warehouse.keineGruppen')}</div>
 ) : filtered.map(g => {
 const cfg = ODOO_TYP_CONFIG[g.typ] || ODOO_TYP_CONFIG.SONDER;
 const overrideId = subtypOverrides?.[g.regalGruppe];
 const st = overrideId ? getSubtypById(subtypen, parseInt(overrideId)) : getDefaultSubtyp(subtypen || {}, g.typ);
 const stList = subtypen?.[g.typ] || subtypen?.[g.typ?.replace(/[-].*$/, '')] || subtypen?.[g.typ?.replace(/\d+$/, '')] || [];
 const reihenCount = g.reihen?.length || 0;
 const calcLen = st && reihenCount > 0 ? calcShelfLength(reihenCount, st) : null;
 return (
 <div key={g.regalGruppe}
 className={`w-full px-3 py-2 text-left transition-colors border-b border-[var(--border-card)] last:border-0
 ${g.hat_shelf ? 'opacity-40 bg-[var(--bg-inset)]' : 'hover:bg-[var(--bg-card-hover)]'}`}>
 <div className={`flex items-center gap-2 ${g.hat_shelf ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'}`}
 onClick={() => !g.hat_shelf && onSelect(g, st)}
 draggable={!g.hat_shelf}
 onDragStart={e => !g.hat_shelf && onDragStart?.(e, g)}>
 <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: cfg.color }} />
 <div className="flex-1 min-w-0">
 <div className="text-xs font-semibold text-[var(--text-primary)] truncate">{g.regalGruppe}</div>
 <div className="text-[10px] text-[var(--text-tertiary)]">
 {g.locations} Loc · {g.ebenen?.length || 0} {t('warehouse.ebenen')} · {reihenCount} {t('warehouse.reihen')}
 </div>
 </div>
 {g.hat_shelf && <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}
 </div>
 {/* Subtype select + calculated dimensions */}
 {!g.hat_shelf && stList.length > 0 && (
 <div className="mt-1 ml-[18px] flex items-center gap-1.5 flex-wrap">
 <select
 value={st?.id || ''}
 onClick={e => e.stopPropagation()}
 onChange={e => { e.stopPropagation(); onSubtypChange?.(g.regalGruppe, e.target.value); }}
 className="text-[10px] bg-[var(--bg-input)] border border-[var(--border-input)] rounded px-1 py-0.5 text-[var(--text-primary)] focus:outline-none focus:border-[#338edc] max-w-[140px]">
 {stList.map(s => (
 <option key={s.id} value={s.id}>{s.label} ({s.fach_breite_cm}×{s.fach_tiefe_cm})</option>
 ))}
 </select>
 {calcLen != null && (
 <span className="text-[9px] text-[var(--text-secondary)] font-mono">→ {calcLen}m × {st.regal_tiefe_m}m</span>
 )}
 </div>
 )}
 </div>
 );
 })}
 </div>
 <div className="px-3 py-2 bg-[var(--bg-inset)] border-t border-[var(--border-card)] text-[10px] text-[var(--text-tertiary)]">
 {placed}/{gruppen.length} {t('warehouse.platziert')}
 </div>
 </div>
 );
}

// ─── Warehouse Copilot Widget ───
function WarehouseCopilot({ warehouseId, onSelectRegal, regalGruppen, panelOpen, t }) {
 const [open, setOpen] = useState(false);
 const [loading, setLoading] = useState(false);
 const [suggestion, setSuggestion] = useState(null);

 const askCopilot = async (action, context = {}) => {
 setLoading(true);
 setSuggestion(null);
 try {
 const res = await fetch(`${API}/api/warehouse/copilot/suggest`, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ action, context: { warehouse_id: warehouseId, ...context } }),
 });
 const data = await res.json();
 setSuggestion(data);
 } catch {
 setSuggestion({ vorschlag: 'Fehler beim Laden der Vorschläge.' });
 }
 setLoading(false);
 };

 // Auto-suggest on regal selection
 const suggestForRegal = useCallback((regalGruppe) => {
 askCopilot('placement_tip', { regal_gruppe: regalGruppe });
 if (!open) setOpen(true);
 }, [open, warehouseId]);

 // Show "well placed" after shelf placement
 const showPlaced = useCallback((name) => {
 setSuggestion({
 action: 'placed',
 vorschlag: t('warehouse.copilotGutPlatziert').replace('{name}', name),
 });
 if (!open) setOpen(true);
 // Auto-suggest next shelf after 2s
 setTimeout(() => askCopilot('next_shelf'), 2000);
 }, [open, warehouseId, t]);

 // Expose methods via ref-like pattern
 WarehouseCopilot._suggest = suggestForRegal;
 WarehouseCopilot._placed = showPlaced;

 const rightOffset = 'right-4';

 if (!open) {
 return (
 <button onClick={() => { setOpen(true); if (!suggestion) askCopilot('next_shelf'); }}
 className={`absolute bottom-4 ${rightOffset} z-20 w-10 h-10 rounded-full bg-[var(--bg-card)] border border-[var(--border-card)] shadow-sm flex items-center justify-center hover:border-[#338edc] transition-all`}
 title={t('warehouse.copilot')}>
 <Sparkles className="w-4 h-4 text-[#338edc]" />
 </button>
 );
 }

 return (
 <div className={`absolute bottom-4 ${rightOffset} w-80 bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl z-20 overflow-hidden transition-all`}>
 {/* Header */}
 <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--border-card)]">
 <Sparkles className="w-3.5 h-3.5 text-[#338edc]" />
 <span className="text-xs font-semibold text-[var(--text-primary)] flex-1">{t('warehouse.copilot')}</span>
 <button onClick={() => setOpen(false)} className="text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]">
 <ChevronDown className="w-3.5 h-3.5" />
 </button>
 </div>

 {/* Content */}
 <div className="p-3 max-h-60 overflow-y-auto">
 {loading && (
 <div className="flex items-center gap-2 text-xs text-[var(--text-tertiary)]">
 <div className="w-3 h-3 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
 {t('warehouse.copilotAnalysiere')}
 </div>
 )}

 {suggestion && !loading && (
 <div className="space-y-2">
 <p className="text-xs text-[var(--text-primary)]">{suggestion.vorschlag}</p>

 {/* next_shelf: clickable list */}
 {suggestion.regale && suggestion.regale.map(r => {
 const gruppe = regalGruppen.find(g => g.regalGruppe === r.loc_regal_gruppe);
 return (
 <button key={r.loc_regal_gruppe}
 onClick={() => gruppe && onSelectRegal(gruppe)}
 className="w-full text-left px-3 py-2 rounded-lg hover:bg-[var(--bg-inset)] transition-colors">
 <span className="text-xs font-semibold text-[#338edc]">{r.loc_regal_gruppe}</span>
 <span className="text-[10px] text-[var(--text-tertiary)] ml-2">
 {r.locations} Loc · {(r.picks || 0).toLocaleString()} Picks
 </span>
 </button>
 );
 })}

 {/* auto_group: list of sequences */}
 {suggestion.gruppen && suggestion.gruppen.slice(0, 8).map((seq, i) => (
 <div key={i} className="px-3 py-2 bg-[var(--bg-inset)] rounded-lg">
 <div className="text-xs font-semibold text-[var(--text-primary)]">
 {seq.typ}: {seq.regale[0]} → {seq.regale[seq.regale.length - 1]}
 </div>
 <div className="text-[10px] text-[var(--text-tertiary)]">{seq.anzahl} {t('warehouse.regale')} · {seq.tipp}</div>
 </div>
 ))}

 {/* placement_tip: co-pick partners */}
 {suggestion.co_pick_partner && suggestion.co_pick_partner.length > 0 && (
 <div className="px-3 py-2 bg-[#338edc]/5 border border-[#338edc]/15 rounded-lg">
 <div className="text-[10px] font-semibold text-[#338edc] uppercase tracking-wider mb-1">Co-Pick</div>
 {suggestion.co_pick_partner.map(p => (
 <div key={p.loc_regal_gruppe} className="text-xs text-[var(--text-primary)]">
 {p.loc_regal_gruppe} — {p.gemeinsame_waves} Waves
 </div>
 ))}
 </div>
 )}

 {suggestion.tipp && (
 <p className="text-[10px] text-[var(--text-tertiary)] italic">{suggestion.tipp}</p>
 )}
 </div>
 )}

 {!suggestion && !loading && (
 <p className="text-xs text-[var(--text-tertiary)]">{t('warehouse.copilotHint')}</p>
 )}
 </div>

 {/* Action buttons */}
 <div className="flex gap-2 p-3 border-t border-[var(--border-card)]">
 <button onClick={() => askCopilot('next_shelf')}
 className="flex-1 text-[10px] font-medium px-2 py-1.5 bg-[var(--bg-inset)] rounded-lg hover:bg-[#338edc]/10 text-[#338edc] transition-colors">
 {t('warehouse.copilotNextShelf')}
 </button>
 <button onClick={() => askCopilot('auto_group')}
 className="flex-1 text-[10px] font-medium px-2 py-1.5 bg-[var(--bg-inset)] rounded-lg hover:bg-[#338edc]/10 text-[#338edc] transition-colors">
 {t('warehouse.copilotAutoGroup')}
 </button>
 </div>
 </div>
 );
}

// ─── Kunden Layer Dropdown (compact search) ───
function KundenLayerDropdown({ layerKunden, kunden, activeLayer, setActiveLayer, layerLoading, kundenLayerData, t }) {
 const [open, setOpen] = useState(false);
 const [search, setSearch] = useState('');
 const dropRef = useRef(null);
 const inputRef = useRef(null);

 // Close on outside click
 useEffect(() => {
 if (!open) return;
 function handler(e) { if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false); }
 document.addEventListener('mousedown', handler);
 return () => document.removeEventListener('mousedown', handler);
 }, [open]);

 // Focus search when opened
 useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 50); }, [open]);

 const allKunden = layerKunden.length > 0 ? layerKunden : kunden;
 const filtered = search
 ? allKunden.filter(k => k.name?.toLowerCase().includes(search.toLowerCase()))
 : allKunden;

 // Find active customer info
 const activeKunde = typeof activeLayer === 'number'
 ? allKunden.find(k => k.id === activeLayer) : null;
 const activeIdx = activeKunde ? allKunden.indexOf(activeKunde) : -1;

 // Count locations per customer from kundenLayerData
 function getLocCount(kundeId) {
 let count = 0;
 for (const shelfId of Object.keys(kundenLayerData)) {
 const info = kundenLayerData[shelfId];
 const km = info?.kunden?.find(k => k.kunde_id === kundeId);
 if (km) count += km.anzahl || 0;
 }
 return count;
 }

 return (
 <div className="relative" ref={dropRef}>
 {/* Trigger button */}
 {activeKunde ? (
 <div className="flex items-center gap-1">
 <button onClick={() => setOpen(!open)}
 className="flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-[var(--bg-inset)] shadow-sm transition-colors">
 <div className="w-2 h-2 rounded-sm shrink-0" style={{ backgroundColor: customerColor(activeIdx >= 0 ? activeIdx : 0) }} />
 <span className="text-[10px] font-semibold text-[var(--text-primary)] max-w-[120px] truncate">{activeKunde.name}</span>
 <ChevronDown className="w-2.5 h-2.5 text-[var(--text-tertiary)]" />
 </button>
 <button onClick={() => setActiveLayer('alle')}
 className="p-0.5 rounded text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] hover:bg-[var(--bg-inset)]">
 <X className="w-3 h-3" />
 </button>
 </div>
 ) : (
 <button onClick={() => setOpen(!open)}
 className={`flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-lg whitespace-nowrap transition-colors
 ${open ? 'bg-[var(--bg-inset)] text-[var(--text-primary)]' : 'text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]'}`}>
 <Search className="w-2.5 h-2.5" />
 <span>{t('warehouse.layerKundeSuchen')}</span>
 <ChevronDown className="w-2.5 h-2.5" />
 {layerLoading && (
 <div className="w-2.5 h-2.5 border border-[#338edc] border-t-transparent rounded-full animate-spin" />
 )}
 </button>
 )}

 {/* Dropdown panel */}
 {open && (
 <div className="absolute left-0 top-full mt-1 z-30 w-[240px] bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl shadow-sm overflow-hidden">
 {/* Search input */}
 <div className="px-2.5 pt-2.5 pb-1.5">
 <div className="flex items-center gap-1.5 bg-[var(--bg-inset)] border border-[var(--border-card)] rounded-lg px-2 py-1">
 <Search className="w-3 h-3 text-[var(--text-tertiary)] shrink-0" />
 <input ref={inputRef} value={search} onChange={e => setSearch(e.target.value)}
 placeholder={t('warehouse.layerKundeSuchen')}
 className="flex-1 bg-transparent text-xs text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] outline-none" />
 {search && (
 <button onClick={() => setSearch('')} className="text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]">
 <X className="w-3 h-3" />
 </button>
 )}
 </div>
 </div>
 {/* Customer list */}
 <div className="max-h-[240px] overflow-y-auto px-1 pb-1.5">
 {filtered.length === 0 ? (
 <div className="text-[10px] text-[var(--text-tertiary)] text-center py-3">{t('warehouse.keineKundenGefunden')}</div>
 ) : filtered.map(k => {
 const origIdx = allKunden.indexOf(k);
 const isActive = activeLayer === k.id;
 const locCount = getLocCount(k.id);
 return (
 <button key={k.id} onClick={() => { setActiveLayer(isActive ? 'alle' : k.id); setOpen(false); setSearch(''); }}
 className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left transition-colors
 ${isActive ? 'bg-[#338edc]/8 text-[var(--text-primary)]' : 'hover:bg-[var(--bg-inset)] text-[var(--text-secondary)]'}`}>
 <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: customerColor(origIdx >= 0 ? origIdx : 0) }} />
 <span className="flex-1 text-[11px] font-medium truncate">{k.name}</span>
 {locCount > 0 && (
 <span className="text-[9px] text-[var(--text-tertiary)] tabular-nums">{locCount}</span>
 )}
 </button>
 );
 })}
 </div>
 </div>
 )}
 </div>
 );
}

// ─── Segmentierungs-Dialog ───
function SegmentierungDialog({ open, gruppe, reihen, shelf, subtyp, onClose, onConfirm, t }) {
 const [mode, setMode] = useState('zusammen'); // zusammen | zwei | mehrere
 const [splitRow, setSplitRow] = useState('');
 const [customSegs, setCustomSegs] = useState([]);

 // Sort reihen numerically
 const sortedReihen = useMemo(() => {
 if (!reihen || reihen.length === 0) return [];
 return [...reihen].sort((a, b) => parseInt(a) - parseInt(b));
 }, [reihen]);

 const cfg = ODOO_TYP_CONFIG[gruppe?.typ] || ODOO_TYP_CONFIG.SONDER;
 const reihenBreite = subtyp ? (subtyp.fach_breite_cm / 100) : (cfg.reihenBreite || 0.5);

 // Reset state when dialog opens
 useEffect(() => {
 if (open && sortedReihen.length > 0) {
 setMode('zusammen');
 const mid = Math.floor(sortedReihen.length / 2);
 setSplitRow(sortedReihen[mid] || '');
 setCustomSegs([
 { von: sortedReihen[0], bis: sortedReihen[sortedReihen.length - 1] }
 ]);
 }
 }, [open, sortedReihen]);

 // Calculate segment dimensions for "zwei" mode
 const twoSegments = useMemo(() => {
 if (!splitRow || sortedReihen.length < 2) return null;
 const splitIdx = sortedReihen.indexOf(splitRow);
 if (splitIdx < 0) return null;
 const seg1Reihen = sortedReihen.slice(0, splitIdx);
 const seg2Reihen = sortedReihen.slice(splitIdx);
 if (seg1Reihen.length === 0 || seg2Reihen.length === 0) return null;
 return [
 { reihe_von: seg1Reihen[0], reihe_bis: seg1Reihen[seg1Reihen.length - 1], count: seg1Reihen.length, laenge_m: +(seg1Reihen.length * reihenBreite).toFixed(2) },
 { reihe_von: seg2Reihen[0], reihe_bis: seg2Reihen[seg2Reihen.length - 1], count: seg2Reihen.length, laenge_m: +(seg2Reihen.length * reihenBreite).toFixed(2) },
 ];
 }, [splitRow, sortedReihen, reihenBreite]);

 // Validation for "mehrere" mode
 const customValid = useMemo(() => {
 if (mode !== 'mehrere') return true;
 if (customSegs.length < 2) return false;
 const allValid = customSegs.every(s => s.von && s.bis && parseInt(s.bis) >= parseInt(s.von));
 if (!allValid) return false;
 // Check for overlaps
 const sorted = [...customSegs].sort((a, b) => parseInt(a.von) - parseInt(b.von));
 for (let i = 1; i < sorted.length; i++) {
 if (parseInt(sorted[i].von) <= parseInt(sorted[i - 1].bis)) return false;
 }
 return true;
 }, [customSegs, mode]);

 // Count assigned/unassigned rows in "mehrere"
 const assignedRows = useMemo(() => {
 const assigned = new Set();
 for (const s of customSegs) {
 if (!s.von || !s.bis) continue;
 for (let r = parseInt(s.von); r <= parseInt(s.bis); r++) {
 assigned.add(String(r).padStart(2, '0'));
 }
 }
 return assigned;
 }, [customSegs]);

 if (!open) return null;

 function handleConfirm() {
 if (mode === 'zusammen') {
 onConfirm([]);
 return;
 }
 if (mode === 'zwei' && twoSegments) {
 onConfirm(twoSegments.map((s, i) => ({
 reihe_von: s.reihe_von, reihe_bis: s.reihe_bis,
 laenge_m: s.laenge_m, breite_m: cfg.breite || 0.6,
 })));
 return;
 }
 if (mode === 'mehrere') {
 const segs = customSegs.filter(s => s.von && s.bis).map(s => ({
 reihe_von: s.von, reihe_bis: s.bis,
 laenge_m: +((parseInt(s.bis) - parseInt(s.von) + 1) * reihenBreite).toFixed(2),
 breite_m: cfg.breite || 0.6,
 }));
 if (segs.length < 2) return;
 onConfirm(segs);
 }
 }

 function addCustomSeg() {
 setCustomSegs(prev => [...prev, { von: '', bis: '' }]);
 }

 function removeCustomSeg(idx) {
 setCustomSegs(prev => prev.filter((_, i) => i !== idx));
 }

 function updateCustomSeg(idx, field, value) {
 setCustomSegs(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
 }

 return (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl shadow-sm max-w-lg w-full max-h-[85vh] overflow-y-auto"
 onClick={e => e.stopPropagation()}>
 <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-card)]">
 <div className="flex items-center gap-2">
 <SplitSquareHorizontal className="w-4 h-4 text-[#338edc]" />
 <h3 className="text-sm font-bold text-[var(--text-primary)]">
 {t('warehouse.segmentierung')} — {gruppe?.regalGruppe || shelf?.name}
 </h3>
 </div>
 <button onClick={onClose} className="text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"><X className="w-4 h-4" /></button>
 </div>
 <div className="px-5 py-4 space-y-4">
 <p className="text-xs text-[var(--text-secondary)]">{t('warehouse.segmentierungDesc')}</p>
 <div className="text-[10px] text-[var(--text-tertiary)]">{sortedReihen.length} {t('warehouse.reihen')} ({sortedReihen[0]} – {sortedReihen[sortedReihen.length - 1]})</div>

 {/* Mode selector */}
 <div className="space-y-2">
 {[
 { key: 'zusammen', label: t('warehouse.segmentZusammen'), desc: t('warehouse.segmentZusammenDesc') },
 { key: 'zwei', label: t('warehouse.segmentZwei'), desc: t('warehouse.segmentZweiDesc') },
 { key: 'mehrere', label: t('warehouse.segmentMehrere'), desc: t('warehouse.segmentMehrereDesc') },
 ].map(opt => (
 <button key={opt.key} onClick={() => setMode(opt.key)}
 className={`w-full text-left px-4 py-3 rounded-xl border transition-colors
 ${mode === opt.key ? 'border-[#338edc] bg-[#338edc]/5' : 'border-[var(--border-card)] hover:border-[var(--text-tertiary)]'}`}>
 <div className="text-xs font-semibold text-[var(--text-primary)]">{opt.label}</div>
 <div className="text-[10px] text-[var(--text-secondary)] mt-0.5">{opt.desc}</div>
 </button>
 ))}
 </div>

 {/* Two-segment split */}
 {mode === 'zwei' && (
 <div className="space-y-3">
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.segmentSplitNach')}</label>
 <select value={splitRow} onChange={e => setSplitRow(e.target.value)}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]">
 {sortedReihen.slice(1).map(r => (
 <option key={r} value={r}>{t('warehouse.segmentVon')} {r}</option>
 ))}
 </select>
 </div>
 {twoSegments && (
 <div className="grid grid-cols-2 gap-2">
 {twoSegments.map((seg, i) => (
 <div key={i} className="bg-[var(--bg-inset)] border border-[var(--border-card)] rounded-lg px-3 py-2">
 <div className="text-[10px] font-bold text-[#338edc]">{t('warehouse.segmente')} {i + 1}</div>
 <div className="text-xs text-[var(--text-primary)] mt-1">{seg.reihe_von} – {seg.reihe_bis}</div>
 <div className="text-[10px] text-[var(--text-tertiary)]">{seg.count} {t('warehouse.reihen')} · {seg.laenge_m}m</div>
 </div>
 ))}
 </div>
 )}
 </div>
 )}

 {/* Multiple segments */}
 {mode === 'mehrere' && (
 <div className="space-y-2">
 {customSegs.map((seg, i) => (
 <div key={i} className="flex items-center gap-2">
 <span className="text-[10px] font-bold text-[var(--text-tertiary)] w-4">{i + 1}.</span>
 <select value={seg.von} onChange={e => updateCustomSeg(i, 'von', e.target.value)}
 className="flex-1 bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-2 py-1 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]">
 <option value="">{t('warehouse.segmentVon')}</option>
 {sortedReihen.map(r => <option key={r} value={r}>{r}</option>)}
 </select>
 <span className="text-[10px] text-[var(--text-tertiary)]">–</span>
 <select value={seg.bis} onChange={e => updateCustomSeg(i, 'bis', e.target.value)}
 className="flex-1 bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-2 py-1 text-xs text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]">
 <option value="">{t('warehouse.segmentBis')}</option>
 {sortedReihen.map(r => <option key={r} value={r}>{r}</option>)}
 </select>
 {seg.von && seg.bis && (
 <span className="text-[10px] text-[var(--text-tertiary)] w-10 text-right">
 {+((parseInt(seg.bis) - parseInt(seg.von) + 1) * reihenBreite).toFixed(1)}m
 </span>
 )}
 <button onClick={() => removeCustomSeg(i)} className="text-red-400 hover:text-red-500">
 <Minus className="w-3.5 h-3.5" />
 </button>
 </div>
 ))}
 <button onClick={addCustomSeg}
 className="flex items-center gap-1 text-[11px] text-[#338edc] hover:text-[#2b7bc4] font-medium">
 <Plus className="w-3 h-3" /> {t('warehouse.segmentHinzufuegen')}
 </button>
 {sortedReihen.length - assignedRows.size > 0 && (
 <div className="text-[10px] text-amber-600">{t('warehouse.nichtZugewiesen').replace('{count}', sortedReihen.length - assignedRows.size)}</div>
 )}
 </div>
 )}
 </div>
 <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[var(--border-card)]">
 <button onClick={onClose}
 className="px-4 py-1.5 text-xs font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] rounded-lg hover:bg-[var(--bg-inset)] transition-colors">
 {t('warehouse.config.step1') === 'Warehouse' ? 'Cancel' : 'Abbrechen'}
 </button>
 <button onClick={handleConfirm}
 disabled={mode === 'mehrere' && !customValid}
 className="px-4 py-1.5 text-xs font-semibold text-white bg-[#338edc] rounded-lg hover:bg-[#2b7bc4] disabled:opacity-40 transition-colors">
 {t('warehouse.config.step1') === 'Warehouse' ? 'Confirm' : 'Bestätigen'}
 </button>
 </div>
 </div>
 </div>
 );
}

// ─── Subtypen Verwaltung (CRUD) ───
function SubtypenVerwaltung({ subtypen, onReload, t }) {
 const [editItem, setEditItem] = useState(null); // null or { id?, typ, subtyp, label_de, label_en, fach_breite_cm, fach_tiefe_cm, regal_tiefe_m, ist_default }
 const [saving, setSaving] = useState(false);
 const [confirmDelete, setConfirmDelete] = useState(null);

 const allTypes = Object.keys(subtypen).sort();

 async function handleSave() {
 if (!editItem) return;
 setSaving(true);
 const body = {
 typ: editItem.typ, subtyp: editItem.subtyp,
 label_de: editItem.label_de, label_en: editItem.label_en,
 fach_breite_cm: parseFloat(editItem.fach_breite_cm) || 100,
 fach_tiefe_cm: parseFloat(editItem.fach_tiefe_cm) || 40,
 regal_tiefe_m: parseFloat(editItem.regal_tiefe_m) || 0.6,
 ist_default: !!editItem.ist_default,
 };
 if (editItem.id) {
 await apiFetch(`/api/warehouse/subtypen/${editItem.id}`, { method: 'PUT', body });
 } else {
 await apiFetch('/api/warehouse/subtypen', { method: 'POST', body });
 }
 setSaving(false);
 setEditItem(null);
 onReload();
 }

 async function handleDelete(id) {
 await apiFetch(`/api/warehouse/subtypen/${id}`, { method: 'DELETE' });
 setConfirmDelete(null);
 onReload();
 }

 return (
 <div className="flex-1 flex flex-col min-h-0 gap-4 p-4 overflow-y-auto">
 <div className="flex items-center justify-between">
 <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('warehouse.subtypVerwaltung') || 'Regal-Subtypen'}</h3>
 <button onClick={() => setEditItem({ typ: 'FB', subtyp: '', label_de: '', label_en: '', fach_breite_cm: 100, fach_tiefe_cm: 40, regal_tiefe_m: 0.6, ist_default: false })}
 className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-[#338edc] hover:bg-[#338edc]/5 rounded-lg transition-colors">
 <Plus className="w-3.5 h-3.5" /> {t('warehouse.subtypHinzufuegen') || 'Subtyp hinzufügen'}
 </button>
 </div>

 {allTypes.length === 0 ? (
 <div className="text-sm text-[var(--text-tertiary)] text-center py-8">{t('warehouse.keineSubtypen') || 'Keine Subtypen vorhanden'}</div>
 ) : allTypes.map(typ => {
 const list = subtypen[typ] || [];
 const cfg = ODOO_TYP_CONFIG[typ] || ODOO_TYP_CONFIG.SONDER;
 return (
 <div key={typ} className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl">
 {/* Typ-Header */}
 <div className="flex items-center gap-3 px-4 py-3">
 <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cfg.color }} />
 <span className="text-sm font-medium" style={{ color: '#1A1A2E' }}>{typ}</span>
 <span className="text-sm" style={{ color: '#6B7280' }}>{cfg.label}</span>
 <span className="text-xs ml-auto" style={{ color: '#9CA3AF' }}>{list.length} {list.length === 1 ? 'Subtyp' : 'Subtypen'}</span>
 </div>
 {/* Subtyp-Einträge — eingerückt mit visueller Verbindung */}
 <div className="ml-8 pl-4 border-l-2 border-[var(--border-table,#F3F4F6)] pb-2">
 {list.map(st => (
 <div key={st.id} className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-[var(--bg-inset,#F8F9FC)] transition-colors">
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2">
 <span className="text-sm font-medium" style={{ color: '#1A1A2E' }}>{st.label}</span>
 {st.ist_default && (
 <span className="text-[10px] font-medium px-2 py-0.5 rounded-full"
 style={{ backgroundColor: '#EBF4FD', color: '#338edc' }}>Default</span>
 )}
 </div>
 <div className="text-xs mt-0.5" style={{ color: '#9CA3AF' }}>
 {st.fach_breite_cm}×{st.fach_tiefe_cm}cm · {t('warehouse.regalTiefe') || 'Tiefe'} {st.regal_tiefe_m}m · {st.subtyp}
 </div>
 </div>
 <button onClick={() => setEditItem({
 id: st.id, typ: st.typ, subtyp: st.subtyp,
 label_de: st.label_de || '', label_en: st.label_en || '',
 fach_breite_cm: st.fach_breite_cm, fach_tiefe_cm: st.fach_tiefe_cm,
 regal_tiefe_m: st.regal_tiefe_m, ist_default: st.ist_default,
 })}
 className="p-1 hover:text-[#338edc] transition-colors" style={{ color: '#9CA3AF' }}>
 <Pencil className="w-3.5 h-3.5" />
 </button>
 <button onClick={() => setConfirmDelete(st.id)}
 className="p-1 hover:text-red-500 transition-colors" style={{ color: '#9CA3AF' }}>
 <Trash2 className="w-3.5 h-3.5" />
 </button>
 </div>
 ))}
 </div>
 </div>
 );
 })}

 {/* Edit / Create Modal */}
 {editItem && (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setEditItem(null)}>
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl shadow-xl w-[420px] max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
 <div className="px-5 py-4 border-b border-[var(--border-card)]">
 <h3 className="text-sm font-bold text-[var(--text-primary)]">
 {editItem.id ? (t('warehouse.subtypBearbeiten') || 'Subtyp bearbeiten') : (t('warehouse.subtypHinzufuegen') || 'Neuer Subtyp')}
 </h3>
 </div>
 <div className="px-5 py-4 space-y-3">
 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.typ') || 'Typ'}</label>
 <input type="text" value={editItem.typ} onChange={e => setEditItem(prev => ({ ...prev, typ: e.target.value.toUpperCase() }))}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]"
 placeholder="FB, PR, KL..." />
 </div>
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.subtypKey') || 'Subtyp-Key'}</label>
 <input type="text" value={editItem.subtyp} onChange={e => setEditItem(prev => ({ ...prev, subtyp: e.target.value }))}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]"
 placeholder="standard, breit, schmal..." />
 </div>
 </div>
 <div className="grid grid-cols-2 gap-3">
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.labelDe') || 'Label DE'}</label>
 <input type="text" value={editItem.label_de} onChange={e => setEditItem(prev => ({ ...prev, label_de: e.target.value }))}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.labelEn') || 'Label EN'}</label>
 <input type="text" value={editItem.label_en} onChange={e => setEditItem(prev => ({ ...prev, label_en: e.target.value }))}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 </div>
 <div className="grid grid-cols-3 gap-3">
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.fachBreite') || 'Fachbreite (cm)'}</label>
 <NumInput value={editItem.fach_breite_cm} integer onChange={v => setEditItem(prev => ({ ...prev, fach_breite_cm: v }))}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.fachTiefe') || 'Fachtiefe (cm)'}</label>
 <NumInput value={editItem.fach_tiefe_cm} integer onChange={v => setEditItem(prev => ({ ...prev, fach_tiefe_cm: v }))}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.regalTiefe') || 'Regaltiefe (m)'}</label>
 <NumInput value={editItem.regal_tiefe_m} onChange={v => setEditItem(prev => ({ ...prev, regal_tiefe_m: v }))}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 </div>
 <label className="flex items-center gap-2 cursor-pointer">
 <input type="checkbox" checked={!!editItem.ist_default} onChange={e => setEditItem(prev => ({ ...prev, ist_default: e.target.checked }))}
 className="w-4 h-4 rounded border-[var(--border-input)] text-[#338edc] focus:ring-[#338edc]" />
 <span className="text-xs text-[var(--text-primary)]">{t('warehouse.istDefault') || 'Standard-Subtyp für diesen Typ'}</span>
 </label>
 </div>
 <div className="flex justify-end gap-2 px-5 py-3 border-t border-[var(--border-card)]">
 <button onClick={() => setEditItem(null)}
 className="px-4 py-1.5 text-xs font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] rounded-lg hover:bg-[var(--bg-inset)] transition-colors">
 {t('common.cancel') || 'Abbrechen'}
 </button>
 <button onClick={handleSave} disabled={saving || !editItem.typ || !editItem.subtyp}
 className="px-4 py-1.5 text-xs font-semibold text-white bg-[#338edc] rounded-lg hover:bg-[#2b7bc4] disabled:opacity-40 transition-colors">
 {saving ? '...' : (t('common.save') || 'Speichern')}
 </button>
 </div>
 </div>
 </div>
 )}

 {/* Delete confirmation */}
 {confirmDelete && (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setConfirmDelete(null)}>
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl shadow-xl w-[340px] p-5" onClick={e => e.stopPropagation()}>
 <p className="text-sm text-[var(--text-primary)] mb-4">{t('warehouse.subtypLoeschenFrage') || 'Diesen Subtyp wirklich löschen?'}</p>
 <div className="flex justify-end gap-2">
 <button onClick={() => setConfirmDelete(null)}
 className="px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] rounded-lg hover:bg-[var(--bg-inset)]">
 {t('common.cancel') || 'Abbrechen'}
 </button>
 <button onClick={() => handleDelete(confirmDelete)}
 className="px-3 py-1.5 text-xs font-semibold text-white bg-red-500 rounded-lg hover:bg-red-600">
 {t('common.delete') || 'Löschen'}
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 );
}

// ─── Shelf Properties Panel ───
function ShelfProperties({ shelf, onUpdate, onDelete, onRotate, onEditSegments, onUpdateSegment, subtypen, t }) {
 if (!shelf) return null;

 const def = getShelfDef(shelf.shelf_type);

 return (
 <div className="space-y-3">
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-4 pt-3">
 <div className="flex items-center gap-2 mb-3 pr-5">
 <div className="w-3 h-3 rounded" style={{ backgroundColor: shelf.color || def.color }} />
 <span className="text-sm font-semibold text-[var(--text-primary)]">{shelf.name}</span>
 <span className="text-[10px] text-[var(--text-tertiary)] ml-auto">{parseInt(shelf.location_count || 0)} loc</span>
 </div>

 <div className="space-y-3">
 {/* Position X/Y — always editable */}
 <div className="grid grid-cols-2 gap-2">
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">X (m)</label>
 <NumInput value={shelf.pos_x ?? 0} onChange={v => onUpdate({ pos_x: v })}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">Y (m)</label>
 <NumInput value={shelf.pos_y ?? 0} onChange={v => onUpdate({ pos_y: v })}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 </div>
 {shelf.odoo_regal_gruppe ? (
 <>
 {/* Odoo-linked shelf: read-only info */}
 <div className="bg-[#338edc]/5 border border-[#338edc]/20 rounded-lg px-3 py-2">
 <div className="text-[10px] font-semibold text-[#338edc] uppercase tracking-wider mb-1">Odoo</div>
 <div className="text-xs text-[var(--text-primary)] font-semibold">{shelf.odoo_regal_gruppe}</div>
 <div className="text-[10px] text-[var(--text-tertiary)] mt-0.5">
 {(ODOO_TYP_CONFIG[shelf.odoo_regal_gruppe?.replace(/\d+/g, '').replace(/-/g, '')] || ODOO_TYP_CONFIG.SONDER)?.label || shelf.shelf_type}
 </div>
 </div>
 {/* Subtype dropdown — changeable after placement */}
 {(() => {
 const odooTyp = shelf.odoo_regal_gruppe?.replace(/\d+/g, '').replace(/-/g, '') || '';
 const candidates = [odooTyp, odooTyp.replace(/[-].*$/, ''), odooTyp.replace(/\d+$/, '')];
 let stList = [];
 for (const key of candidates) { stList = subtypen?.[key] || []; if (stList.length) break; }
 if (!stList.length) return null;
 return (
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.subtyp')}</label>
 <select value={shelf.subtyp_id || ''} onChange={e => {
 const newId = e.target.value ? parseInt(e.target.value) : null;
 const st = newId ? stList.find(s => s.id === newId) : null;
 const updates = { subtyp_id: newId };
 // Auto-recalculate length from subtype
 if (st && shelf.slots_per_level > 0) {
 updates.length_m = calcShelfLength(shelf.slots_per_level, st);
 updates.width_m = st.regal_tiefe_m;
 }
 onUpdate(updates);
 }}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]">
 <option value="">{t('warehouse.keinSubtyp')}</option>
 {stList.map(s => (
 <option key={s.id} value={s.id}>{s.label} ({s.fach_breite_cm}×{s.fach_tiefe_cm}cm)</option>
 ))}
 </select>
 {shelf.subtyp_id && shelf.subtyp_label_de && (
 <div className="text-[9px] text-[var(--text-tertiary)] mt-0.5">
 {shelf.fach_breite_cm}×{shelf.fach_tiefe_cm}cm · {t('warehouse.regalTiefe')} {shelf.regal_tiefe_m}m
 </div>
 )}
 </div>
 );
 })()}
 <div className="grid grid-cols-2 gap-2">
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.config.shelfLength')}</label>
 <NumInput value={shelf.length_m} onChange={v => onUpdate({ length_m: v })}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 {(() => {
 const st = shelf.subtyp_id ? getSubtypById(subtypen, shelf.subtyp_id) : null;
 if (!st || !shelf.slots_per_level) return null;
 const expected = calcShelfLength(shelf.slots_per_level, st);
 if (Math.abs(expected - parseFloat(shelf.length_m)) < 0.05) return null;
 return (
 <button onClick={() => onUpdate({ length_m: expected })}
 className="text-[9px] text-amber-500 hover:text-amber-600 mt-0.5 block">
 ⚠ {t('warehouse.laenge_auto_berechnet')}: {expected}m
 </button>
 );
 })()}
 </div>
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.config.shelfWidth')}</label>
 <NumInput value={shelf.width_m} onChange={v => onUpdate({ width_m: v })}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 </div>
 <div className="grid grid-cols-2 gap-2">
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.config.shelfLevels')}</label>
 <NumInput value={shelf.levels} integer onChange={v => onUpdate({ levels: v })}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.config.shelfSlotsPerLevel')}</label>
 <NumInput value={shelf.slots_per_level} integer onChange={v => {
 const updates = { slots_per_level: v };
 const st = shelf.subtyp_id ? getSubtypById(subtypen, shelf.subtyp_id) : null;
 if (st && v > 0) updates.length_m = calcShelfLength(v, st);
 onUpdate(updates);
 }}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 </div>
 </>
 ) : (
 <>
 {/* Manual shelf: all editable */}
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.config.shelfName')}</label>
 <input type="text" value={shelf.name} onChange={e => onUpdate({ name: e.target.value })}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 <div className="grid grid-cols-2 gap-2">
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.config.shelfLength')}</label>
 <NumInput value={shelf.length_m} onChange={v => onUpdate({ length_m: v })}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 {(() => {
 const st = shelf.subtyp_id ? getSubtypById(subtypen, shelf.subtyp_id) : null;
 if (!st || !shelf.slots_per_level) return null;
 const expected = calcShelfLength(shelf.slots_per_level, st);
 if (Math.abs(expected - parseFloat(shelf.length_m)) < 0.05) return null;
 return (
 <button onClick={() => onUpdate({ length_m: expected })}
 className="text-[9px] text-amber-500 hover:text-amber-600 mt-0.5 block">
 ⚠ {t('warehouse.laenge_auto_berechnet')}: {expected}m
 </button>
 );
 })()}
 </div>
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.config.shelfWidth')}</label>
 <NumInput value={shelf.width_m} onChange={v => onUpdate({ width_m: v })}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 </div>
 <div className="grid grid-cols-2 gap-2">
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.config.shelfLevels')}</label>
 {shelf.shelf_type === 'blocklager' ? (
 <div>
 <input type="number" value={1} disabled
 className="w-full bg-[var(--bg-inset)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-tertiary)] cursor-not-allowed" />
 <span className="text-[9px] text-[var(--text-tertiary)] mt-0.5 block">{t('warehouse.blocklagerKeineEbenen')}</span>
 </div>
 ) : (
 <NumInput value={shelf.levels} integer onChange={v => onUpdate({ levels: v })}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 )}
 </div>
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.config.shelfSlotsPerLevel')}</label>
 <NumInput value={shelf.slots_per_level} integer onChange={v => {
 const updates = { slots_per_level: v };
 const st = shelf.subtyp_id ? getSubtypById(subtypen, shelf.subtyp_id) : null;
 if (st && v > 0) updates.length_m = calcShelfLength(v, st);
 onUpdate(updates);
 }}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 </div>
 {shelf.shelf_type === 'fachboden' && (
 <label className="flex items-center gap-2 cursor-pointer">
 <input type="checkbox" checked={!!shelf.nachbestuckung_ebene}
 onChange={e => onUpdate({ nachbestuckung_ebene: e.target.checked })}
 className="w-4 h-4 rounded border-[var(--border-input)] text-[#338edc] focus:ring-[#338edc] cursor-pointer" />
 <span className="text-[11px] text-[var(--text-secondary)]">{t('warehouse.nachbestuckungEbene')}</span>
 </label>
 )}
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.config.shelfPrefix')}</label>
 <input type="text" value={shelf.odoo_prefix || ''} placeholder="z.B. R01"
 onChange={e => onUpdate({ odoo_prefix: e.target.value })}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 </>
 )}
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.config.shelfRotation')}</label>
 <div className="flex items-center gap-2">
 <select value={shelf.rotation || 0} onChange={e => onUpdate({ rotation: parseInt(e.target.value) })}
 className="flex-1 bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]">
 <option value={0}>0°</option><option value={90}>90°</option><option value={180}>180°</option><option value={270}>270°</option>
 </select>
 <button onClick={onRotate}
 className="w-8 h-8 flex items-center justify-center rounded-lg border border-[var(--border-card)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-card-hover)]">
 <RotateCw className="w-4 h-4" />
 </button>
 </div>
 </div>
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">Farbe</label>
 <div className="flex items-center gap-2">
 <input type="color" value={shelf.color || def.color} onChange={e => onUpdate({ color: e.target.value })}
 className="w-8 h-8 rounded-lg border border-[var(--border-card)] cursor-pointer" />
 <span className="text-xs text-[var(--text-tertiary)] font-mono">{shelf.color || def.color}</span>
 </div>
 </div>
 </div>
 </div>

 {/* Segments display — editable per-segment positions */}
 {shelf.segmentiert && shelf.segmente?.length > 0 && (
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-4 pt-3">
 <div className="flex items-center justify-between mb-2">
 <span className="text-[11px] font-semibold text-[var(--text-secondary)]">{t('warehouse.segmente')} ({shelf.segmente.length})</span>
 {onEditSegments && (
 <button onClick={onEditSegments} className="text-[10px] text-[#338edc] hover:underline font-medium">
 {t('warehouse.segmenteBearbeiten')}
 </button>
 )}
 </div>
 <div className="space-y-3">
 {shelf.segmente.map((seg, i) => (
 <div key={seg.id || i} className="bg-[var(--bg-inset)] rounded-xl px-3 py-2.5 space-y-2">
 <div className="flex items-center justify-between">
 <span className="text-[10px] font-bold text-[#338edc]">{t('warehouse.segmente')} {seg.segment_nr} (R{seg.reihe_von}–R{seg.reihe_bis})</span>
 <span className="text-[10px] text-[var(--text-tertiary)]">
 {parseInt(seg.reihe_bis) - parseInt(seg.reihe_von) + 1} {t('warehouse.reihen')}
 </span>
 </div>
 {/* Position X/Y */}
 <div className="grid grid-cols-2 gap-1.5">
 <div>
 <label className="text-[9px] font-medium text-[var(--text-tertiary)] mb-0.5 block">X (m)</label>
 <NumInput value={parseFloat(seg.pos_x) || 0} onChange={v => onUpdateSegment?.(shelf.id, seg.id, { pos_x: v })}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-2 py-1 text-[11px] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 <div>
 <label className="text-[9px] font-medium text-[var(--text-tertiary)] mb-0.5 block">Y (m)</label>
 <NumInput value={parseFloat(seg.pos_y) || 0} onChange={v => onUpdateSegment?.(shelf.id, seg.id, { pos_y: v })}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-2 py-1 text-[11px] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 </div>
 {/* Dimensions */}
 <div className="grid grid-cols-3 gap-1.5">
 <div>
 <label className="text-[9px] font-medium text-[var(--text-tertiary)] mb-0.5 block">{t('warehouse.laenge')}</label>
 <NumInput value={parseFloat(seg.laenge_m) || 0} onChange={v => onUpdateSegment?.(shelf.id, seg.id, { laenge_m: v })}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-2 py-1 text-[11px] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 <div>
 <label className="text-[9px] font-medium text-[var(--text-tertiary)] mb-0.5 block">{t('warehouse.tiefe')}</label>
 <NumInput value={parseFloat(seg.breite_m) || 0} onChange={v => onUpdateSegment?.(shelf.id, seg.id, { breite_m: v })}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-2 py-1 text-[11px] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 <div>
 <label className="text-[9px] font-medium text-[var(--text-tertiary)] mb-0.5 block">Rot.</label>
 <select value={seg.rotation || 0}
 onChange={e => onUpdateSegment?.(shelf.id, seg.id, { rotation: parseInt(e.target.value) })}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-1 py-1 text-[11px] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]">
 <option value={0}>0°</option><option value={90}>90°</option><option value={180}>180°</option><option value={270}>270°</option>
 </select>
 </div>
 </div>
 </div>
 ))}
 </div>
 </div>
 )}

 <div className="space-y-2">
 <button onClick={onDelete}
 className="w-full flex items-center justify-center gap-2 px-3 py-2 text-red-500 text-xs font-medium rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors">
 <Trash2 className="w-3.5 h-3.5" />
 {t('warehouse.config.step1') === 'Warehouse' ? 'Delete' : 'Löschen'}
 </button>
 </div>
 </div>
 );
}

// ─── Object Properties Panel ───
function ObjektProperties({ objekt, onUpdate, onDelete, t }) {
 if (!objekt) return null;
 const def = OBJEKT_TYPEN[objekt.typ];
 if (!def) return null;

 const props = objekt.properties || {};
 function setProp(key, val) {
 onUpdate({ properties: { ...props, [key]: val } });
 }

 const PROP_LABELS = {
 kapazitaet: 'warehouse.kapazitaet',
 dock_nummer: 'warehouse.dockNummer',
 seite: 'warehouse.seite',
 beschreibung: 'warehouse.bezeichnung',
 bezeichnung: 'warehouse.bezeichnung',
 tor_nummer: 'warehouse.torNummer',
 klasse: 'warehouse.gefahrgutKlasse',
 max_menge_kg: 'warehouse.maxMengeKg',
 };

 return (
 <div className="space-y-3">
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-2xl p-4 pt-3">
 <div className="flex items-center gap-2 mb-3 pr-5">
 <div className="w-3 h-3 rounded" style={{ backgroundColor: objekt.farbe || def.color }} />
 <span className="text-sm font-semibold text-[var(--text-primary)]">{objekt.name}</span>
 <span className="text-[10px] text-[var(--text-tertiary)] ml-auto">{t(def.label)}</span>
 </div>

 <div className="space-y-3">
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">Name</label>
 <input type="text" value={objekt.name} onChange={e => onUpdate({ name: e.target.value })}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 <div className="grid grid-cols-2 gap-2">
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.laenge')}</label>
 <NumInput value={objekt.laenge_m} onChange={v => onUpdate({ laenge_m: v })}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.tiefe')}</label>
 <NumInput value={objekt.breite_m} onChange={v => onUpdate({ breite_m: v })}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 </div>
 <div className="grid grid-cols-2 gap-2">
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.posX')}</label>
 <NumInput value={objekt.pos_x} onChange={v => onUpdate({ pos_x: v })}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">{t('warehouse.posY')}</label>
 <NumInput value={objekt.pos_y} onChange={v => onUpdate({ pos_y: v })}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 </div>
 <div>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">Farbe</label>
 <div className="flex items-center gap-2">
 <input type="color" value={objekt.farbe || def.color} onChange={e => onUpdate({ farbe: e.target.value })}
 className="w-8 h-8 rounded-lg border border-[var(--border-card)] cursor-pointer" />
 <span className="text-xs text-[var(--text-tertiary)] font-mono">{objekt.farbe || def.color}</span>
 </div>
 </div>

 {/* Type-specific properties */}
 {def.props.map(propKey => (
 <div key={propKey}>
 <label className="text-[11px] font-medium text-[var(--text-secondary)] mb-1 block">
 {t(PROP_LABELS[propKey] || propKey)}
 </label>
 <input type={propKey === 'max_menge_kg' || propKey === 'kapazitaet' ? 'number' : 'text'}
 value={props[propKey] || ''} onChange={e => setProp(propKey, e.target.value)}
 className="w-full bg-[var(--bg-input)] border border-[var(--border-input)] rounded-lg px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 </div>
 ))}
 </div>
 </div>

 <button onClick={onDelete}
 className="w-full flex items-center justify-center gap-2 px-3 py-2 text-red-500 text-xs font-medium rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors">
 <Trash2 className="w-3.5 h-3.5" />
 {t('warehouse.config.step1') === 'Warehouse' ? 'Delete' : 'Löschen'}
 </button>
 </div>
 );
}

// ═════════════════════════════════════════════
// HISTORISCHE MAP (read-only snapshot renderer)
// ═════════════════════════════════════════════
function HistorischeMap({ snapshot, diffData, scale = 8 }) {
 const data = typeof snapshot === 'string' ? JSON.parse(snapshot) : snapshot;
 if (!data) return null;

 const halle = data.halle || {};
 const w = parseFloat(halle.warehouse_length_m) || 60;
 const h = parseFloat(halle.warehouse_width_m) || 72;
 const svgW = w * scale;
 const svgH = h * scale;

 // Build diff lookup
 const diffAdded = new Set((diffData?.diff?.added_names || []).map(n => n));
 const diffRemoved = new Set((diffData?.diff?.removed_names || []).map(n => n));
 const diffMoved = new Set((diffData?.diff?.moved_details || []).map(m => m.name));

 return (
 <div className="w-full h-full overflow-auto bg-[var(--bg-inset)] p-2">
 <svg viewBox={`0 0 ${svgW} ${svgH}`} className="mx-auto" style={{ maxHeight: '100%', maxWidth: '100%' }}>
 {/* Grid */}
 <defs>
 <pattern id="hist-grid" width={scale * 5} height={scale * 5} patternUnits="userSpaceOnUse">
 <path d={`M ${scale * 5} 0 L 0 0 0 ${scale * 5}`} fill="none" stroke="var(--border-card)" strokeWidth={0.3} opacity={0.4} />
 </pattern>
 </defs>
 <rect width={svgW} height={svgH} fill="url(#hist-grid)" rx={4} />

 {/* Objects */}
 {(data.objekte || []).map(obj => (
 <rect key={`o-${obj.id}`}
 x={parseFloat(obj.pos_x) * scale} y={parseFloat(obj.pos_y) * scale}
 width={parseFloat(obj.laenge_m) * scale} height={parseFloat(obj.breite_m) * scale}
 fill={obj.farbe || '#6B7280'} fillOpacity={0.15}
 stroke={obj.farbe || '#6B7280'} strokeWidth={1} strokeDasharray="4 2" rx={3} />
 ))}

 {/* Shelves */}
 {(data.regale || []).map(shelf => {
 const sx = parseFloat(shelf.pos_x) * scale;
 const sy = parseFloat(shelf.pos_y) * scale;
 const sw = parseFloat(shelf.length_m) * scale;
 const sh = parseFloat(shelf.width_m) * scale;
 const isAdded = diffAdded.has(shelf.name);
 const isMoved = diffMoved.has(shelf.name);

 let stroke = shelf.color || '#338edc';
 let strokeW = 1;
 let fillOpacity = 0.12;
 let extraClass = '';
 if (isAdded) { stroke = '#10B981'; strokeW = 2.5; fillOpacity = 0.2; }
 else if (isMoved) { stroke = '#F59E0B'; strokeW = 2; fillOpacity = 0.15; }

 const rot = shelf.rotation || 0;
 const cx = sx + sw / 2;
 const cy = sy + sh / 2;

 return (
 <g key={`s-${shelf.id}`} transform={rot ? `rotate(${rot} ${cx} ${cy})` : undefined}>
 <rect x={sx} y={sy} width={sw} height={sh}
 fill={stroke} fillOpacity={fillOpacity}
 stroke={stroke} strokeWidth={strokeW} rx={3} />
 <text x={sx + sw / 2} y={sy + sh / 2 + 3}
 textAnchor="middle" fontSize={Math.min(10, sw / 4)}
 fill="var(--text-primary)" fontWeight={600} opacity={0.7}>
 {shelf.name}
 </text>
 {isAdded && <text x={sx + 3} y={sy - 3} fontSize={8} fill="#10B981" fontWeight={700}>NEU</text>}
 {isMoved && <text x={sx + 3} y={sy - 3} fontSize={8} fill="#F59E0B" fontWeight={700}>MOVED</text>}
 </g>
 );
 })}

 {/* Removed shelves (from diff) */}
 {diffData?.vorher_snapshot && (() => {
 const prev = typeof diffData.vorher_snapshot === 'string' ? JSON.parse(diffData.vorher_snapshot) : diffData.vorher_snapshot;
 return (prev.regale || []).filter(r => diffRemoved.has(r.name)).map(shelf => {
 const sx = parseFloat(shelf.pos_x) * scale;
 const sy = parseFloat(shelf.pos_y) * scale;
 const sw = parseFloat(shelf.length_m) * scale;
 const sh = parseFloat(shelf.width_m) * scale;
 return (
 <g key={`del-${shelf.id}`}>
 <rect x={sx} y={sy} width={sw} height={sh}
 fill="#EF4444" fillOpacity={0.08} stroke="#EF4444" strokeWidth={2}
 strokeDasharray="4 3" rx={3} />
 <text x={sx + sw / 2} y={sy + sh / 2 + 3}
 textAnchor="middle" fontSize={Math.min(10, sw / 4)}
 fill="#EF4444" fontWeight={600} opacity={0.5}>
 {shelf.name}
 </text>
 </g>
 );
 });
 })()}

 {/* Move arrows */}
 {(diffData?.diff?.moved_details || []).map(m => {
 const fromX = parseFloat(m.von.x) * scale;
 const fromY = parseFloat(m.von.y) * scale;
 const toX = parseFloat(m.nach.x) * scale;
 const toY = parseFloat(m.nach.y) * scale;
 return (
 <line key={`mv-${m.id}`}
 x1={fromX} y1={fromY} x2={toX} y2={toY}
 stroke="#F59E0B" strokeWidth={1.5} strokeDasharray="4 2"
 markerEnd="url(#arrowhead)" />
 );
 })}
 <defs>
 <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
 <polygon points="0 0, 8 3, 0 6" fill="#F59E0B" />
 </marker>
 </defs>
 </svg>
 </div>
 );
}

// ═════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════
export default function WarehouseConfig() {
 const { t } = useLanguage();
 const [warehouseId, setWarehouseId] = useState(1);
 const [standorte, setStandorte] = useState([]);
 const [config, setConfig] = useState({ warehouse_length_m: 60, warehouse_width_m: 72 });
 const [shelves, setShelves] = useState([]);
 const [selectedShelfId, setSelectedShelfId] = useState(null);
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);
 const [message, setMessage] = useState(null);
 const [bgOpacity, setBgOpacity] = useState(0.3);
 const [editHalle, setEditHalle] = useState(false);
 const [halleForm, setHalleForm] = useState({ length: 60, width: 72 });
 const fileInputRef = useRef(null);
 const saveTimerRef = useRef(null);

 // Detail panel tab + side view + resizable width
 const [detailTab, setDetailTab] = useState('eigenschaften'); // eigenschaften | seitenansicht
 const [panelWidth, setPanelWidth] = useState(() => parseInt(localStorage.getItem('weship_panel_width')) || 480);
 const panelDrag = useRef({ active: false, startX: 0, startW: 0 });
 useEffect(() => { localStorage.setItem('weship_panel_width', panelWidth); }, [panelWidth]);
 const handlePanelDragStart = useCallback((e) => {
 panelDrag.current = { active: true, startX: e.clientX, startW: panelWidth };
 document.body.style.cursor = 'col-resize';
 document.body.style.userSelect = 'none';
 const onMove = (ev) => {
 if (!panelDrag.current.active) return;
 const delta = panelDrag.current.startX - ev.clientX;
 setPanelWidth(Math.min(Math.max(panelDrag.current.startW + delta, 320), 800));
 };
 const onUp = () => {
 panelDrag.current.active = false;
 document.body.style.cursor = '';
 document.body.style.userSelect = '';
 document.removeEventListener('mousemove', onMove);
 document.removeEventListener('mouseup', onUp);
 };
 document.addEventListener('mousemove', onMove);
 document.addEventListener('mouseup', onUp);
 }, [panelWidth]);

 const [hoveredGridLoc, setHoveredGridLoc] = useState(null); // location_name from grid hover → map highlight
 const [kunden, setKunden] = useState([]);
 // Inline map locations for selected shelf
 const [selectedShelfLocs, setSelectedShelfLocs] = useState([]);
 // All shelf locations for zoom level 2 (batch loaded)
 const [allShelfLocs, setAllShelfLocs] = useState({});
 const [allShelfLocsLoaded, setAllShelfLocsLoaded] = useState(false);
 const [currentDetailLevel, setCurrentDetailLevel] = useState(1);

 // Object state
 const [objekte, setObjekte] = useState([]);
 const [selectedObjektId, setSelectedObjektId] = useState(null);
 const [placingType, setPlacingType] = useState(null);
 const [placingShelfType, setPlacingShelfType] = useState(null);
 const [segmentDialog, setSegmentDialog] = useState(null); // { gruppe } — for segmentation dialog
 const [placingSegments, setPlacingSegments] = useState(null); // { segments, currentIndex, gruppe, shelfType, placed: [{...seg, pos_x, pos_y}] }
 const segSaveTimerRef = useRef(null);
 const objSaveTimerRef = useRef(null);

 // Odoo regal-gruppen state
 const [regalGruppen, setRegalGruppen] = useState([]);
 const [openCategory, setOpenCategory] = useState(null);
 const [selectedRegalGruppe, setSelectedRegalGruppe] = useState(null);
 const [selectedSubtyp, setSelectedSubtyp] = useState(null); // subtype for current non-segmented placement

 // Subtypen state — loaded once, keyed by typ (e.g. { FB: [...], PR: [...] })
 const [subtypen, setSubtypen] = useState({});
 const [subtypOverrides, setSubtypOverrides] = useState({}); // { regalGruppe: subtypId }

 // Drag & Drop state (HTML5 DnD from dropdown to map)
 const [isDragOver, setIsDragOver] = useState(false);
 const [dragGhostData, setDragGhostData] = useState(null);
 const mapContainerRef = useRef(null);

 // Map theme (light/dark) — persisted in localStorage
 const [mapTheme, setMapTheme] = useState(() => {
 try { return localStorage.getItem('weship_map_theme') || 'light'; } catch { return 'light'; }
 });
 const handleMapThemeToggle = useCallback((theme) => {
 setMapTheme(theme);
 try { localStorage.setItem('weship_map_theme', theme); } catch {}
 }, []);

 // Kunden Layer System
 const [activeLayer, setActiveLayer] = useState('alle'); // 'alle' | 'heatmap' | kunde_id (number)
 const [layerKunden, setLayerKunden] = useState([]);
 const [kundenLayerData, setKundenLayerData] = useState({});
 const [layerLoading, setLayerLoading] = useState(false);

 // Tab + Historie state
 const [activeTab, setActiveTab] = useTabParam('grundriss');
 const [versionen, setVersionen] = useState([]);
 const [changelog, setChangelog] = useState([]);
 const [changelogTotal, setChangelogTotal] = useState(0);
 const [historieLoading, setHistorieLoading] = useState(false);
 const [viewingVersion, setViewingVersion] = useState(null);
 const [diffData, setDiffData] = useState(null);
 const [showDetailLog, setShowDetailLog] = useState(false);

 const SCALE = useMemo(() => {
 const maxDim = Math.max(config.warehouse_length_m || 60, config.warehouse_width_m || 72);
 return Math.min(12, Math.max(4, 700 / maxDim));
 }, [config.warehouse_length_m, config.warehouse_width_m]);

 // ─── Load standorte ───
 useEffect(() => {
 (async () => {
 try {
 const standRes = await apiFetch('/api/standorte');
 const list = Array.isArray(standRes) ? standRes : standRes.standorte || [];
 setStandorte(list);
 if (list.length > 0 && !list.find(s => s.id === warehouseId)) setWarehouseId(list[0].id);
 } catch { /* fallback */ }
 })();
 }, []);

 const loadData = useCallback(async () => {
 if (!warehouseId) return;
 setLoading(true);
 const [cfg, sh, kundenList, objs, rg, st] = await Promise.all([
 apiFetch(`/api/warehouse/${warehouseId}/config`),
 apiFetch(`/api/warehouse/${warehouseId}/shelves`),
 apiFetch(`/api/warehouse/${warehouseId}/kunden-liste`),
 apiFetch(`/api/warehouse/${warehouseId}/objekte`),
 apiFetch(`/api/warehouse/locations/regal-gruppen?warehouse_id=${warehouseId}`),
 apiFetch('/api/warehouse/subtypen'),
 ]);
 if (cfg && !cfg.error) {
 setConfig(cfg);
 setHalleForm({ length: cfg.warehouse_length_m || 60, width: cfg.warehouse_width_m || 72 });
 }
 if (Array.isArray(sh)) setShelves(sh);
 if (Array.isArray(kundenList)) setKunden(kundenList);
 if (Array.isArray(objs)) setObjekte(objs);
 if (rg && Array.isArray(rg.gruppen)) setRegalGruppen(rg.gruppen);
 if (st && typeof st === 'object' && !st.error) setSubtypen(st);
 setLoading(false);
 }, [warehouseId]);

 useEffect(() => { loadData(); }, [loadData]);

 // ─── Load Historie data ───
 const loadHistorie = useCallback(async () => {
 if (!warehouseId) return;
 setHistorieLoading(true);
 const [vRes, clRes] = await Promise.all([
 apiFetch(`/api/warehouse/${warehouseId}/versionen`),
 apiFetch(`/api/warehouse/${warehouseId}/changelog?limit=50`),
 ]);
 if (vRes?.versionen) setVersionen(vRes.versionen);
 if (clRes?.changelog) { setChangelog(clRes.changelog); setChangelogTotal(clRes.total || 0); }
 setHistorieLoading(false);
 }, [warehouseId]);

 useEffect(() => { if (activeTab === 'historie') loadHistorie(); }, [activeTab, loadHistorie]);

 const viewVersion = async (versionId) => {
 const v = await apiFetch(`/api/warehouse/${warehouseId}/versionen/${versionId}`);
 if (v && !v.error) setViewingVersion(v);
 };

 const viewDiff = async (versionId) => {
 const d = await apiFetch(`/api/warehouse/${warehouseId}/versionen/${versionId}/diff`);
 if (d && !d.error) setDiffData(d);
 };

 const createManualSnapshot = async () => {
 const label = prompt(t('warehouse.snapshotLabel') || 'Snapshot-Label eingeben:');
 if (!label) return;
 await apiFetch(`/api/warehouse/${warehouseId}/versionen/snapshot`, { method: 'POST', body: { label } });
 loadHistorie();
 showMsg(t('warehouse.snapshotErstellt') || 'Snapshot erstellt');
 };

 // ─── Fetch locations for selected shelf (inline map preview) ───
 useEffect(() => {
 if (!selectedShelfId || !warehouseId) { setSelectedShelfLocs([]); return; }
 (async () => {
 const locs = await apiFetch(`/api/warehouse/${warehouseId}/shelves/${selectedShelfId}/locations`);
 setSelectedShelfLocs(Array.isArray(locs) ? locs : []);
 })();
 }, [selectedShelfId, warehouseId]);

 // ─── Lazy-load all shelf locations when zoom level >= 2 ───
 const handleZoomChange = useCallback((zoom, detailLevel) => {
 setCurrentDetailLevel(detailLevel);
 if (detailLevel >= 2 && !allShelfLocsLoaded && warehouseId && shelves.length > 0) {
 loadAllShelfLocations();
 }
 }, [allShelfLocsLoaded, warehouseId, shelves.length]);

 const loadAllShelfLocations = useCallback(async () => {
 if (!warehouseId || shelves.length === 0) return;
 const locsMap = {};
 // Batch load: fetch locations for all shelves in parallel (max 10 concurrent)
 const batchSize = 10;
 for (let i = 0; i < shelves.length; i += batchSize) {
 const batch = shelves.slice(i, i + batchSize);
 const results = await Promise.all(
 batch.map(s => apiFetch(`/api/warehouse/${warehouseId}/shelves/${s.id}/locations`))
 );
 batch.forEach((s, idx) => {
 const locs = results[idx];
 if (Array.isArray(locs) && locs.length > 0) locsMap[s.id] = locs;
 });
 }
 setAllShelfLocs(locsMap);
 setAllShelfLocsLoaded(true);
 }, [warehouseId, shelves]);

 // Reset allShelfLocs when warehouse changes
 useEffect(() => {
 setAllShelfLocs({});
 setAllShelfLocsLoaded(false);
 setActiveLayer('alle');
 setKundenLayerData({});
 setLayerKunden([]);
 }, [warehouseId]);

 // Load Kunden Layer data when activeLayer changes from 'alle'
 useEffect(() => {
 if (activeLayer === 'alle' || !warehouseId) return;
 if (Object.keys(kundenLayerData).length > 0) return; // Already loaded
 (async () => {
 setLayerLoading(true);
 try {
 const res = await apiFetch(`/api/warehouse/${warehouseId}/regale/kunden-layer`);
 if (res && !res.error) {
 setKundenLayerData(res.shelves || {});
 setLayerKunden(res.kunden || []);
 }
 } catch { /* ignore */ }
 setLayerLoading(false);
 })();
 }, [activeLayer, warehouseId]);

 // ─── Auto-save shelf position (debounced) ───
 const saveShelf = useCallback(async (shelfId, data) => {
 await apiFetch(`/api/warehouse/${warehouseId}/shelves/${shelfId}`, { method: 'PUT', body: data });
 }, [warehouseId]);

 const debouncedSaveShelf = useCallback((shelfId, data) => {
 clearTimeout(saveTimerRef.current);
 saveTimerRef.current = setTimeout(() => saveShelf(shelfId, data), 400);
 }, [saveShelf]);

 // ─── Hall dimensions (inline edit) ───
 async function saveHalleDimensions() {
 setSaving(true);
 await apiFetch(`/api/warehouse/${warehouseId}/config`, {
 method: 'PUT', body: { length_m: halleForm.length, width_m: halleForm.width },
 });
 setConfig(prev => ({ ...prev, warehouse_length_m: halleForm.length, warehouse_width_m: halleForm.width }));
 setSaving(false);
 setEditHalle(false);
 showMsg(t('warehouse.config.dimensionsSaved'));
 }

 // ─── Handlers ───
 function moveShelf(shelfId, newX, newY) {
 setShelves(prev => prev.map(s => s.id === shelfId ? { ...s, pos_x: newX, pos_y: newY } : s));
 debouncedSaveShelf(shelfId, { pos_x: newX, pos_y: newY });
 }

 function updateSelectedShelf(data) {
 if (!selectedShelfId) return;
 setShelves(prev => prev.map(s => s.id === selectedShelfId ? { ...s, ...data } : s));
 debouncedSaveShelf(selectedShelfId, data);
 }

 function rotateSelectedShelf() {
 if (!selectedShelfId) return;
 const shelf = shelves.find(s => s.id === selectedShelfId);
 if (!shelf) return;
 updateSelectedShelf({ rotation: ((shelf.rotation || 0) + 90) % 360 });
 }

 async function deleteSelectedShelf() {
 if (!selectedShelfId) return;
 await apiFetch(`/api/warehouse/${warehouseId}/shelves/${selectedShelfId}`, { method: 'DELETE' });
 setShelves(prev => prev.filter(s => s.id !== selectedShelfId));
 setSelectedShelfId(null);
 }

 async function handleBackgroundUpload(e) {
 const file = e.target.files?.[0];
 if (!file) return;
 const formData = new FormData();
 formData.append('file', file);
 const result = await apiFetch(`/api/warehouse/${warehouseId}/config/background`, { method: 'POST', body: formData });
 if (result && result.path) setConfig(prev => ({ ...prev, warehouse_background_image: result.path }));
 }

 async function removeBackground() {
 await apiFetch(`/api/warehouse/${warehouseId}/config/background`, { method: 'DELETE' });
 setConfig(prev => ({ ...prev, warehouse_background_image: null }));
 }

 function showMsg(msg) { setMessage(msg); setTimeout(() => setMessage(null), 3000); }

 // Reset detail tab when shelf selection changes
 useEffect(() => { setDetailTab('eigenschaften'); }, [selectedShelfId]);

 // ─── ESC to cancel placement ───
 useEffect(() => {
 function onKeyDown(e) {
 if (e.key === 'Escape') {
 if (placingSegments) { setPlacingSegments(null); setPlacingShelfType(null); return; }
 if (placingType) setPlacingType(null);
 if (placingShelfType) setPlacingShelfType(null);
 if (openCategory) setOpenCategory(null);
 if (selectedRegalGruppe) setSelectedRegalGruppe(null);
 }
 }
 window.addEventListener('keydown', onKeyDown);
 return () => window.removeEventListener('keydown', onKeyDown);
 }, [placingType, placingShelfType, openCategory, selectedRegalGruppe, placingSegments]);

 // ─── Drag & Drop from dropdown to map ───
 function handleDropdownDragStart(e, gruppe) {
 const cfg = ODOO_TYP_CONFIG[gruppe.typ] || ODOO_TYP_CONFIG.SONDER;
 e.dataTransfer.setData('application/json', JSON.stringify({ regalGruppe: gruppe, shelfType: cfg.shelfType }));
 e.dataTransfer.effectAllowed = 'copy';
 // Ghost image
 const ghost = document.createElement('div');
 ghost.textContent = gruppe.regalGruppe;
 ghost.style.cssText = 'padding: 4px 10px; background: #338edc; color: white; border-radius: 6px; font-size: 12px; font-weight: 600; position: absolute; top: -1000px;';
 document.body.appendChild(ghost);
 e.dataTransfer.setDragImage(ghost, 30, 15);
 setTimeout(() => document.body.removeChild(ghost), 0);
 setDragGhostData({ laenge: gruppe.empfohlene_laenge || 5, breite: gruppe.empfohlene_breite || 0.6, color: cfg.color });
 }

 function handleMapDragOver(e) {
 e.preventDefault();
 e.dataTransfer.dropEffect = 'copy';
 setIsDragOver(true);
 }

 function handleMapDragLeave() {
 setIsDragOver(false);
 }

 async function handleMapDrop(e) {
 e.preventDefault();
 setIsDragOver(false);
 setDragGhostData(null);
 try {
 const data = JSON.parse(e.dataTransfer.getData('application/json'));
 if (!data?.regalGruppe) return;
 // Calculate position in meters from drop coordinates
 const svgEl = mapContainerRef.current?.querySelector('svg');
 if (!svgEl) return;
 const rect = svgEl.getBoundingClientRect();
 const viewBox = svgEl.viewBox.baseVal;
 const sx = viewBox.width / rect.width;
 const sy = viewBox.height / rect.height;
 const svgX = (e.clientX - rect.left) * sx + viewBox.x;
 const svgY = (e.clientY - rect.top) * sy + viewBox.y;
 const x = Math.round((svgX / SCALE) * 2) / 2;
 const y = Math.round((svgY / SCALE) * 2) / 2;
 // Use the same placement logic as click-flow
 setSelectedRegalGruppe(data.regalGruppe);
 setPlacingShelfType(data.shelfType);
 // Directly call placeShelf with the group pre-set
 await placeShelfFromDrop(data.regalGruppe, data.shelfType, x, y);
 } catch { /* ignore bad data */ }
 }

 async function placeShelfFromDrop(gruppe, shelfType, x, y) {
 if (!warehouseId) return;
 const g = gruppe;
 const cfg = ODOO_TYP_CONFIG[g.typ] || ODOO_TYP_CONFIG.SONDER;
 // Resolve subtype for drag-drop placement
 const overrideId = subtypOverrides[g.regalGruppe];
 const st = overrideId ? getSubtypById(subtypen, parseInt(overrideId)) : getDefaultSubtyp(subtypen, g.typ);
 const reihenCount = g.reihen_anzahl || (g.reihen || []).length || 1;
 const ebenenCount = g.ebenen_anzahl || (g.ebenen || []).length || 1;
 const lengthM = st ? calcShelfLength(reihenCount, st) : (g.empfohlene_laenge || reihenCount * cfg.reihenBreite);
 const widthM = st ? st.regal_tiefe_m : (g.empfohlene_breite || cfg.breite);
 const shelf = await apiFetch(`/api/warehouse/${warehouseId}/shelves`, {
 method: 'POST',
 body: {
 name: g.regalGruppe, shelf_type: cfg.shelfType, length_m: lengthM, width_m: widthM,
 levels: ebenenCount, slots_per_level: reihenCount, pos_x: x, pos_y: y, rotation: 0,
 color: cfg.color, odoo_prefix: g.regalGruppe, odoo_regal_gruppe: g.regalGruppe,
 subtyp_id: st?.id || null,
 },
 });
 if (shelf && shelf.id) {
 await apiFetch('/api/warehouse/locations/link-shelf', {
 method: 'POST', body: { regal_gruppe: g.regalGruppe, shelf_id: shelf.id, warehouse_id: warehouseId },
 });
 const [sh, rg] = await Promise.all([
 apiFetch(`/api/warehouse/${warehouseId}/shelves`),
 apiFetch(`/api/warehouse/locations/regal-gruppen?warehouse_id=${warehouseId}`),
 ]);
 if (Array.isArray(sh)) setShelves(sh);
 if (Array.isArray(rg)) setRegalGruppen(rg);
 setSelectedShelfId(shelf.id);
 setSelectedObjektId(null);
 showMsg(`${g.regalGruppe} — ${g.locations} Locations ${t('warehouse.verknuepft')}`);
 if (WarehouseCopilot._placed) WarehouseCopilot._placed(g.regalGruppe);
 }
 setPlacingShelfType(null);
 setSelectedRegalGruppe(null);
 setOpenCategory(null);
 }

 // ─── Segmentation dialog confirm handler ───
 function handleSegmentDialogConfirm(segments) {
 const g = segmentDialog?.gruppe;
 if (!g) { setSegmentDialog(null); return; }
 const cfg = ODOO_TYP_CONFIG[g.typ] || ODOO_TYP_CONFIG.SONDER;
 const st = segmentDialog?.subtyp || null;

 if (!segments || segments.length === 0) {
 // No segmentation — existing flow
 setSegmentDialog(null);
 setSelectedRegalGruppe(g);
 setSelectedSubtyp(st);
 setPlacingShelfType(cfg.shelfType);
 return;
 }

 // Start segment placement flow
 setSegmentDialog(null);
 setPlacingSegments({
 segments,
 currentIndex: 0,
 gruppe: g,
 shelfType: cfg.shelfType,
 placed: [],
 subtyp: st,
 });
 setPlacingShelfType(cfg.shelfType);
 }

 // ─── Update single segment position (via panel edit) ───
 function updateSegment(shelfId, segId, data) {
 setShelves(prev => prev.map(s => {
 if (s.id !== shelfId || !s.segmente) return s;
 return { ...s, segmente: s.segmente.map(seg => seg.id === segId ? { ...seg, ...data } : seg) };
 }));
 clearTimeout(segSaveTimerRef.current);
 segSaveTimerRef.current = setTimeout(() => {
 apiFetch(`/api/warehouse/${warehouseId}/shelves/${shelfId}/segmente/${segId}`, { method: 'PUT', body: data });
 }, 400);
 }

 // ─── Move segment on map ───
 function moveSegment(shelfId, segId, newX, newY) {
 setShelves(prev => prev.map(s => {
 if (s.id !== shelfId || !s.segmente) return s;
 return { ...s, segmente: s.segmente.map(seg => seg.id === segId ? { ...seg, pos_x: newX, pos_y: newY } : seg) };
 }));
 clearTimeout(segSaveTimerRef.current);
 segSaveTimerRef.current = setTimeout(() => {
 apiFetch(`/api/warehouse/${warehouseId}/shelves/${shelfId}/segmente/${segId}`, { method: 'PUT', body: { pos_x: newX, pos_y: newY } });
 }, 400);
 }

 // ─── Shelf placement handler ───
 async function placeShelf(x, y) {
 if (!placingShelfType || !warehouseId) return;

 // Segment placement flow
 if (placingSegments) {
 const { segments, currentIndex, gruppe, placed } = placingSegments;
 const current = segments[currentIndex];
 const newPlaced = [...placed, { ...current, pos_x: x, pos_y: y }];

 if (currentIndex + 1 < segments.length) {
 // More segments to place
 setPlacingSegments({ ...placingSegments, currentIndex: currentIndex + 1, placed: newPlaced });
 return;
 }

 // All segments placed — create shelf with segmentation
 const g = gruppe;
 const cfg = ODOO_TYP_CONFIG[g.typ] || ODOO_TYP_CONFIG.SONDER;
 const st = placingSegments.subtyp || null;
 const reihenCount = g.reihen_anzahl || (g.reihen || []).length || 1;
 const ebenenCount = g.ebenen_anzahl || (g.ebenen || []).length || 1;
 const lengthM = st ? calcShelfLength(reihenCount, st) : (g.empfohlene_laenge || reihenCount * cfg.reihenBreite);
 const widthM = st ? st.regal_tiefe_m : (g.empfohlene_breite || cfg.breite);

 const shelf = await apiFetch(`/api/warehouse/${warehouseId}/shelves`, {
 method: 'POST',
 body: {
 name: g.regalGruppe, shelf_type: cfg.shelfType, length_m: lengthM, width_m: widthM,
 levels: ebenenCount, slots_per_level: reihenCount,
 pos_x: newPlaced[0].pos_x, pos_y: newPlaced[0].pos_y,
 rotation: 0, color: cfg.color, odoo_prefix: g.regalGruppe,
 odoo_regal_gruppe: g.regalGruppe,
 subtyp_id: st?.id || null,
 segmentiert: true,
 segmente: newPlaced.map((seg, i) => ({
 reihe_von: seg.reihe_von, reihe_bis: seg.reihe_bis,
 pos_x: seg.pos_x, pos_y: seg.pos_y,
 laenge_m: seg.laenge_m, breite_m: seg.breite_m || widthM,
 rotation: 0, label: null,
 })),
 },
 });
 if (shelf && shelf.id) {
 await apiFetch('/api/warehouse/locations/link-shelf', {
 method: 'POST', body: { regal_gruppe: g.regalGruppe, shelf_id: shelf.id, warehouse_id: warehouseId },
 });
 const [sh, rg] = await Promise.all([
 apiFetch(`/api/warehouse/${warehouseId}/shelves`),
 apiFetch(`/api/warehouse/locations/regal-gruppen?warehouse_id=${warehouseId}`),
 ]);
 if (Array.isArray(sh)) setShelves(sh);
 if (Array.isArray(rg)) setRegalGruppen(rg);
 setSelectedShelfId(shelf.id);
 setSelectedObjektId(null);
 showMsg(`${g.regalGruppe} — ${newPlaced.length} ${t('warehouse.segmente')} ${t('warehouse.verknuepft')}`);
 if (WarehouseCopilot._placed) WarehouseCopilot._placed(g.regalGruppe);
 }
 setPlacingSegments(null);
 setPlacingShelfType(null);
 setSelectedRegalGruppe(null);
 return;
 }

 // Odoo-driven placement: selectedRegalGruppe has gruppe detail
 if (selectedRegalGruppe) {
 const g = selectedRegalGruppe;
 const cfg = ODOO_TYP_CONFIG[g.typ] || ODOO_TYP_CONFIG.SONDER;
 const st = selectedSubtyp || null;
 const reihenCount = g.reihen_anzahl || (g.reihen || []).length || 1;
 const ebenenCount = g.ebenen_anzahl || (g.ebenen || []).length || 1;
 const lengthM = st ? calcShelfLength(reihenCount, st) : (g.empfohlene_laenge || reihenCount * cfg.reihenBreite);
 const widthM = st ? st.regal_tiefe_m : (g.empfohlene_breite || cfg.breite);

 const shelf = await apiFetch(`/api/warehouse/${warehouseId}/shelves`, {
 method: 'POST',
 body: {
 name: g.regalGruppe,
 shelf_type: cfg.shelfType,
 length_m: lengthM,
 width_m: widthM,
 levels: ebenenCount,
 slots_per_level: reihenCount,
 pos_x: x,
 pos_y: y,
 rotation: 0,
 color: cfg.color,
 odoo_prefix: g.regalGruppe,
 odoo_regal_gruppe: g.regalGruppe,
 subtyp_id: st?.id || null,
 },
 });
 if (shelf && shelf.id) {
 // Link Odoo locations to the new shelf
 await apiFetch('/api/warehouse/locations/link-shelf', {
 method: 'POST',
 body: { regal_gruppe: g.regalGruppe, shelf_id: shelf.id, warehouse_id: warehouseId },
 });
 // Reload shelves and regal-gruppen
 const [sh, rg] = await Promise.all([
 apiFetch(`/api/warehouse/${warehouseId}/shelves`),
 apiFetch(`/api/warehouse/locations/regal-gruppen?warehouse_id=${warehouseId}`),
 ]);
 if (Array.isArray(sh)) setShelves(sh);
 if (Array.isArray(rg)) setRegalGruppen(rg);
 setSelectedShelfId(shelf.id);
 setSelectedObjektId(null);
 showMsg(`${g.regalGruppe} — ${g.locations} Locations ${t('warehouse.verknuepft')}`);
 // Trigger copilot "well placed" + auto-suggest next
 if (WarehouseCopilot._placed) WarehouseCopilot._placed(g.regalGruppe);
 }
 setPlacingShelfType(null);
 setSelectedRegalGruppe(null);
 setSelectedSubtyp(null);
 return;
 }

 // Manual placement (non-Odoo)
 const def = getShelfDef(placingShelfType);
 if (!def) return;
 const nextNum = shelves.length + 1;
 const name = `R${String(nextNum).padStart(2, '0')}`;
 const shelf = await apiFetch(`/api/warehouse/${warehouseId}/shelves`, {
 method: 'POST',
 body: {
 name,
 shelf_type: placingShelfType,
 length_m: def.defaultW,
 width_m: def.defaultH,
 levels: def.levels,
 slots_per_level: def.slots,
 pos_x: x,
 pos_y: y,
 rotation: 0,
 color: def.color,
 odoo_prefix: name,
 },
 });
 if (shelf && shelf.id) {
 await apiFetch(`/api/warehouse/${warehouseId}/shelves/${shelf.id}/generate-locations`, { method: 'POST' });
 const sh = await apiFetch(`/api/warehouse/${warehouseId}/shelves`);
 if (Array.isArray(sh)) setShelves(sh);
 setSelectedShelfId(shelf.id);
 setSelectedObjektId(null);
 showMsg(`${name} ${t(`warehouse.config.shelfTypes.${placingShelfType}`)} — ${def.levels * def.slots} Locations`);
 }
 setPlacingShelfType(null);
 }

 // ─── Object handlers ───
 async function placeObjekt(x, y) {
 if (!placingType || !warehouseId) return;
 const def = OBJEKT_TYPEN[placingType];
 if (!def) return;
 const result = await apiFetch(`/api/warehouse/${warehouseId}/objekte`, {
 method: 'POST',
 body: { name: t(def.label), typ: placingType, pos_x: x, pos_y: y, laenge_m: def.defaultW, breite_m: def.defaultH, farbe: def.color },
 });
 if (result && result.id) {
 setObjekte(prev => [...prev, result]);
 setSelectedObjektId(result.id);
 setSelectedShelfId(null);
 }
 setPlacingType(null);
 }

 function moveObjekt(objId, newX, newY) {
 setObjekte(prev => prev.map(o => o.id === objId ? { ...o, pos_x: newX, pos_y: newY } : o));
 clearTimeout(objSaveTimerRef.current);
 objSaveTimerRef.current = setTimeout(() => {
 apiFetch(`/api/warehouse/${warehouseId}/objekte/${objId}/position`, { method: 'PUT', body: { pos_x: newX, pos_y: newY } });
 }, 400);
 }

 function updateSelectedObjekt(data) {
 if (!selectedObjektId) return;
 setObjekte(prev => prev.map(o => o.id === selectedObjektId ? { ...o, ...data } : o));
 clearTimeout(objSaveTimerRef.current);
 objSaveTimerRef.current = setTimeout(() => {
 apiFetch(`/api/warehouse/${warehouseId}/objekte/${selectedObjektId}`, { method: 'PUT', body: data });
 }, 400);
 }

 async function deleteSelectedObjekt() {
 if (!selectedObjektId) return;
 await apiFetch(`/api/warehouse/${warehouseId}/objekte/${selectedObjektId}`, { method: 'DELETE' });
 setObjekte(prev => prev.filter(o => o.id !== selectedObjektId));
 setSelectedObjektId(null);
 }

 // ─── Computed ───
 const selectedShelf = shelves.find(s => s.id === selectedShelfId) || null;
 const selectedObjekt = objekte.find(o => o.id === selectedObjektId) || null;
 const totalLocations = shelves.reduce((s, sh) => s + parseInt(sh.location_count || 0), 0);
 const whWidth = config.warehouse_length_m || 60;
 const whHeight = config.warehouse_width_m || 72;
 const SHELF_ABBR = { fachboden: 'FB', hochregal: 'PR', blocklager: 'BLA' };

 // ─── Overlap detection ───
 const overlapWarnings = useMemo(() => {
 if (shelves.length < 2) return [];
 const warnings = [];
 const boxes = shelves.map(s => {
 const rot = s.rotation || 0;
 const w = (rot === 90 || rot === 270) ? parseFloat(s.width_m) : parseFloat(s.length_m);
 const h = (rot === 90 || rot === 270) ? parseFloat(s.length_m) : parseFloat(s.width_m);
 return { id: s.id, name: s.name, x: parseFloat(s.pos_x), y: parseFloat(s.pos_y), w, h };
 });
 for (let i = 0; i < boxes.length; i++) {
 for (let j = i + 1; j < boxes.length; j++) {
 const a = boxes[i], b = boxes[j];
 // AABB overlap check with 0.05m tolerance
 if (a.x < b.x + b.w - 0.05 && a.x + a.w > b.x + 0.05 &&
 a.y < b.y + b.h - 0.05 && a.y + a.h > b.y + 0.05) {
 warnings.push({ a: a.name, b: b.name });
 }
 }
 }
 return warnings;
 }, [shelves]);

 // Active dropdown data (for rendering inside map container)
 const activeDropdownGruppen = useMemo(() => {
 if (!openCategory) return [];
 const cat = ODOO_TYP_CATEGORIES.find(c => c.key === openCategory);
 if (!cat) return [];
 return regalGruppen.filter(g => cat.typs.includes(g.typ));
 }, [openCategory, regalGruppen]);

 if (loading) {
 return (
 <div className="flex items-center justify-center h-64">
 <div className="w-6 h-6 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
 </div>
 );
 }

 return (
 <div className="flex flex-col h-full">
 {/* Compact single-line header: Title + Tabs + Info strip */}
 <div className="flex items-center gap-4 px-6 pt-4 pb-2">
 <h1 className="text-lg font-semibold text-[var(--text-primary)] whitespace-nowrap">{t('warehouse.config.title')}</h1>
 {standorte.length > 1 && (
 <select value={warehouseId} onChange={e => setWarehouseId(Number(e.target.value))}
 className="bg-[var(--bg-inset)] border border-[var(--border-input)] rounded-lg px-2 py-1 text-xs text-[var(--text-primary)] focus:outline-none">
 {standorte.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
 </select>
 )}
 <div className="inline-flex bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-card)]">
 {[
 { key: 'grundriss', label: t('warehouse.grundriss') || 'Grundriss' },
 { key: 'historie', label: t('warehouse.historie') || 'Historie' },
 { key: 'subtypen', label: t('warehouse.subtypVerwaltung') || 'Subtypen' },
 ].map(tab => (
 <button key={tab.key} onClick={() => { setActiveTab(tab.key); setViewingVersion(null); setDiffData(null); }}
 className={`px-3 py-1 text-xs font-medium rounded-[8px] transition-all
 ${activeTab === tab.key
 ? 'bg-white dark:bg-[var(--bg-card)] text-[var(--text-primary)] shadow-sm'
 : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}>
 {tab.label}
 </button>
 ))}
 </div>
 <div className="flex items-center gap-2 text-[11px] text-[var(--text-secondary)]">
 {editHalle ? (
 <div className="flex items-center gap-1.5 px-2 py-0.5 bg-[var(--bg-card)] border border-[var(--border-card)] rounded-lg">
 <NumInput value={halleForm.length} onChange={v => setHalleForm(prev => ({ ...prev, length: v }))}
 className="w-14 bg-[var(--bg-input)] border border-[var(--border-input)] rounded px-1 py-0.5 text-[11px] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 <span className="text-[10px] text-[var(--text-tertiary)]">×</span>
 <NumInput value={halleForm.width} onChange={v => setHalleForm(prev => ({ ...prev, width: v }))}
 className="w-14 bg-[var(--bg-input)] border border-[var(--border-input)] rounded px-1 py-0.5 text-[11px] text-[var(--text-primary)] focus:outline-none focus:border-[#338edc]" />
 <span className="text-[10px] text-[var(--text-tertiary)]">m</span>
 <button onClick={saveHalleDimensions} disabled={saving}
 className="p-0.5 text-[#338edc] hover:text-[#2b7bc4] disabled:opacity-50"><Check className="w-3 h-3" /></button>
 <button onClick={() => { setEditHalle(false); setHalleForm({ length: config.warehouse_length_m, width: config.warehouse_width_m }); }}
 className="p-0.5 text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"><X className="w-3 h-3" /></button>
 </div>
 ) : (
 <button onClick={() => setEditHalle(true)}
 className="flex items-center gap-1 hover:text-[var(--text-primary)] transition-colors">
 <Settings className="w-3 h-3 text-[var(--text-tertiary)]" />
 {Number(whWidth).toFixed(0)}×{Number(whHeight).toFixed(0)}m
 </button>
 )}
 <span className="text-[var(--text-tertiary)]">·</span>
 <span>{shelves.length} {t('warehouse.regale')}</span>
 {regalGruppen.length > 0 && (() => {
 const placed = regalGruppen.filter(g => g.hat_shelf).length;
 const total = regalGruppen.length;
 return <><span className="text-[var(--text-tertiary)]">·</span><span>{placed}/{total} {t('warehouse.platziert')}</span></>;
 })()}
 </div>
 <div className="flex-1" />
 <input ref={fileInputRef} type="file" accept=".png,.jpg,.jpeg,.svg,.webp" onChange={handleBackgroundUpload} className="hidden" />
 <button onClick={() => fileInputRef.current?.click()}
 className="p-1 text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-card-hover)] rounded-lg transition-colors"
 title={t('warehouse.grundrissLaden')}>
 <Upload className="w-3.5 h-3.5" />
 </button>
 {config.warehouse_background_image && (
 <>
 <input type="range" min={0.05} max={0.8} step={0.05} value={bgOpacity}
 onChange={e => setBgOpacity(parseFloat(e.target.value))}
 className="w-14 h-1 rounded-full appearance-none bg-[var(--bg-inset)] cursor-pointer accent-[#338edc]" />
 <span className="text-[9px] text-[var(--text-tertiary)]">{Math.round(bgOpacity * 100)}%</span>
 <button onClick={removeBackground}
 className="p-1 text-red-400 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10"
 title={t('warehouse.config.removeBackground')}>
 <Trash2 className="w-3 h-3" />
 </button>
 </>
 )}
 </div>

 <div className="px-6 pb-6 flex-1 flex flex-col min-h-0">
 {activeTab === 'grundriss' && <>

 {message && (
 <div className="mb-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-xs text-emerald-600 dark:text-emerald-400">
 {message}
 </div>
 )}

 {overlapWarnings.length > 0 && (
 <div className="mb-2 px-3 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-lg text-xs text-amber-600 dark:text-amber-400 flex items-center gap-2">
 <span>⚠️</span>
 <span>
 {t('warehouse.ueberlappung_warnung', { count: overlapWarnings.length })}:
 {' '}{overlapWarnings.map((w, i) => (
 <span key={i}>{i > 0 ? ', ' : ''}{w.a} ↔ {w.b}</span>
 ))}
 </span>
 </div>
 )}

 <div className="flex-1 flex gap-1 min-h-0">
 {/* Toolbar — 80px with icons + names */}
 <div className="w-[80px] shrink-0 flex flex-col gap-1 p-1.5 bg-[var(--bg-card,#fff)] border-r border-[var(--border-card)] overflow-y-auto">
 <div className="text-[9px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] text-center my-1">
 {t('warehouse.regale')}
 </div>
 {ODOO_TYP_CATEGORIES.map(cat => {
 const isOpen = openCategory === cat.key;
 const typCfg = ODOO_TYP_CONFIG[cat.typs[0]] || ODOO_TYP_CONFIG.SONDER;
 const catGruppen = regalGruppen.filter(g => cat.typs.includes(g.typ));
 const catPlaced = catGruppen.filter(g => g.hat_shelf).length;
 const hasUnplaced = catGruppen.some(g => !g.hat_shelf);
 return (
 <button key={cat.key}
 onClick={() => {
 if (isOpen) { setOpenCategory(null); return; }
 setOpenCategory(cat.key);
 setPlacingType(null); setPlacingShelfType(null); setSelectedShelfId(null); setSelectedObjektId(null);
 }}
 className={`w-full py-2 px-1 rounded-lg flex flex-col items-center gap-0.5 transition-colors
 ${isOpen ? 'bg-[#EBF4FD] dark:bg-[#338edc]/15 text-[#338edc]' : 'hover:bg-[var(--bg-inset)] text-[var(--text-secondary)]'}`}
 style={isOpen ? { color: typCfg.color } : { color: hasUnplaced ? typCfg.color : undefined }}
 title={`${cat.shortName} (${catPlaced}/${catGruppen.length})`}>
 <span className="text-[16px] leading-none">{cat.icon}</span>
 <span className="text-[10px] font-medium leading-tight text-center">{cat.shortName}</span>
 {catGruppen.length > 0 && (
 <span className={`text-[8px] ${isOpen ? 'opacity-60' : 'text-[var(--text-tertiary)]'}`}>
 {catPlaced}/{catGruppen.length}
 </span>
 )}
 </button>
 );
 })}

 <div className="w-12 h-px bg-[var(--border-card)] mx-auto my-1" />

 <div className="text-[9px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)] text-center my-1">
 {t('warehouse.objekte') || 'Objekte'}
 </div>
 {Object.entries(OBJEKT_TYPEN).map(([typ, def]) => {
 const Icon = def.icon;
 const isActive = placingType === typ;
 const shortNames = { packtisch: 'Packtisch', dock: 'LKW-Dock', zone_anlieferung: 'Anliefer.', zone_versand: 'Versand', buero: 'Büro', tor: 'Tor', gefahrgut: 'Gefahr' };
 return (
 <button key={typ}
 onClick={() => { setPlacingType(isActive ? null : typ); setPlacingShelfType(null); setSelectedShelfId(null); setSelectedObjektId(null); setSelectedRegalGruppe(null); setOpenCategory(null); }}
 className={`w-full py-1.5 px-1 rounded-lg flex flex-col items-center gap-0.5 transition-colors
 ${isActive ? 'bg-[#EBF4FD] dark:bg-[#338edc]/15' : 'hover:bg-[var(--bg-inset)]'}`}
 title={t(def.label)}>
 <Icon className="w-4 h-4" style={{ color: isActive ? def.color : 'var(--text-secondary)' }} />
 <span className="text-[9px] leading-tight text-center" style={{ color: isActive ? def.color : 'var(--text-tertiary)' }}>
 {shortNames[typ] || typ}
 </span>
 </button>
 );
 })}

 {selectedRegalGruppe && (
 <div className="mt-1 px-1 py-1.5 bg-[#338edc]/10 rounded-lg text-center">
 <div className="text-[8px] font-bold text-[#338edc] leading-tight">{selectedRegalGruppe.regalGruppe}</div>
 {selectedRegalGruppe.empfohlene_laenge && (
 <div className="text-[7px] text-[#338edc]/70">{selectedRegalGruppe.empfohlene_laenge}m × {selectedRegalGruppe.empfohlene_breite}m</div>
 )}
 <div className="text-[7px] text-[var(--text-tertiary)]">{t('warehouse.dragHint')}</div>
 </div>
 )}
 {(placingType || placingShelfType) && (
 <div className="text-[8px] text-center text-[var(--text-tertiary)] mt-1 leading-tight">ESC</div>
 )}
 </div>

 {/* Floorplan — flex-1 with DnD drop zone */}
 <div className="flex-1 flex flex-col relative min-w-0"
 ref={mapContainerRef}
 onDragOver={handleMapDragOver}
 onDragLeave={handleMapDragLeave}
 onDrop={handleMapDrop}
 style={isDragOver ? { outline: '2px dashed #338edc', outlineOffset: '-2px', borderRadius: '8px' } : {}}>
 {/* Layer Bar — Alle / Heatmap / Kunden-Dropdown */}
 <div className="flex items-center gap-1.5 px-2 py-1 bg-[var(--bg-card)] border-b border-[var(--border-card)] shrink-0">
 <button onClick={() => setActiveLayer('alle')}
 className={`px-2 py-0.5 text-[10px] font-medium rounded-lg whitespace-nowrap transition-colors
 ${activeLayer === 'alle' ? 'bg-[#338edc]/10 text-[#338edc]' : 'text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]'}`}>
 {t('warehouse.layerAlle')}
 </button>
 <button onClick={() => setActiveLayer('heatmap')}
 className={`px-2 py-0.5 text-[10px] font-medium rounded-lg whitespace-nowrap transition-colors
 ${activeLayer === 'heatmap' ? 'bg-amber-500/10 text-amber-600' : 'text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]'}`}>
 {t('warehouse.layerHeatmap')}
 </button>
 <div className="w-px h-4 bg-[var(--border-card)]" />
 <KundenLayerDropdown
 layerKunden={layerKunden} kunden={kunden} activeLayer={activeLayer}
 setActiveLayer={setActiveLayer} layerLoading={layerLoading}
 kundenLayerData={kundenLayerData} t={t} />
 </div>
 {/* Segment placement indicator bar */}
 {placingSegments && (
 <div className="flex items-center gap-2 px-3 py-1.5 bg-[#338edc]/10 border-b border-[#338edc]/20 shrink-0">
 <SplitSquareHorizontal className="w-3.5 h-3.5 text-[#338edc]" />
 <span className="text-xs font-semibold text-[#338edc]">
 {t('warehouse.platziereSegment').replace('{x}', placingSegments.currentIndex + 1).replace('{y}', placingSegments.segments.length)}
 </span>
 <span className="text-[10px] text-[var(--text-secondary)]">
 ({placingSegments.segments[placingSegments.currentIndex]?.reihe_von}–{placingSegments.segments[placingSegments.currentIndex]?.reihe_bis})
 </span>
 {/* Copilot tip: "Gegenüber platzieren" — when placing segment 2+ */}
 {placingSegments.currentIndex > 0 && placingSegments.placed.length > 0 && (() => {
 const prev = placingSegments.placed[placingSegments.placed.length - 1];
 const gangBreite = 2; // default aisle width 2m
 const prevBreite = prev.breite_m || (ODOO_TYP_CONFIG[placingSegments.gruppe?.typ] || ODOO_TYP_CONFIG.SONDER).breite || 0.6;
 const oppositeY = parseFloat(prev.pos_y) + parseFloat(prevBreite) + gangBreite;
 return (
 <button onClick={() => placeShelf(parseFloat(prev.pos_x), oppositeY)}
 className="flex items-center gap-1 px-2 py-0.5 bg-[#338edc]/15 text-[#338edc] text-[10px] font-semibold rounded-lg hover:bg-[#338edc]/25 transition-colors ml-1"
 title={`X: ${parseFloat(prev.pos_x).toFixed(1)}, Y: ${oppositeY.toFixed(1)}`}>
 <Sparkles className="w-3 h-3" />
 {t('warehouse.gegenueber')}
 </button>
 );
 })()}
 <div className="flex-1" />
 <button onClick={() => { setPlacingSegments(null); setPlacingShelfType(null); }}
 className="text-[10px] text-red-500 hover:text-red-600 font-medium">ESC</button>
 </div>
 )}
 <FloorplanGrid width={whWidth} height={whHeight} scale={SCALE}
 shelves={shelves} selectedId={selectedShelfId}
 selectedShelfLocs={selectedShelfLocs} kundenList={kunden}
 allShelfLocs={allShelfLocs} onZoomChange={handleZoomChange}
 mapTheme={mapTheme} onThemeToggle={handleMapThemeToggle}
 activeLayer={activeLayer} layerKunden={layerKunden} kundenLayerData={kundenLayerData}
 onSelectShelf={(id) => { setSelectedShelfId(id); if (id) setSelectedObjektId(null); }}
 onMoveShelf={moveShelf}
 onMoveSegment={moveSegment}
 backgroundImage={config.warehouse_background_image} bgOpacity={bgOpacity} t={t}
 objekte={objekte} selectedObjektId={selectedObjektId}
 onSelectObjekt={(id) => { setSelectedObjektId(id); if (id) setSelectedShelfId(null); }}
 onMoveObjekt={moveObjekt}
 placingType={placingType} onPlaceObjekt={placeObjekt}
 placingShelfType={placingShelfType} onPlaceShelf={placeShelf}
 placingSegments={placingSegments} />

 {/* Regal-Dropdown — floats inside map container */}
 {openCategory && activeDropdownGruppen.length > 0 && (
 <div className="absolute left-0 top-0 bottom-0 w-[220px] z-20 bg-[var(--bg-card)] border-r border-[var(--border-card)] shadow-sm">
 <RegalAuswahlPanel
 gruppen={activeDropdownGruppen}
 onSelect={(g, st) => {
 setOpenCategory(null);
 setSegmentDialog({ gruppe: g, subtyp: st });
 if (WarehouseCopilot._suggest) WarehouseCopilot._suggest(g.regalGruppe);
 }}
 onDragStart={handleDropdownDragStart}
 onClose={() => setOpenCategory(null)}
 subtypen={subtypen}
 subtypOverrides={subtypOverrides}
 onSubtypChange={(rg, val) => setSubtypOverrides(prev => ({ ...prev, [rg]: val }))}
 t={t} />
 </div>
 )}

 {/* Copilot Widget */}
 <WarehouseCopilot
 warehouseId={warehouseId}
 regalGruppen={regalGruppen}
 onSelectRegal={(g) => {
 const cfg = ODOO_TYP_CONFIG[g.typ] || ODOO_TYP_CONFIG.SONDER;
 setOpenCategory(null);
 setSelectedRegalGruppe(g);
 setPlacingShelfType(cfg.shelfType);
 }}
 panelOpen={false}
 t={t} />
 </div>

 {/* Drag Handle — between map and detail panel */}
 {!!(selectedShelfId || selectedObjektId) && (
 <div className="w-1 shrink-0 cursor-col-resize group relative z-10"
 onMouseDown={handlePanelDragStart}>
 <div className="absolute inset-y-0 -left-1 -right-1" />
 <div className="w-full h-full transition-colors group-hover:bg-[#338edc]/30 group-active:bg-[#338edc]/50" />
 </div>
 )}

 {/* Detail-Panel — resizable via drag handle */}
 {(() => {
 const panelOpen = !!(selectedShelfId || selectedObjektId);
 return (
 <div className={`shrink-0 border-l border-[var(--border-card)] overflow-hidden
 ${!panelOpen ? 'w-10 transition-all duration-300' : ''} ${panelOpen ? 'bg-[var(--bg-card)]' : 'bg-[var(--bg-inset)]'}`}
 style={panelOpen ? { width: panelWidth } : undefined}>
 {panelOpen ? (
 <div className="overflow-y-auto p-3 h-full">
 {/* Close button */}
 <div className="flex items-center justify-end mb-1">
 <button onClick={() => { setSelectedShelfId(null); setSelectedObjektId(null); setDetailTab('eigenschaften'); }}
 className="p-1 rounded-lg hover:bg-[var(--bg-inset)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]">
 <X className="w-3.5 h-3.5" />
 </button>
 </div>
 {selectedObjektId ? (
 <ObjektProperties objekt={selectedObjekt} onUpdate={updateSelectedObjekt} onDelete={deleteSelectedObjekt} t={t} />
 ) : (
 <>
 {/* Tab bar: Eigenschaften / Seitenansicht */}
 <div className="flex gap-1 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-card)] mb-3">
 {[['eigenschaften', t('warehouse.eigenschaften')], ['seitenansicht', t('warehouse.seitenansicht')]].map(([key, label]) => (
 <button key={key} onClick={() => setDetailTab(key)}
 className={`flex-1 px-2 py-1 text-[10px] font-medium rounded-[8px] transition-all
 ${detailTab === key ? 'bg-white dark:bg-[var(--bg-card)] shadow-sm text-[var(--text-primary)]' : 'text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]'}`}>
 {label}
 </button>
 ))}
 </div>
 {detailTab === 'eigenschaften' ? (
 <ShelfProperties shelf={selectedShelf} onUpdate={updateSelectedShelf} onDelete={deleteSelectedShelf}
 onRotate={rotateSelectedShelf}
 onEditSegments={selectedShelf?.segmentiert ? () => {
 const g = regalGruppen.find(rg => rg.regalGruppe === selectedShelf.odoo_regal_gruppe);
 if (g) setSegmentDialog({ gruppe: g, editShelf: selectedShelf });
 } : null}
 onUpdateSegment={updateSegment}
 subtypen={subtypen}
 t={t} />
 ) : (
 <PanelErrorBoundary fallback={<div className="p-4 text-xs text-red-500 text-center">{t('warehouse.keineDaten')}</div>}>
 <ShelfSeitenansicht
 shelfId={selectedShelfId}
 warehouseId={warehouseId}
 t={t}
 onHoverLoc={setHoveredGridLoc}
 shelves={shelves} />
 </PanelErrorBoundary>
 )}
 </>
 )}
 </div>
 ) : (
 <div className="w-10 h-full flex items-center justify-center">
 <span className="text-[11px] text-[var(--text-tertiary)]" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
 {t('warehouse.elementAuswaehlen')}
 </span>
 </div>
 )}
 </div>
 );
 })()}
 </div>

 {/* Customer legend for selected shelf */}
 {selectedShelfId && selectedShelfLocs.length > 0 && (() => {
 const slotData = buildSlotColors(selectedShelfLocs, kunden);
 if (!slotData?.legend?.length) return null;
 const frei = selectedShelfLocs.filter(l => !l.kunde_id).length;
 return (
 <div className="flex flex-wrap items-center gap-3 text-[10px] mt-3">
 {slotData.legend.map(l => (
 <div key={l.id} className="flex items-center gap-1.5 px-2 py-1 bg-[var(--bg-card)] border border-[var(--border-card)] rounded-lg">
 <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: l.color }} />
 <span className="text-[var(--text-secondary)]">{l.name}</span>
 <span className="text-[var(--text-tertiary)] font-mono">{selectedShelfLocs.filter(lo => lo.kunde_id === l.id).length}</span>
 </div>
 ))}
 {frei > 0 && (
 <div className="flex items-center gap-1.5 px-2 py-1 bg-[var(--bg-card)] border border-[var(--border-card)] rounded-lg">
 <div className="w-2.5 h-2.5 rounded-sm bg-[#475569]/30" />
 <span className="text-[var(--text-secondary)]">{t('warehouse.frei')}</span>
 <span className="text-[var(--text-tertiary)] font-mono">{frei}</span>
 </div>
 )}
 </div>
 );
 })()}

 {/* Object legend */}
 {objekte.length > 0 && (
 <div className="flex flex-wrap gap-3 text-[10px] mt-3">
 {[...new Set(objekte.map(o => o.typ))].map(typ => {
 const def = OBJEKT_TYPEN[typ];
 if (!def) return null;
 const Icon = def.icon;
 return (
 <div key={typ} className="flex items-center gap-1.5 px-2 py-1 bg-[var(--bg-card)] border border-[var(--border-card)] rounded-lg">
 <Icon className="w-3 h-3" style={{ color: def.color }} />
 <span className="text-[var(--text-secondary)]">{t(def.label)}</span>
 <span className="text-[var(--text-tertiary)] font-mono">{objekte.filter(o => o.typ === typ).length}</span>
 </div>
 );
 })}
 </div>
 )}
 </>}

 {/* ═══ HISTORIE TAB ═══ */}
 {activeTab === 'historie' && (
 <div className="flex-1 flex flex-col min-h-0 gap-4">
 {/* Viewing a historical snapshot */}
 {viewingVersion && (
 <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl px-4 py-3 flex items-center gap-3">
 <span className="text-sm text-amber-800 dark:text-amber-300">
 {t('warehouse.historischeAnsicht', { nr: viewingVersion.version_nr, datum: new Date(viewingVersion.created_at).toLocaleString('de-AT') })
 || `Historische Ansicht — Version ${viewingVersion.version_nr} vom ${new Date(viewingVersion.created_at).toLocaleString('de-AT')}`}
 </span>
 <button onClick={() => setViewingVersion(null)}
 className="ml-auto text-sm text-amber-600 dark:text-amber-400 underline hover:no-underline">
 {t('warehouse.zurueckAktuell') || 'Zurück zur aktuellen Ansicht'}
 </button>
 </div>
 )}

 {/* Viewing a diff */}
 {diffData && !viewingVersion && (
 <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl px-4 py-3 flex items-center gap-3">
 <span className="text-sm text-blue-800 dark:text-blue-300">
 {t('warehouse.vergleich', { von: diffData.version_nr - 1, bis: diffData.version_nr })
 || `Vergleich: V${diffData.version_nr - 1} → V${diffData.version_nr}`}
 </span>
 <div className="flex gap-3 text-xs ml-3">
 {diffData.diff?.added > 0 && <span className="text-emerald-600">+{diffData.diff.added} {t('warehouse.neu') || 'Neu'}</span>}
 {diffData.diff?.moved > 0 && <span className="text-amber-600">{diffData.diff.moved} {t('warehouse.verschoben') || 'Verschoben'}</span>}
 {diffData.diff?.removed > 0 && <span className="text-red-500">-{diffData.diff.removed} {t('warehouse.entfernt') || 'Entfernt'}</span>}
 </div>
 <button onClick={() => setDiffData(null)}
 className="ml-auto text-sm text-blue-600 dark:text-blue-400 underline hover:no-underline">
 {t('warehouse.zurueckAktuell') || 'Schließen'}
 </button>
 </div>
 )}

 {/* Historical/diff map preview */}
 {(viewingVersion || diffData) && (
 <div className="bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl overflow-hidden" style={{ height: 400 }}>
 <HistorischeMap
 snapshot={viewingVersion?.layout_snapshot || diffData?.aktuell_snapshot}
 diffData={diffData}
 scale={SCALE} />
 </div>
 )}

 {/* Timeline + Detail-Log (only when not viewing snapshot/diff) */}
 {!viewingVersion && !diffData && (
 <div className="flex gap-4 flex-1 min-h-0">
 {/* Timeline */}
 <div className="flex-1 bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl overflow-hidden flex flex-col">
 <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-card)]">
 <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('warehouse.timeline') || 'Timeline'}</h3>
 <button onClick={createManualSnapshot}
 className="text-xs font-medium text-[#338edc] hover:text-[#2b7bc4] px-3 py-1.5 rounded-lg hover:bg-[#338edc]/5 transition-colors">
 {t('warehouse.snapshotErstellen') || 'Snapshot erstellen'}
 </button>
 </div>
 <div className="flex-1 overflow-y-auto p-4">
 {historieLoading ? (
 <div className="flex justify-center py-8">
 <div className="w-5 h-5 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
 </div>
 ) : versionen.length === 0 ? (
 <div className="text-center text-sm text-[var(--text-tertiary)] py-8">
 {t('warehouse.keineVersionen') || 'Noch keine Versionen vorhanden'}
 </div>
 ) : (
 <div className="space-y-0">
 {versionen.map((v, i) => (
 <div key={v.id} className="relative pl-6 pb-5">
 {/* Timeline line */}
 {i < versionen.length - 1 && (
 <div className="absolute left-[9px] top-3 bottom-0 w-px bg-[var(--border-card)]" />
 )}
 {/* Timeline dot */}
 <div className={`absolute left-0 top-1 w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center
 ${v.import_quelle === 'xls_import' ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/30' :
 v.import_quelle === 'auto' ? 'border-[var(--border-card)] bg-[var(--bg-inset)]' :
 'border-[#338edc] bg-blue-50 dark:bg-blue-900/30'}`}>
 <div className={`w-2 h-2 rounded-full
 ${v.import_quelle === 'xls_import' ? 'bg-indigo-400' :
 v.import_quelle === 'auto' ? 'bg-[var(--text-tertiary)]' : 'bg-[#338edc]'}`} />
 </div>
 {/* Content */}
 <div>
 <div className="flex items-center gap-2 mb-0.5">
 <span className="text-xs font-bold text-[var(--text-primary)]">V{v.version_nr}</span>
 <span className="text-[10px] text-[var(--text-tertiary)]">
 {new Date(v.created_at).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
 </span>
 {v.created_by_name && (
 <span className="text-[10px] text-[var(--text-tertiary)]">— {v.created_by_name}</span>
 )}
 </div>
 <p className="text-sm text-[var(--text-primary)] mb-1">{v.label}</p>
 {v.diff && (
 <div className="flex gap-2 text-[10px] mb-2">
 {v.diff.added > 0 && <span className="text-emerald-600">+{v.diff.added}</span>}
 {v.diff.removed > 0 && <span className="text-red-500">-{v.diff.removed}</span>}
 {v.diff.moved > 0 && <span className="text-amber-600">{v.diff.moved} verschoben</span>}
 <span className="text-[var(--text-tertiary)]">{v.regale_count} Regale · {v.objekte_count} Objekte</span>
 </div>
 )}
 <div className="flex gap-2">
 <button onClick={() => viewVersion(v.id)}
 className="text-[11px] text-[#338edc] hover:underline">{t('warehouse.anzeigen') || 'Anzeigen'}</button>
 {v.version_nr > 1 && (
 <button onClick={() => viewDiff(v.id)}
 className="text-[11px] text-[#338edc] hover:underline">{t('warehouse.diff') || 'Diff'}</button>
 )}
 </div>
 </div>
 </div>
 ))}
 </div>
 )}
 </div>
 </div>

 {/* Detail Log */}
 <div className="w-[380px] shrink-0 bg-[var(--bg-card)] border border-[var(--border-card)] rounded-xl overflow-hidden flex flex-col">
 <button onClick={() => setShowDetailLog(!showDetailLog)}
 className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-card)] hover:bg-[var(--bg-card-hover)] transition-colors">
 <h3 className="text-sm font-semibold text-[var(--text-primary)]">{t('warehouse.detailLog') || 'Detail-Log'}</h3>
 <div className="flex items-center gap-2">
 <span className="text-[10px] text-[var(--text-tertiary)]">{changelogTotal} Einträge</span>
 <ChevronDown className={`w-4 h-4 text-[var(--text-tertiary)] transition-transform ${showDetailLog ? 'rotate-180' : ''}`} />
 </div>
 </button>
 {showDetailLog && (
 <div className="flex-1 overflow-y-auto">
 {changelog.length === 0 ? (
 <div className="text-center text-xs text-[var(--text-tertiary)] py-6">Keine Änderungen</div>
 ) : (
 <div className="divide-y divide-[var(--border-card)]">
 {changelog.map(c => (
 <div key={c.id} className="px-4 py-2.5 hover:bg-[var(--bg-card-hover)] transition-colors">
 <div className="flex items-center gap-2 mb-0.5">
 <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
 c.aktion.includes('erstellt') ? 'bg-emerald-500' :
 c.aktion.includes('geloescht') ? 'bg-red-500' :
 c.aktion.includes('verschoben') ? 'bg-amber-500' :
 c.aktion.includes('import') ? 'bg-indigo-500' : 'bg-[var(--text-tertiary)]'
 }`} />
 <span className="text-[10px] text-[var(--text-tertiary)]">
 {new Date(c.created_at).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
 </span>
 {c.created_by_name && <span className="text-[10px] text-[var(--text-tertiary)]">{c.created_by_name}</span>}
 </div>
 <p className="text-xs text-[var(--text-primary)]">{c.beschreibung}</p>
 </div>
 ))}
 </div>
 )}
 </div>
 )}
 </div>
 </div>
 )}
 </div>
 )}

 {/* ═══ SUBTYPEN TAB ═══ */}
 {activeTab === 'subtypen' && (
 <SubtypenVerwaltung subtypen={subtypen} onReload={async () => {
 const st = await apiFetch('/api/warehouse/subtypen');
 if (st && typeof st === 'object' && !st.error) setSubtypen(st);
 }} t={t} />
 )}
 </div>

 {/* Segmentation dialog */}
 <SegmentierungDialog
 open={!!segmentDialog}
 gruppe={segmentDialog?.gruppe}
 reihen={segmentDialog?.gruppe?.reihen || []}
 shelf={segmentDialog?.editShelf}
 subtyp={segmentDialog?.subtyp || (segmentDialog?.editShelf?.subtyp_id ? getSubtypById(subtypen, segmentDialog.editShelf.subtyp_id) : null)}
 onClose={() => setSegmentDialog(null)}
 onConfirm={(segments) => {
 if (segmentDialog?.editShelf) {
 // Edit mode: update existing shelf's segments
 const shelfId = segmentDialog.editShelf.id;
 apiFetch(`/api/warehouse/${warehouseId}/shelves/${shelfId}/segmentierung`, {
 method: 'PUT', body: { segmente: segments },
 }).then(() => loadData());
 setSegmentDialog(null);
 } else {
 handleSegmentDialogConfirm(segments);
 }
 }}
 t={t} />

 </div>
 );
}
